import {
  Plugin,
  Widget,
  toWidget,
  toWidgetEditable,
  WidgetToolbarRepository,
} from 'ckeditor5'
import {
  removeAlldropAreaWides,
  removeDropAreaNarrow,
  createDropFromHtml,
  adddropAreaWidesAroundAll,
  updateButtonVisibility,
  handleButtonOptions,
  handleIconButtonOptions,
  handleAddModal,
  handleAddButton,
  handleAddNumberedCard,
  handleHeaderSettingsModal,
  handleAddAccordion,
  handleTabOptions,
  handleAddTab,
  handleHotspotSettings,
  handleAddHotspot,
  generateUUID,
} from './utils'
import {
  addBlockSvg,
  addCardAndSvg,
  addCopyButtonSvg,
  addDeleteButtonSvg,
  addDuplicateButtonSvg,
  addSettingButtonSvg,
  closeButtonSvg,
  dropAreaWideSvg,
  editIconSvg,
  editInteractionSvg,
  hotspotDeleteSvg,
  hotspotEditSvg,
  hotspotPlusSvg,
  hotspotSettingsSvg,
  keySvg,
  reportButtonSvg,
} from './svg'
import { createCourseTemplate } from './course-outline-template'
import CreateCourseList from './course-list'
import CreateCollectionList from './collection-list'
import CreateCatalogList, { initCatalogFilterEvents } from './catalog'
import { handleCatalogOrderingModal } from './catalog/ordering-modal'
import { catalogOrderingSvg } from './catalog/svg'
import CreateContinueLearning, {
  initContinueLearningEvents,
  DEFAULT_TITLE as CONTINUE_LEARNING_TITLE,
  DEFAULT_LINK_LABEL as CONTINUE_LEARNING_LINK_LABEL,
} from './continue-learning'
import CreateCourseList2 from './course-list2'
import CreateBadgeList, { initRewardEvents, initHeaderEvents } from './badge-list'
import { createCourseTemplateAccordion } from './course-outline-template-accordion'

import { headerDefaultTemplate } from './header-default-template'
import { headerHeroTemplate } from './header-hero-template'
import { headerPromoTemplate } from './header-promo-template'
import { editorEnum } from 'src/model/editor/editorEnum'
import axios from 'axios'
import URL from 'src/api/url'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'
import { messageBoxShowAction } from 'src/reducers/message-box/action'
import { snackbarShowAction } from 'src/reducers/snackbar/action'
import SnackbarMessage from 'src/model/snackbar'
import { SnackbarType } from 'src/model/snackbar/type'
import { track } from '@amplitude/analytics-browser'
import { getPortalPageEditing } from 'src/utils/portal-page-editing'
import { withWorkspaceLogo } from 'src/utils/workspace-logo'
import { headerBackgroundCss } from 'src/utils/default-header-background'

const COPY_FLAG_KEY = 'mcg-show-paste-button'
const COPY_SOURCE_TYPE_KEY = 'mcg-copy-source-type'

function showEditorToast(message) {
  const dispatch = window.store?.dispatch
  if (!dispatch) return

  dispatch(
    snackbarShowAction(
      new SnackbarMessage(`success_${Date.now()}`, SnackbarType.SUCCESS, [
        message,
      ]),
    ),
  )
}

// Helper: update tab scroll button visibility based on scroll position
function updateTabScrollButtons(tabHeadersEl) {
  const wrapper = tabHeadersEl.closest('.tab-headers-scroll-wrapper')
  if (!wrapper) return
  const leftBtn = wrapper.querySelector('.tab-scroll-left')
  const rightBtn = wrapper.querySelector('.tab-scroll-right')
  if (!leftBtn || !rightBtn) return

  const hasOverflow = tabHeadersEl.scrollWidth > tabHeadersEl.clientWidth + 1
  const atStart = tabHeadersEl.scrollLeft <= 1
  const atEnd = tabHeadersEl.scrollLeft >= tabHeadersEl.scrollWidth - tabHeadersEl.clientWidth - 1

  leftBtn.style.display = hasOverflow && !atStart ? 'flex' : 'none'
  rightBtn.style.display = hasOverflow && !atEnd ? 'flex' : 'none'
}

class SimpleBox extends Plugin {
  static get requires() {
    return [Widget]
  }

  init() {
    const editor = this.editor
    editor.editing.view.document.on('delete', (evt, data) => {
      const selection = editor.model.document.selection
      const selectedElement = selection.getSelectedElement()

      if (
        selectedElement &&
        (selectedElement.name === 'buttonWrapper' ||
          selectedElement.name === 'cardWrapper' ||
          selectedElement.name === 'imageCarouselWrapper')
      ) {
        evt.stop()
        data.preventDefault()
        return false
      }
    })

    editor.editing.view.document.on(
      'click',
      (evt, data) => {
        const target = data.target

        let currentElement = target
        while (currentElement) {
          if (
            currentElement.hasClass &&
            currentElement.hasClass('image-hotspot-content')
          ) {
            evt.stop()
            data.preventDefault()
            return false
          }
          currentElement = currentElement.parent
        }
      },
      { priority: 'highest' },
    )
    editor.editing.view.document.on('keydown', (evt, data) => {
      const selection = editor.model.document.selection
      const selectedElement = selection.getSelectedElement()

      if (
        selectedElement &&
        (selectedElement.name === 'buttonWrapper' ||
          selectedElement.name === 'cardWrapper' ||
          selectedElement.name === 'imageCarouselWrapper')
      ) {
        evt.stop()
        data.preventDefault()
        return false
      }
    })
    this._defineSchema()
    this._defineConverters()
    this._defineCommands()
    this._refreshIntercomArticles()
    this._setupListMarkerColorSync()
  }
  afterInit() {
    const editor = this.editor
    const widgetToolbarRepository = editor.plugins.get(WidgetToolbarRepository)

    // Video wrapper için tam toolbar
    widgetToolbarRepository.register('videoToolbar', {
      ariaLabel: 'Video toolbar',
      items: editor.config.get('video.toolbar'),
      getRelatedElement: selection => {
        const viewElement = selection.getSelectedElement()
        if (viewElement && viewElement.hasClass('video-wrapper')) {
          return viewElement
        }
        return null
      },
    })

    // Media embed için sadece insertViaUrl ve upload
    widgetToolbarRepository.register('mediaEmbedToolbar', {
      ariaLabel: 'Media embed toolbar',
      items: ['videoGalleryLink', 'videoGalleryChange'], // Sadece insert ve upload
      getRelatedElement: selection => {
        const viewElement = selection.getSelectedElement()

        // Check for media embed elements
        if (
          viewElement &&
          viewElement.name === 'figure' &&
          viewElement.hasClass('media')
        ) {
          return viewElement
        }

        // Check for oembed elements
        if (viewElement && viewElement.name === 'oembed') {
          return viewElement.parent || viewElement
        }

        // Check if parent is media figure
        if (viewElement && viewElement.parent) {
          const parent = viewElement.parent
          if (parent.name === 'figure' && parent.hasClass('media')) {
            return parent
          }
        }

        return null
      },
    })

    widgetToolbarRepository.register('imageHotspotToolbar', {
      ariaLabel: 'Image hotspot toolbar',
      items: editor.config.get('image.toolbar'),
      getRelatedElement: selection => {
        const viewElement = selection.getSelectedElement()
        if (viewElement && viewElement.hasClass('image-hotspot-container')) {
          return viewElement
        }
        return null
      },
    })

  }
  _findHotspotById(element, id) {
    if (element.name === 'imageHotspot' && element.getAttribute('id') === id) {
      return element
    }

    if (element.getChildren) {
      for (const child of element.getChildren()) {
        const result = this._findHotspotById(child, id)
        if (result) return result
      }
    }

    return null
  }

  _refreshIntercomArticles() {
    const editor = this.editor

    const refreshArticles = () => {
      const root = editor.model.document.getRoot()
      const course = editor.config.get('simpleBox.course') || {}
      const courseId = course.id || ''

      for (const child of root.getChildren()) {
        if (child.name !== 'simpleBoxWrapper') continue
        const articleId = child.getAttribute('intercomArticleId')
        if (!articleId) continue

        const endpoint = URL.API + URL.INTERCOM_ARTICLE_BY_ID
          .replace('{articleId}', articleId)
          .replace('{courseId}', courseId)

        axios.get(endpoint).then(response => {
          const articleData = response?.data?.data || response?.data
          const body = articleData?.body
          if (!body) return

          const title = articleData?.title || ''
          const description = articleData?.description || ''
          let fullBody = body
            .replace(/<iframe[^>]*src="https?:\/\/(?:www\.)?youtube\.com\/embed\/([^"?]+)[^"]*"[^>]*>(?:<\/iframe>)?/gi,
              (match, videoId) => '<a href="https://www.youtube.com/watch?v=' + videoId + '" target="_blank" style="display:block"><img src="https://img.youtube.com/vi/' + videoId + '/hqdefault.jpg" style="max-width:100%;height:auto;border-radius:8px;" /></a>')
          if (description) {
            fullBody = '<p class="no-margin" style="font-size:14px;color:#637381">' + description + '</p><hr>' + fullBody
          }
          if (title) {
            fullBody = '<h1>' + title + '</h1>' + fullBody
          }

          editor.model.change(writer => {
            for (const simpleBox of child.getChildren()) {
              if (simpleBox.name !== 'simpleBox') continue
              for (const column of simpleBox.getChildren()) {
                if (column.name !== 'column') continue
                for (const rawHtml of column.getChildren()) {
                  if (rawHtml.name !== 'rawHtml') continue
                  writer.setAttribute('value', fullBody, rawHtml)
                }
              }
            }
          })
        }).catch(err => {
          console.error('Intercom article refresh error:', err)
        })
      }
    }

    editor.on('ready', () => refreshArticles())
  }

  _setupListMarkerColorSync() {
    const editor = this.editor

    const applyListColors = container => {
      container.querySelectorAll('li').forEach(li => {
        // Find all colored elements at any depth inside the li
        const coloredEls = Array.from(li.querySelectorAll('[style]')).filter(
          el => el.style.color,
        )
        const totalText = li.textContent.replace(/\s+/g, ' ').trim()

        if (!totalText || coloredEls.length === 0) {
          li.style.color = ''
          return
        }

        // Check if all colored elements share the same color
        let commonColor = coloredEls[0].style.color
        const allSameColor = coloredEls.every(
          el => el.style.color === commonColor,
        )

        if (!allSameColor) {
          li.style.color = ''
          return
        }

        // Check if all text content is covered by colored elements
        const coloredText = coloredEls
          .map(el => el.textContent)
          .join('')
          .replace(/\s+/g, ' ')
          .trim()

        li.style.color = coloredText === totalText ? commonColor : ''
      })
    }

    const updateColors = () => {
      const domRoot = editor.editing.view.getDomRoot()
      if (!domRoot) return
      applyListColors(domRoot)
    }

    editor.model.document.on('change:data', () => {
      setTimeout(updateColors, 0)
    })

    editor.on('ready', () => {
      setTimeout(updateColors, 50)
    })

    // Apply list marker colors to data output so they appear in preview/embed
    const originalGetData = editor.getData.bind(editor)
    editor.getData = function (options) {
      const html = originalGetData(options)
      const parser = new DOMParser()
      const doc = parser.parseFromString(`<body>${html}</body>`, 'text/html')
      applyListColors(doc.body)
      return doc.body.innerHTML
    }
  }

  _defineCommands() {
    const editor = this.editor
    const onAddButtonClick = editor.config.get('simpleBox.onAddButtonClick')

    editor.commands.add('addClickdropAreaWide', {
      execute: () => {
        onAddButtonClick()
      },
    })

    let hasDragStarted = false

    const dragEnterFunc = evt => {
      const viewElement = editor.editing.view.document.getRoot()
      for (const child of viewElement.getChildren()) {
        if (child && child.hasClass('empty-dom')) {
          editor.model.change(writer => {
            const modelElement = editor.editing.mapper.toModelElement(child)
            if (modelElement) {
              writer.remove(modelElement)
            }
          })
        }
      }

      if (!hasDragStarted) {
        hasDragStarted = true
        adddropAreaWidesAroundAll(null, null, editor)

        const dropAreaWides = document.querySelectorAll('.drop-area-wide')
        dropAreaWides.forEach(area => {
          area.classList.add('drag-over-enter')
        })
      }
    }

    const resetDrag = () => {
      hasDragStarted = false
      const wrapperButtons = document.querySelectorAll(
        '.simple-box-wrapper-button',
      )
      wrapperButtons.forEach(button => {
        button.style.display = '' // Reset to default display value
      })
    }

    document.addEventListener('dragenter', dragEnterFunc)

    editor.editing.view.document.on('dragover', () => {
      const dropAreaWides = document.querySelectorAll('.drop-area-wide')
      dropAreaWides.forEach(area => {
        if (!area.classList.contains('drag-over-enter')) {
          area.classList.add('drag-over-enter')
        }
      })
      const wrapperButtons = document.querySelectorAll(
        '.simple-box-wrapper-button',
      )

      // Hide wrapper buttons
      wrapperButtons.forEach(button => {
        button.style.display = 'none'
      })
    })
    editor.editing.view.document.on('drop', e => {
      resetDrag()
    })
    editor.editing.view.document.on('dragend', e => {
      resetDrag()
    })

    document.addEventListener('dragend', e => {
      e.preventDefault()
      setTimeout(() => {
        removeDropAreaNarrow(editor)
      }, 300)
      resetDrag()
    })
    document.addEventListener('drop', e => {
      resetDrag()
    })

    editor.commands.add('contentDelete', {
      execute: () => {
        return new Promise(resolve => {
          setTimeout(() => {
            try {
              editor.model.change(writer => {
                const selection = editor.model.document.selection

                if (!selection || selection.rangeCount === 0) {
                  resolve()
                  return
                }

                const selectedElement = selection.getSelectedElement()
                if (!selectedElement || !selectedElement.parent) {
                  resolve()
                  return
                }

                const parentElement = selectedElement.parent

                if (
                  parentElement.name === 'simpleBoxWrapper' &&
                  parentElement.parent
                ) {
                  const grandParent = parentElement.parent

                  // Remove the simpleBoxWrapper
                  writer.remove(parentElement)

                  // Check if grandParent is a content area that needs dropAreaWide
                  if (
                    grandParent.name === 'tabContent' ||
                    grandParent.name === 'accordionContent' ||
                    grandParent.name === 'imageHotspotContent'
                  ) {
                    // Check if there are any simpleBoxWrapper left
                    const hasSimpleBoxWrapper = Array.from(
                      grandParent.getChildren(),
                    ).some(child => child.name === 'simpleBoxWrapper')

                    // Check if there's already a dropAreaWide
                    const hasDropAreaWide = Array.from(
                      grandParent.getChildren(),
                    ).some(child => child.name === 'dropAreaWide')

                    // If no simpleBoxWrapper and no dropAreaWide, add one
                    if (!hasSimpleBoxWrapper && !hasDropAreaWide) {
                      const dropAreaWide = writer.createElement('dropAreaWide')
                      writer.append(dropAreaWide, grandParent)
                    }
                  }
                }
                resolve()
              })
            } catch (error) {
              console.error('Error:', error)
              resolve()
            }
          }, 10)
        })
      },
    })

    editor.commands.add('contentCopy', {
      execute: () => {
        // Find the simpleBoxWrapper element
        let wrapperElement = null

        if (editor._contentAddTarget && editor._contentAddTarget.name === 'simpleBoxWrapper') {
          wrapperElement = editor._contentAddTarget
        } else {
          const selection = editor.model.document.selection
          const selectedElement = selection.getSelectedElement()
          if (!selectedElement) return

          if (selectedElement.name === 'simpleBoxWrapper') {
            wrapperElement = selectedElement
          } else if (selectedElement.parent && selectedElement.parent.name === 'simpleBoxWrapper') {
            wrapperElement = selectedElement.parent
          }
        }

        if (!wrapperElement) return

        // Get inner content HTML (toView only converts children, not the element itself)
        const viewFragment = editor.data.toView(wrapperElement)
        const innerHtml = editor.data.processor.toData(viewFragment)

        // Build wrapper element with all attributes (matching data downcast converter)
        const backgroundImage = wrapperElement.getAttribute('backgroundImage') || ''
        const backgroundColor = wrapperElement.getAttribute('backgroundColor') || ''
        const backgroundType = wrapperElement.getAttribute('backgroundType') || ''
        const backgroundPosition = wrapperElement.getAttribute('backgroundPosition') || 'center'
        const sizeType = wrapperElement.getAttribute('sizeType') || 'SCALE_TO_FILL'
        const overlayColor = wrapperElement.getAttribute('overlayColor') || ''
        const opacity = wrapperElement.getAttribute('opacity') || ''
        const linearValue = wrapperElement.getAttribute('linearValue') || ''
        const useImageOverlay = wrapperElement.getAttribute('useImageOverlay') || ''
        const layoutType = wrapperElement.getAttribute('layoutType')
        const blockId = wrapperElement.getAttribute('blockId') || ''
        const intercomArticleId = wrapperElement.getAttribute('intercomArticleId') || ''

        const SizeTypeMap = { SCALE_TO_FILL: 'cover', ORIGINAL_SIZE: 'auto', TILE: 'contain' }
        const backgroundSize = SizeTypeMap[sizeType] || 'cover'

        let styleString = ''
        if (backgroundColor) styleString += `background-color: ${backgroundColor}; `
        if (sizeType === 'ORIGINAL_SIZE') styleString += `background-repeat: no-repeat; `
        styleString += `background-size: ${backgroundSize}; `
        if (backgroundPosition) styleString += `background-position: ${backgroundPosition}; `
        if (backgroundImage && linearValue && String(useImageOverlay) === 'true') {
          styleString += `background-image: linear-gradient(${linearValue}, ${linearValue}), url(${backgroundImage}); `
        } else if (backgroundImage) {
          styleString += `background-image: url(${backgroundImage}); `
        }

        const attrPairs = [
          ['class', 'simple-box-wrapper'],
          ['style', styleString.trim()],
          ['background-image', backgroundImage],
          ['background-color', backgroundColor],
          ['background-type', backgroundType],
          ['background-size', backgroundSize],
          ['size-type', sizeType],
          ['use-image-overlay', useImageOverlay],
          ['background-position', backgroundPosition],
          ['linear-value', linearValue],
          ['opacity', opacity],
          ['overlay-color', overlayColor],
        ]
        if (layoutType) attrPairs.push(['layout-type', layoutType])
        if (blockId) attrPairs.push(['data-block-id', blockId])
        if (intercomArticleId) attrPairs.push(['data-intercom-article-id', intercomArticleId])

        const attrsString = attrPairs.map(([k, v]) => `${k}="${v}"`).join(' ')
        const htmlString = `<div ${attrsString}>${innerHtml}</div>`

        navigator.clipboard.writeText(htmlString).then(() => {
          localStorage.setItem(COPY_FLAG_KEY, '1')
          localStorage.setItem(COPY_SOURCE_TYPE_KEY, 'block')
          window.dispatchEvent(new Event('mcg-editor-copy'))
          track('block_copied', {
            copy_content: 'block',
            page_context: 'editor_block',
          })
        })
        showEditorToast('Block copied to clipboard')
      },
    })

    editor.commands.add('contentDuplicate', {
      execute: () => {
        const selection = editor.model.document.selection
        const selectedElement = selection.getSelectedElement()
        if (!selectedElement || !selectedElement.parent) return

        const wrapperElement = selectedElement.parent
        if (wrapperElement && wrapperElement.name === 'simpleBoxWrapper') {
          // toView only converts children, not the element itself — rebuild wrapper manually
          const viewFragment = editor.data.toView(wrapperElement)
          const innerHtml = editor.data.processor.toData(viewFragment)

          const backgroundImage = wrapperElement.getAttribute('backgroundImage') || ''
          const backgroundColor = wrapperElement.getAttribute('backgroundColor') || ''
          const backgroundType = wrapperElement.getAttribute('backgroundType') || ''
          const backgroundPosition = wrapperElement.getAttribute('backgroundPosition') || 'center'
          const sizeType = wrapperElement.getAttribute('sizeType') || 'SCALE_TO_FILL'
          const overlayColor = wrapperElement.getAttribute('overlayColor') || ''
          const opacity = wrapperElement.getAttribute('opacity') || ''
          const linearValue = wrapperElement.getAttribute('linearValue') || ''
          const useImageOverlay = wrapperElement.getAttribute('useImageOverlay') || ''
          const layoutType = wrapperElement.getAttribute('layoutType')
          const intercomArticleId = wrapperElement.getAttribute('intercomArticleId') || ''

          const SizeTypeMap = { SCALE_TO_FILL: 'cover', ORIGINAL_SIZE: 'auto', TILE: 'contain' }
          const backgroundSize = SizeTypeMap[sizeType] || 'cover'

          let styleString = ''
          if (backgroundColor) styleString += `background-color: ${backgroundColor}; `
          if (sizeType === 'ORIGINAL_SIZE') styleString += `background-repeat: no-repeat; `
          styleString += `background-size: ${backgroundSize}; `
          if (backgroundPosition) styleString += `background-position: ${backgroundPosition}; `
          if (backgroundImage && linearValue && String(useImageOverlay) === 'true') {
            styleString += `background-image: linear-gradient(${linearValue}, ${linearValue}), url(${backgroundImage}); `
          } else if (backgroundImage) {
            styleString += `background-image: url(${backgroundImage}); `
          }

          const attrPairs = [
            ['class', 'simple-box-wrapper'],
            ['style', styleString.trim()],
            ['background-image', backgroundImage],
            ['background-color', backgroundColor],
            ['background-type', backgroundType],
            ['background-size', backgroundSize],
            ['size-type', sizeType],
            ['use-image-overlay', useImageOverlay],
            ['background-position', backgroundPosition],
            ['linear-value', linearValue],
            ['opacity', opacity],
            ['overlay-color', overlayColor],
          ]
          if (layoutType) attrPairs.push(['layout-type', layoutType])
          if (intercomArticleId) attrPairs.push(['data-intercom-article-id', intercomArticleId])

          const attrsString = attrPairs.map(([k, v]) => `${k}="${v}"`).join(' ')
          const htmlString = `<div ${attrsString}>${innerHtml}</div>`

          const viewFragmentParsed = editor.data.processor.toView(htmlString)
          const modelFragment = editor.data.toModel(viewFragmentParsed)

          editor.model.change(writer => {
            writer.insert(modelFragment, wrapperElement, 'after')
          })
          track('block_duplicated', {
            duplicate_content: 'block',
            page_context: 'editor_block',
          })
          showEditorToast('Block duplicated')
        }
      },
    })

    editor.commands.add('editInteraction', {
      execute: () => {
        const selection = editor.model.document.selection
        let simpleBox = null

        try {
          simpleBox = selection.getSelectedElement()
          if (!simpleBox) {
            const range = selection.getFirstRange()
            if (range) {
              for (const item of range.getWalker()) {
                if (item.item.is('element') && item.item.name === 'simpleBox') {
                  simpleBox = item.item
                  break
                }
              }
            }
          }

          if (!simpleBox) {
            const root = editor.model.document.getRoot()
            for (const child of root.getChildren()) {
              if (child.name === 'simpleBox') {
                simpleBox = child
              }
            }
          }
        } catch (error) {
          console.warn('Selection error:', error)

          // Fallback: Manual search for simpleBox elements
          const root = editor.model.document.getRoot()
          for (const child of root.getChildren()) {
            if (child.name === 'simpleBox') {
              simpleBox = child
              break
            }
          }
        }

        if (!simpleBox) {
          console.error('No simpleBox element found')
          return
        }

        // Extract HTML content from the element
        let extractedHtml = null
        let interactionCode = null

        // Function to extract HTML from iframe srcdoc - Updated to handle column structure
        const extractHtmlFromElement = element => {
          if (!element) return null

          // First, check if element has direct rawHtml child
          for (const child of element.getChildren()) {
            if (child.name === 'rawHtml') {
              const result = extractFromRawHtml(child)
              if (result) return result
            }
          }

          // If no direct rawHtml found, check within columns
          for (const child of element.getChildren()) {
            if (child.name === 'column') {
              // Search within column for rawHtml
              for (const columnChild of child.getChildren()) {
                if (columnChild.name === 'rawHtml') {
                  const result = extractFromRawHtml(columnChild)
                  if (result) return result
                }
              }
            }
          }

          return null
        }

        // Helper function to extract HTML from rawHtml element
        const extractFromRawHtml = rawHtmlElement => {
          const iframeValue = rawHtmlElement.getAttribute('value')
          if (iframeValue) {
            // Extract srcdoc content from iframe
            const srcdocMatch = iframeValue.match(/srcdoc="([^"]*)"/)
            if (srcdocMatch && srcdocMatch[1]) {
              // Decode HTML entities
              const decodedHtml = srcdocMatch[1]
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&quot;/g, '"')
                .replace(/&amp;/g, '&')

              // Extract interaction code if exists
              const codeMatch = iframeValue.match(
                /data-interaction-code="([^"]*)"/,
              )
              if (codeMatch && codeMatch[1]) {
                interactionCode = codeMatch[1]
              }

              return decodedHtml
            }
          }
          return null
        }

        const getElementPosition = () => {
          try {
            const viewElement = editor.editing.mapper.toViewElement(simpleBox)
            if (viewElement) {
              const domElement = editor.editing.view.domConverter.viewToDom(
                viewElement,
              )
              if (domElement) {
                const rect = domElement.getBoundingClientRect()
                return {
                  x: rect.left + rect.width / 2,
                  y: rect.top + rect.height / 2,
                }
              }
            }

            const editorElement = document.querySelector('.ck-editor__editable')
            if (editorElement) {
              const rect = editorElement.getBoundingClientRect()
              return {
                x: rect.left + rect.width / 2,
                y: rect.top + rect.height / 2,
              }
            }
          } catch (error) {
            console.warn('Could not get element position:', error)
          }

          return { x: window.innerWidth / 2, y: window.innerHeight / 2 }
        }

        extractedHtml = extractHtmlFromElement(simpleBox)
        const elementPosition = getElementPosition()

        const dispatch = editor.config.get('videoGalleryPlugin.dispatch')
        if (dispatch) {
          dispatch({
            type: 'OPEN_CHAT_MODAL',
            payload: {
              blockId: simpleBox.getAttribute('id') || Date.now(),
              blockType: simpleBox.getAttribute('blockType'),
              element: simpleBox,
              existingHtml: extractedHtml,
              interactionCode: interactionCode,
              mode: 'editInteraction',
              position: elementPosition,
            },
          })
        }

        const onEditInteraction = editor.config.get(
          'simpleBox.onEditInteraction',
        )
        if (typeof onEditInteraction === 'function') {
          onEditInteraction({
            blockId: simpleBox.getAttribute('id') || Date.now(),
            blockType: simpleBox.getAttribute('blockType'),
            element: simpleBox,
            existingHtml: extractedHtml,
            interactionCode: interactionCode,
            mode: 'editInteraction',
            position: elementPosition,
          })
        }
      },
    })
    editor.commands.add('contentAdd', {
      execute: () => {
        const selection = editor.model.document.selection
        const simpleBox = selection.getSelectedElement()

        // Search for buttonWrapper in the entire simpleBox structure
        let hasButtonWrapper = false
        let hasAccordionWrapper = false
        let hasHotspotWrapper = false

        const findAccordionWrapper = element => {
          if (!element || !element.is('element')) return false

          // Check if current element is a buttonWrapper
          if (element.name === 'accordionWrapper') {
            return true
          }

          // Check all children recursively
          for (const child of element.getChildren()) {
            if (findAccordionWrapper(child)) {
              return true
            }
          }

          return false
        }
        let hasTabWrapper = false

        const findTabWrapper = element => {
          if (!element || !element.is('element')) return false

          // Check if current element is a buttonWrapper
          if (element.name === 'tabContainer') {
            return true
          }

          // Check all children recursively
          for (const child of element.getChildren()) {
            if (findTabWrapper(child)) {
              return true
            }
          }

          return false
        }

        // Function to recursively search for buttonWrapper
        const findButtonWrapper = element => {
          if (!element || !element.is('element')) return false

          // Check if current element is a buttonWrapper
          if (element.name === 'buttonWrapper') {
            return true
          }

          // Check all children recursively
          for (const child of element.getChildren()) {
            if (findButtonWrapper(child)) {
              return true
            }
          }

          return false
        }

        const findHotspotWrapper = element => {
          if (!element || !element.is('element')) return false

          // Check if current element is a buttonWrapper
          if (element.name === 'imageHotspotContainer') {
            return true
          }

          // Check all children recursively
          for (const child of element.getChildren()) {
            if (findHotspotWrapper(child)) {
              return true
            }
          }

          return false
        }

        // Start the search from simpleBox
        hasButtonWrapper = findButtonWrapper(simpleBox)
        hasAccordionWrapper = findAccordionWrapper(simpleBox)
        hasTabWrapper = findTabWrapper(simpleBox)
        hasHotspotWrapper = findHotspotWrapper(simpleBox)
        if (hasButtonWrapper) {
          handleAddButton(editor)
        } else if (hasAccordionWrapper) {
          handleAddAccordion(editor)
          editor.editing.reconvertItem(simpleBox)
          editor.editing.view.forceRender()
        } else if (hasTabWrapper) {
          handleAddTab(editor)
          editor.editing.reconvertItem(simpleBox)
          editor.editing.view.forceRender()
        } else if (hasHotspotWrapper) {
          handleAddHotspot(editor, { simpleBox })
          editor.editing.reconvertItem(simpleBox)
          editor.editing.view.forceRender()
        } else {
          handleAddNumberedCard(editor)
          editor.editing.reconvertItem(simpleBox)
          editor.editing.view.forceRender()
        }
      },
    })
    editor.commands.add('contentSettings', {
      execute: () => {
        editor.model.change(writer => {
          const selection = editor.editing.view.document.selection
          const simpleBoxWrapper = selection.getSelectedElement().parent
          if (
            simpleBoxWrapper &&
            simpleBoxWrapper.hasClass('simple-box-wrapper')
          ) {
            this.dispatch = editor.config.get('videoGalleryPlugin.dispatch')
            handleAddModal(this.dispatch, editor, simpleBoxWrapper)
          }
        })
      },
    })

    editor.commands.add('catalogOrdering', {
      execute: () => {
        const selection = editor.editing.view.document.selection
        const selectedElement = selection.getSelectedElement()
        if (!selectedElement) return

        this.dispatch = editor.config.get('videoGalleryPlugin.dispatch')
        handleCatalogOrderingModal(this.dispatch, editor)
      },
    })

    editor.commands.add('addContentAbove', {
      execute: html => {
        editor.model.change(writer => {
          const selection = editor.model.document.selection
          const container = selection.getSelectedElement()

          if (
            container &&
            (container.name === 'simpleBoxWrapper' ||
              container.name === 'dynamicList')
          ) {
            removeAlldropAreaWides(writer, editor)

            const dropAreaWide = html
              ? createDropFromHtml(writer, html, editor)
              : writer.createElement('dropAreaWide')

            writer.insert(dropAreaWide, container, 'before')
            writer.setSelection(dropAreaWide, 'end')

            updateButtonVisibility(container, editor)
            if (onAddButtonClick) {
              onAddButtonClick()
            }
          }
        })
      },
    })

    editor.commands.add('addContentBelow', {
      execute: html => {
        editor.model.change(writer => {
          const selection = editor.model.document.selection
          const simpleBoxWrapper = selection.getSelectedElement()

          if (
            simpleBoxWrapper &&
            simpleBoxWrapper.name === 'simpleBoxWrapper'
          ) {
            removeAlldropAreaWides(writer, editor)

            const dropAreaWide = html
              ? createDropFromHtml(writer, html, editor)
              : writer.createElement('dropAreaWide')

            writer.insert(dropAreaWide, simpleBoxWrapper, 'after')
            writer.setSelection(dropAreaWide, 'end')

            updateButtonVisibility(simpleBoxWrapper, editor)
            if (onAddButtonClick) {
              onAddButtonClick()
            }
          }
        })
      },
    })

    editor.commands.add('addTab', {
      execute: () => {
        const selection = editor.model.document.selection
        const tabContainer = selection.getSelectedElement()

        if (tabContainer && tabContainer.name === 'tabContainer') {
          editor.model.change(writer => {
            const tabHeaders = tabContainer.getChild(0) // tabHeaders
            const tabContents = tabContainer.getChild(1) // tabContents

            const tabCount = tabHeaders.childCount + 1

            // Create new tab header with proper title
            const newTabHeader = writer.createElement('tabHeader', {
              tabIndex: tabCount.toString(),
              tabTitle: `Tab ${tabCount}`, // This will be displayed as button text
              isActive: 'false',
            })

            // Create new tab content
            const newTabContent = writer.createElement('tabContent', {
              tabIndex: tabCount.toString(),
              isActive: 'false',
            })

            const contentParagraph = writer.createElement('paragraph')
            writer.insertText(`Content for Tab ${tabCount}`, contentParagraph)
            writer.append(contentParagraph, newTabContent)

            // Add to containers
            writer.append(newTabHeader, tabHeaders)
            writer.append(newTabContent, tabContents)

            // Force re-render
            editor.editing.reconvertItem(tabContainer)
            editor.editing.view.forceRender()
          })
        }
      },
    })

    // Switch Tab Command
    editor.commands.add('switchTab', {
      execute: tabIndex => {
        const selection = editor.model.document.selection
        let tabContainer = selection.getSelectedElement()

        // If not directly selected, find parent tab container
        if (!tabContainer || tabContainer.name !== 'tabContainer') {
          let element = selection.getFirstPosition().parent
          while (element && element.name !== 'tabContainer') {
            element = element.parent
          }
          tabContainer = element
        }

        if (tabContainer && tabContainer.name === 'tabContainer') {
          editor.model.change(writer => {
            // Update active tab attribute
            writer.setAttribute('activeTab', tabIndex, tabContainer)

            const tabHeaders = tabContainer.getChild(0)
            const tabContents = tabContainer.getChild(1)

            // Update all tab headers
            for (let i = 0; i < tabHeaders.childCount; i++) {
              const header = tabHeaders.getChild(i)
              const headerIndex = header.getAttribute('tabIndex')
              writer.setAttribute(
                'isActive',
                headerIndex === tabIndex ? 'true' : 'false',
                header,
              )
            }

            // Update all tab contents
            for (let i = 0; i < tabContents.childCount; i++) {
              const content = tabContents.getChild(i)
              const contentIndex = content.getAttribute('tabIndex')
              writer.setAttribute(
                'isActive',
                contentIndex === tabIndex ? 'true' : 'false',
                content,
              )
            }

            // Force re-render
            editor.editing.reconvertItem(tabContainer)
            editor.editing.view.forceRender()

            // Scroll active tab header into view & update scroll buttons
            setTimeout(() => {
              const viewTabContainer = editor.editing.mapper.toViewElement(tabContainer)
              if (viewTabContainer) {
                const domTabContainer = editor.editing.view.domConverter.mapViewToDom(viewTabContainer)
                if (domTabContainer) {
                  const activeHeader = domTabContainer.querySelector(`.tab-header[data-tab-index="${tabIndex}"]`)
                  const tabHeadersEl = domTabContainer.querySelector('.tab-headers')
                  if (activeHeader && tabHeadersEl) {
                    const scrollLeft = activeHeader.offsetLeft - (tabHeadersEl.clientWidth / 2) + (activeHeader.offsetWidth / 2)
                    tabHeadersEl.scrollTo({ left: scrollLeft, behavior: 'smooth' })
                  }
                  if (tabHeadersEl) {
                    setTimeout(() => updateTabScrollButtons(tabHeadersEl), 350)
                  }
                }
              }
            }, 50)
          })
        }
      },
    })

    // Export Report Command for Pixel Quest
    editor.commands.add('exportReport', {
      execute: async () => {
        try {
          const selection = editor.model.document.selection
          let element =
            selection.getSelectedElement() ||
            selection.getFirstPosition().parent
          if (!element) {
            console.error('No element selected')
            return
          }

          // Check if this is a SCORM widget — use packageId instead of blockId
          let isScorm = false
          let scormPackageId = null
          // Find simpleBoxWrapper by walking up from selected element
          let wrapper = element
          while (wrapper) {
            if (wrapper.name === 'simpleBoxWrapper') break
            wrapper = wrapper.parent
          }
          if (wrapper) {
            const findScormPackage = (el) => {
              if (!el) return null
              if (el.name === 'scormPackage') return el
              if (el.getChildren) {
                for (const child of el.getChildren()) {
                  const found = findScormPackage(child)
                  if (found) return found
                }
              }
              return null
            }
            const scormEl = findScormPackage(wrapper)
            if (scormEl) {
              isScorm = true
              try {
                const cfg = JSON.parse(scormEl.getAttribute('scormConfig') || '{}')
                scormPackageId = cfg.packageId
              } catch (e) {
                // ignore parse error
              }
            }
          }

          // Find the block ID from the element or its ancestors
          let blockId = null
          let searchElement = element

          // Search through parent elements to find blockId
          while (searchElement) {
            // Check if this is simpleBoxWrapper with blockId attribute
            if (
              searchElement.name === 'simpleBoxWrapper' &&
              searchElement.getAttribute('blockId')
            ) {
              blockId = searchElement.getAttribute('blockId')
              break
            }
            // Also check for blockId attribute directly
            if (
              searchElement.getAttribute &&
              searchElement.getAttribute('blockId')
            ) {
              blockId = searchElement.getAttribute('blockId')
              break
            }
            // Fallback: check for data-block-id attribute
            if (
              searchElement.getAttribute &&
              searchElement.getAttribute('data-block-id')
            ) {
              blockId = searchElement.getAttribute('data-block-id')
              break
            }
            searchElement = searchElement.parent
          }

          // Determine the export ID
          const exportId = isScorm ? scormPackageId : blockId

          if (!exportId) {
            console.error(isScorm ? 'SCORM Package ID not found' : 'Block ID not found')
            // Show error message to user
            const dispatch = editor.config.get('videoGalleryPlugin.dispatch')
            if (dispatch) {
              dispatch(
                messageBoxShowAction(
                  MessageBoxContent({
                    title: 'Error',
                    description: isScorm
                      ? 'SCORM Package ID not found. Please upload a package first.'
                      : 'Block ID not found. Please try again.',
                    type: MessageBoxType.ERROR,
                    buttons: [],
                  }),
                ),
              )
            }
            return
          }

          // Make API request to export the interaction data
          const exportUrl = `/interaction/export/block/${exportId}`

          const response = await axios.get(exportUrl, {
            responseType: 'blob',
          })

          // Create a blob from the response
          const blob = new Blob([response.data], {
            type:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          })

          // Create a download link and trigger download
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = isScorm
            ? `scorm-report-${exportId}.xlsx`
            : `interaction-report-${exportId}.xlsx`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)

          // Success message removed as per user request for direct download
        } catch (error) {
          console.error('Error exporting report:', error)

          const dispatch = editor.config.get('videoGalleryPlugin.dispatch')

          // Handle 404 Not Found as a Warning
          if (error.response && error.response.status === 404) {
            let errorMessage = 'No interactions found.'
            try {
              if (error.response.data instanceof Blob) {
                const text = await error.response.data.text()
                const json = JSON.parse(text)
                if (json && json.data) {
                  errorMessage = json.data
                }
              }
            } catch (e) {
              console.error('Error parsing error response:', e)
            }

            if (dispatch) {
              dispatch(
                messageBoxShowAction(
                  MessageBoxContent({
                    title: 'Warning',
                    description: errorMessage,
                    type: MessageBoxType.WARNING,
                    buttons: [],
                  }),
                ),
              )
            }
            return
          }

          // Show generic error message for other errors
          if (dispatch) {
            dispatch(
              messageBoxShowAction(
                MessageBoxContent({
                  title: 'Error',
                  description: 'Failed to download report. Please try again.',
                  type: MessageBoxType.ERROR,
                  buttons: [],
                }),
              ),
            )
          }
        }
      },
    })

    // Remove Tab Command
    editor.commands.add('removeTab', {
      execute: tabIndex => {
        const selection = editor.model.document.selection
        let tabContainer = selection.getSelectedElement()

        if (!tabContainer || tabContainer.name !== 'tabContainer') {
          let element = selection.getFirstPosition().parent
          while (element && element.name !== 'tabContainer') {
            element = element.parent
          }
          tabContainer = element
        }

        if (tabContainer && tabContainer.name === 'tabContainer') {
          const tabHeaders = tabContainer.getChild(0)
          const tabContents = tabContainer.getChild(1)

          // Don't allow removing if only one tab left
          if (tabHeaders.childCount <= 1) {
            return
          }

          editor.model.change(writer => {
            // Find and remove the tab header and content
            for (let i = 0; i < tabHeaders.childCount; i++) {
              const header = tabHeaders.getChild(i)
              if (header.getAttribute('tabIndex') === tabIndex) {
                writer.remove(header)
                break
              }
            }

            for (let i = 0; i < tabContents.childCount; i++) {
              const content = tabContents.getChild(i)
              if (content.getAttribute('tabIndex') === tabIndex) {
                writer.remove(content)
                break
              }
            }

            // If we removed the active tab, activate the first tab
            const activeTab = tabContainer.getAttribute('activeTab')
            if (activeTab === tabIndex) {
              const firstHeader = tabHeaders.getChild(0)
              if (firstHeader) {
                const firstTabIndex = firstHeader.getAttribute('tabIndex')
                editor.execute('switchTab', firstTabIndex)
              }
            }

            // Force re-render
            editor.editing.reconvertItem(tabContainer)
            editor.editing.view.forceRender()
          })
        }
      },
    })

    editor.commands.add('cardBoxDelete', {
      execute() {
        const selection = editor.model.document.selection
        const cardBox = selection.getSelectedElement()

        const cardWrapper = cardBox.parent

        if (cardBox && cardBox.name === 'cardBox') {
          editor.model.change(writer => {
            writer.remove(cardBox)

            // Force re-conversion of the card wrapper
            editor.editing.reconvertItem(cardWrapper)
            editor.editing.reconvertItem(cardWrapper.parent.parent)
            editor.editing.view.forceRender()
          })
        }
      },
    })

    const findCarouselWrapper = () => {
      const selection = editor.model.document.selection
      let element = selection.getSelectedElement()
      if (!element) {
        element = selection.getFirstPosition().parent
      }
      while (element && element.name !== 'imageCarouselWrapper') {
        element = element.parent
      }
      if (element) return element

      // Try to find within the clicked target element (from button click)
      const findInChildren = parent => {
        if (!parent) return null
        for (const child of parent.getChildren()) {
          if (child.name === 'imageCarouselWrapper') return child
          if (child.getChildren) {
            const found = findInChildren(child)
            if (found) return found
          }
        }
        return null
      }

      if (editor._contentAddTarget) {
        const found = findInChildren(editor._contentAddTarget)
        if (found) return found
      }

      // Last fallback: find first imageCarouselWrapper in the document
      const root = editor.model.document.getRoot()
      return findInChildren(root)
    }

    editor.commands.add('imageCarouselItemDelete', {
      execute() {
        const carouselWrapper = findCarouselWrapper()
        if (!carouselWrapper) return
        const items = Array.from(carouselWrapper.getChildren()).filter(
          child => child.name === 'imageCarouselItem'
        )
        if (items.length <= 2) return

        // Find active item from DOM
        const viewWrapper = editor.editing.mapper.toViewElement(carouselWrapper)
        const domWrapper = editor.editing.view.domConverter.mapViewToDom(viewWrapper)
        let activeIndex = 0
        if (domWrapper) {
          const domItems = domWrapper.querySelectorAll(':scope > .ck-widget.image-carousel-item, :scope > div > .ck-widget.image-carousel-item, :scope > .image-carousel-item')
          domItems.forEach((item, i) => {
            if (item.classList.contains('active')) activeIndex = i
          })
        }

        const itemToDelete = items[activeIndex]
        if (itemToDelete) {
          const newActiveIndex = activeIndex > 0 ? activeIndex - 1 : 0
          editor._carouselActiveIndex = newActiveIndex
          editor.model.change(writer => {
            writer.remove(itemToDelete)
            editor.editing.reconvertItem(carouselWrapper)
            editor.editing.reconvertItem(carouselWrapper.parent.parent)
            editor.editing.view.forceRender()
          })
        }
      },
    })

    editor.commands.add('imageCarouselItemAdd', {
      execute({ carouselWrapper } = {}) {
        editor.model.change(writer => {
          let wrapper = carouselWrapper

          if (!wrapper) {
            wrapper = findCarouselWrapper()
          }

          if (wrapper && wrapper.name === 'imageCarouselWrapper') {
            const newItem = writer.createElement('imageCarouselItem')

            // Check if this is a hotspot carousel by looking at existing items
            const existingItems = Array.from(wrapper.getChildren()).filter(
              child => child.name === 'imageCarouselItem'
            )
            let isHotspotCarousel = false
            if (existingItems.length > 0) {
              for (const child of existingItems[0].getChildren()) {
                if (child.name === 'imageHotspotContainer') {
                  isHotspotCarousel = true
                  break
                }
              }
            }

            if (isHotspotCarousel) {
              // Create imageHotspotContainer without hotspots
              const hotspotContainer = writer.createElement('imageHotspotContainer', {
                src: 'https://cdn.minicoursegenerator.com/assets/carousel-and-hotspot/carouselAndHotspotImage.png',
                alt: '',
              })
              writer.append(hotspotContainer, newItem)
            } else {
              // Create normal imageWrapper for regular carousel
              const newImageWrapper = writer.createElement('imageWrapper')
              const newImage = writer.createElement('imageBlock', {
                src: 'https://cdn.minicoursegenerator.com/assets/carousel-and-hotspot/carousel-one.jpeg',
              })
              writer.append(newImage, newImageWrapper)
              writer.append(newImageWrapper, newItem)
            }

            // Find active item index from DOM
            const items = Array.from(wrapper.getChildren()).filter(
              child => child.name === 'imageCarouselItem'
            )
            let activeIndex = 0
            const viewWrapper = editor.editing.mapper.toViewElement(wrapper)
            const domWrapper = editor.editing.view.domConverter.mapViewToDom(viewWrapper)
            if (domWrapper) {
              const domItems = domWrapper.querySelectorAll(':scope > .ck-widget.image-carousel-item, :scope > div > .ck-widget.image-carousel-item, :scope > .image-carousel-item')
              domItems.forEach((item, i) => {
                if (item.classList.contains('active')) activeIndex = i
              })
            }

            // Insert after active item
            const activeItem = items[activeIndex]
            const newIndex = activeIndex + 1
            if (activeItem) {
              writer.insert(newItem, activeItem, 'after')
            } else {
              writer.append(newItem, wrapper)
            }

            // Save target index on editor instance (survives reconvert)
            editor._carouselActiveIndex = newIndex

            // Only reconvert wrapper, not parent.parent (which triggers initCarousel reset)
            editor.editing.reconvertItem(wrapper)
            editor.editing.view.forceRender()
          }
        })
      },
    })

    // Update button visibility on any data changes
    editor.model.document.on('change:data', () => {
      editor.model.change(writer => {
        const root = editor.model.document.getRoot()
        Array.from(root.getChildren())
          .filter(child => child.name === 'simpleBoxWrapper')
          .forEach(wrapper => {
            updateButtonVisibility(wrapper, editor)
          })
      })
    })
  }

  _defineSchema() {
    const schema = this.editor.model.schema

    schema.register('simpleBoxWrapper', {
      isObject: true,
      allowWhere: '$block',
      allowChildren: ['simpleBox', 'htmlEmbed', 'rawHtml'],
      allowAttributes: [
        'backgroundImage',
        'backgroundColor',
        'backgroundType',
        'backgroundPosition',
        'backgroundSize',
        'sizeType',
        'useImageOverlay',
        'overlayColor',
        'opacity',
        'layoutType',
        'blockId',
        'intercomArticleId',
      ],
    })

    schema.register('simpleBox', {
      isObject: true,
      allowIn: 'simpleBoxWrapper',
      inheritAllFrom: '$blockObject',
      allowChildren: ['htmlEmbed', 'rawHtml'],
      allowAttributes: ['backgroundImage', 'backgroundColor', 'blockType'],
    })

    schema.register('column', {
      isLimit: true,
      allowIn: 'simpleBox',
      allowWhere: '$block',
      allowAttributes: ['flex-size'],
      allowChildren: [
        'imageBlock',
        'imageInline',
        'heading1',
        'heading2',
        'heading3',
        'paragraph',
        'media',
        'videoPlayer',
        'iframePlayer',
        'rawHtml',
        'htmlEmbed',
        'audioPlayer',
        'table',
        'tr',
        'td',
        'th',
        'htmlEmbed',
        'buttonWrapper',
        'codeBlock',
        'simpleButton',
        'imageWrapper',
        'columnHeader',
        'audioPlayer',
        'audioPlayer',
        'accordionWrapper',
        'imageHotspotContainer',
        'dynamicList',
        'imageCarouselWrapper',
      ],
    })
    schema.register('columnHeader', {
      isLimit: true,
      allowIn: 'column',
      allowWhere: '$block',
      allowChildren: [
        'imageBlock',
        'imageInline',
        'heading1',
        'heading2',
        'heading3',
        'paragraph',
        'media',
        'videoPlayer',
        'iframePlayer',
        'table',
        'tr',
        'td',
        'th',
        'htmlEmbed',
        'buttonWrapper',
        'codeBlock',
        'simpleButton',
        'imageWrapper',
        'dynamicList',
      ],
    })

    schema.register('dropAreaWide', {
      isObject: true,
      allowWhere: '$block',
      inheritAllFrom: '$blockObject',
    })

    schema.register('dropAreaNarrow', {
      isObject: true,
      allowWhere: '$block',
      inheritAllFrom: '$blockObject',
      allowAttributes: ['dataVisibility'],
    })

    schema.register('buttonWrapper', {
      isLimit: true,
      allowIn: 'column',
      allowWhere: '$block',
      isObject: true,
      allowChildren: ['simpleButton', 'a'],
      allowAttributes: ['dataAlignment', 'dataSizing'],
    })

    schema.register('cardWrapper', {
      isLimit: true,
      allowIn: 'column',
      allowWhere: '$block',
      isObject: true,
      allowChildren: ['cardBox'],
    })

    schema.register('cardBox', {
      isLimit: true,
      allowIn: 'cardWrapper',
      allowWhere: '$block',
      isObject: true,
      allowAttributes: ['type'],
      allowChildren: ['cardHeader', 'cardContent', 'cardIconButton'],
    })

    schema.register('cardHeader', {
      isLimit: true,
      allowIn: 'cardBox',
      isObject: true,
      allowChildren: [
        'paragraph',
        'heading1',
        'heading2',
        'heading3',
        'imageBlock',
        'imageInline',
        'codeBlock',
      ],
    })
    schema.register('cardIconButton', {
      isLimit: true,
      allowIn: 'cardHeader',
      isObject: true,
      allowChildren: [
        'paragraph',
        'heading1',
        'heading2',
        'heading3',
        'imageBlock',
        'imageInline',
        'simpleIcon',
        'codeBlock',
      ],
    })

    schema.register('cardContent', {
      isLimit: true,
      allowIn: 'cardBox',
      allowChildren: [
        'paragraph',
        'heading1',
        'heading2',
        'heading3',
        'imageBlock',
        'imageInline',
        'codeBlock',
      ],
    })

    schema.register('simpleButton', {
      allowIn: 'buttonWrapper',
      allowWhere: '$block',
      isObject: true,
      allowContentOf: '$block',
      allowAttributes: [
        'href',
        'dataSizing',
        'textcolor',
        'text',
        'pdfname',
        'type',
        'targetvalue',
      ],
    })

    schema.register('simpleIcon', {
      allowIn: ['cardIconButton', 'simpleButton'],
      allowWhere: '$block',
      isObject: true,
      allowContentOf: '$block',
      allowAttributes: ['icon', 'iconname', 'width', 'height'],
    })

    schema.register('htmlBox')

    schema.extend('horizontalLine', {
      allowIn: ['column', 'simpleBox', 'quote'],
    })

    schema.extend('paragraph', {
      allowIn: 'column',
      isBlock: true,
    })

    schema.register('quote', {
      isLimit: true,
      allowIn: 'simpleBox',
      allowWhere: '$block',
      allowChildren: [
        'imageBlock',
        'imageInline',
        'heading1',
        'heading2',
        'heading3',
        'paragraph',
        'media',
        'codeBlock',
      ],
    })

    schema.register('imageWrapper', {
      isLimit: true,
      allowIn: 'column',
      allowWhere: '$block',
      allowAttributes: ['aspectratio', 'id'],
      allowChildren: [
        'imageBlock',
        'imageInline',
        'heading1',
        'heading2',
        'heading3',
        'paragraph',
        'media',
        'img',
      ],
    })

    schema.register('imageCarouselWrapper', {
      isLimit: true,
      isObject: true,
      allowIn: 'column',
      allowWhere: '$block',
      allowChildren: ['imageCarouselItem'],
    })

    schema.register('imageCarouselItem', {
      isLimit: true,
      isObject: true,
      allowIn: 'imageCarouselWrapper',
      allowChildren: ['imageWrapper', 'imageBlock', 'imageInline', 'img', 'imageHotspotContainer'],
    })

    schema.register('videoPlayer', {
      isLimit: false,
      allowAttributes: [
        'src',
        'controls',
        'width',
        'height',
        'poster',
        'aspectratio',
        'autoplay',
        'shopClipId',
        'tracks',
        'crossorigin',
        'muted',
        'mustComplete',
        'disableSkipAhead',
        'hideSpeed',
      ],
      isObject: true,
      isLimit: true,
      isBlock: true,
      allowWhere: '$block',
    })
    schema.register('dynamicList', {
      allowWhere: '$block',
      isObject: true,
      isBlock: true,
      allowAttributes: [
        'type',
        'template',
        'grid',
        'id',
        'collections',
        'catalogId',
        'priceTagActive',
        'completionRewardBgColor',
        'startText',
        'resumeText',
        'badgeHeaderTitle',
        'badgeHeaderIcon',
        'clTitle',
        'clLinkLabel',
        'clTitleColor',
      ],
    })
    schema.register('iframePlayer', {
      isLimit: false,
      allowWhere: '$block',
      allowIn: 'paragraph',
      allowAttributes: [
        'src',
        'width',
        'height',
        'frameborder',
        'allow',
        'allowfullscreen',
      ],
      isObject: true,
    })
    schema.register('editorHeader', {
      isLimit: true,
      allowWhere: '$block',
      isObject: true,
      allowContentOf: '$root',
      allowAttributes: ['background'],
      isBlock: true,
    })

    schema.register('flipCardFront', {
      isLimit: true,
      allowIn: 'cardBox',
      isObject: true,
      allowChildren: [
        'paragraph',
        'heading1',
        'heading2',
        'heading3',
        'imageBlock',
        'imageInline',
        'codeBlock',
      ],
    })

    schema.register('flipCardBack', {
      isLimit: true,
      allowIn: 'cardBox',
      isObject: true,
      allowChildren: [
        'paragraph',
        'heading1',
        'heading2',
        'heading3',
        'imageBlock',
        'imageInline',
        'codeBlock',
      ],
    })

    schema.register('audioPlayer', {
      isLimit: false,
      isObject: true,
      isBlock: true,
      allowWhere: '$block',
      allowIn: 'column',
      allowAttributes: ['src'],
    })

    schema.register('accordionWrapper', {
      isLimit: true,
      allowIn: 'column',
      allowWhere: '$block',
      isObject: true,
      allowChildren: ['accordionItem'],
    })

    schema.register('accordionItem', {
      isLimit: true,
      allowIn: 'accordionWrapper',
      allowWhere: '$block',
      isObject: true,
      allowChildren: ['accordionHeader', 'accordionContent'],
      allowAttributes: ['isOpen', 'addOpen'],
    })

    schema.register('accordionHeader', {
      isLimit: true,
      allowIn: 'accordionItem',
      isObject: true,
      allowChildren: [
        'paragraph',
        'heading1',
        'heading2',
        'heading3',
        'imageBlock',
        'imageInline',
        'codeBlock',
      ],
    })

    schema.register('accordionContent', {
      isLimit: true,
      allowIn: 'accordionItem',
      allowChildren: [
        'paragraph',
        'heading1',
        'heading2',
        'heading3',
        'imageBlock',
        'imageInline',
        'htmlEmbed',
        'rawHtml',
        'videoPlayer',
        'audioPlayer',
        'table',
        'codeBlock',
        'simpleBox',
        'simpleBoxWrapper',
        'column',
      ],
    })

    schema.register('tabContainer', {
      isLimit: true,
      allowIn: 'column',
      allowWhere: '$block',
      isObject: true,
      allowChildren: ['tabHeaders', 'tabContents'],
      allowAttributes: ['activeTab', 'type'],
    })

    schema.register('tabHeaders', {
      isLimit: true,
      allowIn: 'tabContainer',
      isObject: true,
      allowChildren: ['tabHeader'],
    })
    // 1. First, update the schema registration for tabHeader
    schema.register('tabHeader', {
      isLimit: true,
      allowIn: 'tabHeaders',
      isObject: true,
      allowAttributes: [
        'tabIndex',
        'tabTitle',
        'isActive',
        'icon', // New: icon attribute
        'backgroundColor', // New: background color
        'textColor', // New: text color
        'text', // New: text content
      ],
      allowChildren: [
        'paragraph',
        'heading1',
        'heading2',
        'heading3',
        'imageBlock',
        'imageInline',
        'media',
        'videoPlayer',
        'iframePlayer',
        'rawHtml',
        'htmlEmbed',
        'audioPlayer',
        'table',
        'tr',
        'td',
        'th',
        'buttonWrapper',
        'cardWrapper',
        'codeBlock',
        'imageWrapper',
        'horizontalLine',
      ],
    })

    schema.register('tabContents', {
      isLimit: true,
      allowIn: 'tabContainer',
      isObject: true,
      allowChildren: ['tabContent'],
    })

    schema.register('tabContent', {
      isLimit: true,
      allowIn: 'tabContents',
      allowAttributes: ['tabIndex', 'isActive'],
      allowChildren: [
        'paragraph',
        'heading1',
        'heading2',
        'heading3',
        'imageBlock',
        'imageInline',
        'media',
        'videoPlayer',
        'iframePlayer',
        'rawHtml',
        'htmlEmbed',
        'audioPlayer',
        'table',
        'tr',
        'td',
        'th',
        'buttonWrapper',
        'cardWrapper',
        'codeBlock',
        'imageWrapper',
        'horizontalLine',
        'simpleBox',
        'simpleBoxWrapper',
        'column',
      ],
    })
    // In _defineSchema() method, update these registrations:

    schema.register('imageHotspot', {
      isObject: true,
      allowIn: 'imageHotspotContainer',
      allowAttributes: [
        'x',
        'y',
        'title',
        'id',
        'isVisible',
        'backgroundColor',
        'iconColor',
        'icon',
        'iconName',
      ],
      // Remove allowChildren - hotspot has no children now
    })

    schema.register('imageHotspotContainer', {
      isObject: true,
      allowWhere: '$block',
      allowIn: ['column', 'imageCarouselItem'],
      allowAttributes: ['src', 'alt', 'width', 'height', 'id'],
      allowChildren: ['imageHotspot', 'imageWrapper', 'imageHotspotContent'], // Add imageHotspotContent here
    })

    schema.register('imageHotspotContent', {
      isLimit: true,
      allowIn: 'imageHotspotContainer',
      allowAttributes: ['hotspotId'],
      allowChildren: [
        'paragraph',
        'heading1',
        'heading2',
        'heading3',
        'imageBlock',
        'imageInline',
        'htmlEmbed',
        'rawHtml',
        'videoPlayer',
        'audioPlayer',
        'table',
        'codeBlock',
        'simpleBox',
        'simpleBoxWrapper',
        'column',
      ],
    })
  }

  _defineConverters() {
    const conversion = this.editor.conversion
    const editor = this.editor

    // Flip Card Front converters
    conversion.for('upcast').elementToElement({
      model: 'flipCardFront',
      view: {
        name: 'div',
        classes: ['flip-card-front'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'flipCardFront',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createContainerElement('div', {
          class: 'flip-card-front',
        })

        // Create flip button for the back side (same as in editingDowncast)
        const flipButton = viewWriter.createUIElement(
          'button',
          {
            class: 'flip-card-button',
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            domElement.innerHTML = `
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none">
                        <path d="M12.6904 13.1298C11.7146 14.1024 10.4727 14.7644 9.12125 15.0323C7.76977 15.3002 6.36924 15.162 5.0962 14.6351C3.82316 14.1082 2.73461 13.2162 1.96776 12.0716C1.20091 10.9269 0.7901 9.58089 0.787109 8.20312" stroke="#838D98" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="round"/>
                        <path d="M12.1291 16.7081L12.8224 13.8981C12.8544 13.7757 12.8611 13.648 12.8423 13.5229C12.8235 13.3977 12.7795 13.2777 12.713 13.17C12.6465 13.0624 12.5588 12.9693 12.4553 12.8965C12.3518 12.8237 12.2346 12.7727 12.1108 12.7464L9.30078 12.0439" stroke="#838D98" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2.65039 4.86855C3.62615 3.89561 4.86814 3.23337 6.21976 2.96532C7.57137 2.69728 8.97209 2.83543 10.2453 3.36236C11.5185 3.88929 12.6072 4.78141 13.3741 5.92622C14.141 7.07103 14.5517 8.41728 14.5546 9.79522" stroke="#838D98" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="round"/>
                        <path d="M3.21166 1.29102L2.51833 4.10102C2.48653 4.22352 2.47989 4.35121 2.4988 4.47635C2.51772 4.6015 2.56181 4.72152 2.6284 4.82915C2.69499 4.93679 2.7827 5.02981 2.88624 5.1026C2.98979 5.1754 3.10701 5.22645 3.23083 5.25268L6.03999 5.95518" stroke="#838D98" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
            `

            // Add click event for flipping
            domElement.addEventListener('click', event => {
              event.stopPropagation()

              // Find the closest flip-card element
              const flipCard = domElement.closest('.card-box[type="flipCard"]')
              if (flipCard) {
                // Toggle the flipped class
                flipCard.classList.toggle('flipped')
              }
            })

            return domElement
          },
        )

        // Add the flip button to the back side
        viewWriter.insert(viewWriter.createPositionAt(div, 'end'), flipButton)

        return div
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'flipCardFront',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('div', {
          class: 'flip-card-front',
        })

        // Create flip button
        const flipButton = viewWriter.createUIElement(
          'button',
          {
            class: 'flip-card-button',
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            domElement.innerHTML = `
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none">
                        <path d="M12.6904 13.1298C11.7146 14.1024 10.4727 14.7644 9.12125 15.0323C7.76977 15.3002 6.36924 15.162 5.0962 14.6351C3.82316 14.1082 2.73461 13.2162 1.96776 12.0716C1.20091 10.9269 0.7901 9.58089 0.787109 8.20312" stroke="#838D98" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="round"/>
                        <path d="M12.1291 16.7081L12.8224 13.8981C12.8544 13.7757 12.8611 13.648 12.8423 13.5229C12.8235 13.3977 12.7795 13.2777 12.713 13.17C12.6465 13.0624 12.5588 12.9693 12.4553 12.8965C12.3518 12.8237 12.2346 12.7727 12.1108 12.7464L9.30078 12.0439" stroke="#838D98" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2.65039 4.86855C3.62615 3.89561 4.86814 3.23337 6.21976 2.96532C7.57137 2.69728 8.97209 2.83543 10.2453 3.36236C11.5185 3.88929 12.6072 4.78141 13.3741 5.92622C14.141 7.07103 14.5517 8.41728 14.5546 9.79522" stroke="#838D98" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="round"/>
                        <path d="M3.21166 1.29102L2.51833 4.10102C2.48653 4.22352 2.47989 4.35121 2.4988 4.47635C2.51772 4.6015 2.56181 4.72152 2.6284 4.82915C2.69499 4.93679 2.7827 5.02981 2.88624 5.1026C2.98979 5.1754 3.10701 5.22645 3.23083 5.25268L6.03999 5.95518" stroke="#838D98" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
            `

            // Add click event for flipping
            domElement.addEventListener('click', event => {
              event.stopPropagation()

              // Find the closest flip-card element
              const flipCard = domElement.closest('.card-box[type="flipCard"]')
              if (flipCard) {
                // Toggle the flipped class
                flipCard.classList.toggle('flipped')
              }
            })

            return domElement
          },
        )

        // Add the flip button to the front side
        viewWriter.insert(viewWriter.createPositionAt(div, 'end'), flipButton)

        return toWidgetEditable(div, viewWriter)
      },
    })

    // Flip Card Back converters
    conversion.for('upcast').elementToElement({
      model: 'flipCardBack',
      view: {
        name: 'div',
        classes: ['flip-card-back'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'flipCardBack',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createContainerElement('div', {
          class: 'flip-card-back',
        })

        // Create flip button for the back side (same as in editingDowncast)
        const flipButton = viewWriter.createUIElement(
          'button',
          {
            class: 'flip-card-button',
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            domElement.innerHTML = `
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none">
                        <path d="M12.6904 13.1298C11.7146 14.1024 10.4727 14.7644 9.12125 15.0323C7.76977 15.3002 6.36924 15.162 5.0962 14.6351C3.82316 14.1082 2.73461 13.2162 1.96776 12.0716C1.20091 10.9269 0.7901 9.58089 0.787109 8.20312" stroke="#838D98" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="round"/>
                        <path d="M12.1291 16.7081L12.8224 13.8981C12.8544 13.7757 12.8611 13.648 12.8423 13.5229C12.8235 13.3977 12.7795 13.2777 12.713 13.17C12.6465 13.0624 12.5588 12.9693 12.4553 12.8965C12.3518 12.8237 12.2346 12.7727 12.1108 12.7464L9.30078 12.0439" stroke="#838D98" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2.65039 4.86855C3.62615 3.89561 4.86814 3.23337 6.21976 2.96532C7.57137 2.69728 8.97209 2.83543 10.2453 3.36236C11.5185 3.88929 12.6072 4.78141 13.3741 5.92622C14.141 7.07103 14.5517 8.41728 14.5546 9.79522" stroke="#838D98" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="round"/>
                        <path d="M3.21166 1.29102L2.51833 4.10102C2.48653 4.22352 2.47989 4.35121 2.4988 4.47635C2.51772 4.6015 2.56181 4.72152 2.6284 4.82915C2.69499 4.93679 2.7827 5.02981 2.88624 5.1026C2.98979 5.1754 3.10701 5.22645 3.23083 5.25268L6.03999 5.95518" stroke="#838D98" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
            `

            // Add click event for flipping
            domElement.addEventListener('click', event => {
              event.stopPropagation()

              // Find the closest flip-card element
              const flipCard = domElement.closest('.card-box[type="flipCard"]')
              if (flipCard) {
                // Toggle the flipped class
                flipCard.classList.toggle('flipped')
              }
            })

            return domElement
          },
        )

        // Add the flip button to the back side
        viewWriter.insert(viewWriter.createPositionAt(div, 'end'), flipButton)

        return div
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'flipCardBack',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('div', {
          class: 'flip-card-back',
        })

        // Create flip button for the back side
        const flipButton = viewWriter.createUIElement(
          'button',
          {
            class: 'flip-card-button',
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            domElement.innerHTML = `
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none">
                        <path d="M12.6904 13.1298C11.7146 14.1024 10.4727 14.7644 9.12125 15.0323C7.76977 15.3002 6.36924 15.162 5.0962 14.6351C3.82316 14.1082 2.73461 13.2162 1.96776 12.0716C1.20091 10.9269 0.7901 9.58089 0.787109 8.20312" stroke="#838D98" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="round"/>
                        <path d="M12.1291 16.7081L12.8224 13.8981C12.8544 13.7757 12.8611 13.648 12.8423 13.5229C12.8235 13.3977 12.7795 13.2777 12.713 13.17C12.6465 13.0624 12.5588 12.9693 12.4553 12.8965C12.3518 12.8237 12.2346 12.7727 12.1108 12.7464L9.30078 12.0439" stroke="#838D98" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2.65039 4.86855C3.62615 3.89561 4.86814 3.23337 6.21976 2.96532C7.57137 2.69728 8.97209 2.83543 10.2453 3.36236C11.5185 3.88929 12.6072 4.78141 13.3741 5.92622C14.141 7.07103 14.5517 8.41728 14.5546 9.79522" stroke="#838D98" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="round"/>
                        <path d="M3.21166 1.29102L2.51833 4.10102C2.48653 4.22352 2.47989 4.35121 2.4988 4.47635C2.51772 4.6015 2.56181 4.72152 2.6284 4.82915C2.69499 4.93679 2.7827 5.02981 2.88624 5.1026C2.98979 5.1754 3.10701 5.22645 3.23083 5.25268L6.03999 5.95518" stroke="#838D98" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
            `

            // Add click event for flipping
            domElement.addEventListener('click', event => {
              event.stopPropagation()

              // Find the closest flip-card element
              const flipCard = domElement.closest('.card-box[type="flipCard"]')
              if (flipCard) {
                // Toggle the flipped class
                flipCard.classList.toggle('flipped')
              }
            })

            return domElement
          },
        )

        // Add the flip button to the back side
        viewWriter.insert(viewWriter.createPositionAt(div, 'end'), flipButton)

        return toWidgetEditable(div, viewWriter)
      },
    })

    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: ['editor-header'],
      },
      model: (viewElement, { writer }) => {
        return writer.createElement('editorHeader', {
          class: 'editor-header',
        })
      },
    })

    // Add dataDowncast converter (model to HTML for saving)
    conversion.for('dataDowncast').elementToElement({
      model: 'editorHeader',
      view: {
        name: 'p',
        classes: ['empty-dom'],
      },
    })

    // Add editingDowncast converter (model to editable widget)
    conversion.for('editingDowncast').elementToElement({
      model: 'editorHeader',
      view: (modelElement, { writer: viewWriter }) => {
        const headerId = `editor-header-${Date.now()}`

        const savedLandingPageData =
          editor.config.get('simpleBox.landingPageData') || []
        // A page without a logo shows the workspace logo, as learners see it.
        const landingPageData = Array.isArray(savedLandingPageData)
          ? savedLandingPageData
          : withWorkspaceLogo(savedLandingPageData)
        const course = editor.config.get('simpleBox.course') || []
        const collection = editor.config.get('simpleBox.collection') || []
        // A portal page (Catalog, About…) shows its own name in the header,
        // not the showcase collection's.
        const portalPage = getPortalPageEditing()
        const headerCollection = portalPage
          ? {
              ...collection,
              name: portalPage.name,
              collectionName: portalPage.name,
              description: landingPageData?.description || '',
            }
          : collection
        const collectionSettings =
          editor.config.get('simpleBox.collectionSettings') || {}
        const pageUrl = editor.config.get('simpleBox.pageUrl') || ''
        const subPage = editor.config.get('simpleBox.subPage') || ''
        const theme = editor.config.get('simpleBox.theme')
        const collectionGeneralSettings =
          editor.config.get('simpleBox.collectionGeneralSettings') || {}
        const certificatePayment =
          editor.config.get('simpleBox.certificatePayment') || null
        const shouldShowHeader =
          (pageUrl === 'mini-course' && subPage === 'editor') ||
          (pageUrl === 'collection' && subPage === 'editor')

        const existingHeaders = document.querySelectorAll('.editor-header')
        if (existingHeaders.length > 0) {
          for (let i = 1; i < existingHeaders.length; i++) {
            existingHeaders[i].remove()
          }
        }

        // If conditions aren't met, just return a hidden container
        if (!shouldShowHeader) {
          const hiddenContainer = viewWriter.createContainerElement('div', {
            class: 'editor-header-hidden',
            style: 'display: none;',
            'data-id': headerId,
          })
          return toWidget(hiddenContainer, viewWriter, {
            label: 'hidden header widget',
            hasSelectionHandle: false,
          })
        }
        // Otherwise, continue with normal header creation
        const widgetContainer = viewWriter.createContainerElement('div', {
          class: 'editor-header',
          'data-non-draggable': 'true',
          'data-id': headerId,
        })

        // Create overlay with edit icon
        const overlay = viewWriter.createUIElement(
          'div',
          { class: 'editor-header-overlay' },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            // Create edit button with icon and text
            const button = domDocument.createElement('button')
            button.className = 'editor-header-edit-button'
            button.innerHTML = `${editIconSvg} <span>Edit header</span>`

            domElement.appendChild(button)
            button.addEventListener('click', () => {
              handleHeaderSettingsModal(
                editor.config.get('videoGalleryPlugin.dispatch'),
                pageUrl,
              )
            })

            return domElement
          },
        )
        const landingPageSection = landingPageData?.sections.find(
          x => x.type === editorEnum.headerType,
        )

        const backgroundImageUrl =
          landingPageData &&
            landingPageData.sections &&
            landingPageSection &&
            landingPageSection.content &&
            landingPageSection.content.image
            ? landingPageSection.content.image.startsWith('https')
              ? landingPageSection.content.image
              : 'https://cdn.minicoursegenerator.com/' +
              landingPageSection.content.image
            : '/default-image.jpg'
        let landingPageListElement
        if (theme === 0) {
          landingPageListElement = viewWriter.createRawElement(
            'div',
            {
              class: 'editor-header-container',
            },
            function (domElement) {
              var landingPageSection = landingPageData?.sections.find(
                x => x.type === editorEnum.headerType,
              )
              const type = landingPageSection.content.type
              if (type !== 2 && type !== 3) {
                domElement.style.background = headerBackgroundCss(
                  backgroundImageUrl,
                  0.3,
                  landingPageData?.primaryColor,
                )
              } else if (type === 3) {
                domElement.style.background = headerBackgroundCss(
                  backgroundImageUrl,
                  0.3,
                  landingPageData?.primaryColor,
                )
                domElement.style.position = 'relative'
              } else {
                const landingPageSection = landingPageData?.sections?.find(
                  x => x.type === editorEnum.headerType,
                )
                // For Hero type (2), apply background color from settings
                const backgroundColor =
                  landingPageSection?.content?.backgroundColor || '#FFFFFF'
                domElement.style.backgroundColor = backgroundColor
              }

              setTimeout(() => {
                const allHeaders = document.querySelectorAll('.editor-header')
                if (allHeaders.length > 1) {
                  // Only keep this one
                  Array.from(allHeaders)
                    .filter(el => el.getAttribute('data-id') !== headerId)
                    .forEach(el => el.remove())
                }
              }, 0)

              let contentHTML = ''
              if (subPage === 'editor') {
                const landingPageSection = landingPageData?.sections?.find(
                  x => x.type === editorEnum.headerType,
                )
                // Select template based on the header type
                const headerType = landingPageSection?.content?.type || 1

                switch (headerType) {
                  case 2: // HERO
                    contentHTML = headerHeroTemplate(
                      landingPageData,
                      course,
                      headerCollection,
                      pageUrl,
                      collectionSettings,
                      collectionGeneralSettings,
                      certificatePayment,
                    )
                    break
                  case 3: // PROMO
                    contentHTML = headerPromoTemplate(
                      landingPageData,
                      course,
                      headerCollection,
                      pageUrl,
                      collectionSettings,
                      collectionGeneralSettings,
                      certificatePayment,
                    )
                    break
                  case 1: // DEFAULT
                  default:
                    contentHTML = headerDefaultTemplate(
                      landingPageData,
                      course,
                      headerCollection,
                      pageUrl,
                      collectionSettings,
                      collectionGeneralSettings,
                    )
                    break
                }
              }

              const positionMap = {
                left: 'flex-start',
                center: 'center',
                right: 'flex-end',
              }

              const contentContainer = document.createElement('div')
              contentContainer.className = 'editor-header-content'
              contentContainer.innerHTML = contentHTML
              if (
                landingPageData &&
                landingPageData &&
                landingPageData.sections &&
                landingPageSection &&
                landingPageSection.content &&
                landingPageSection.content.alignment
              ) {
                contentContainer.style.textAlign =
                  landingPageSection.content.alignment
                contentContainer.style.alignItems =
                  positionMap[landingPageSection.content.alignment]
              }

              domElement.appendChild(contentContainer)
            },
          )
        } else {
          landingPageListElement = viewWriter.createRawElement(
            'div',
            {
              class: 'theme-header-container',
            },
            function (domElement) {
              // Add just an h1 element for non-zero theme
              const themeTitle = document.createElement('h1')
              themeTitle.className = 'theme-title'
              themeTitle.textContent = course.name || 'Course Title'

              domElement.appendChild(themeTitle)
            },
          )
        }

        // Check if there's a dropAreaWide element after this header
        const shouldShowAddBelowButton = (() => {
          const root = editor.model.document.getRoot()
          const headerIndex = Array.from(root.getChildren()).findIndex(
            child => child.name === 'editorHeader',
          )

          if (headerIndex !== -1 && headerIndex < root.childCount - 1) {
            const nextElement = Array.from(root.getChildren())[headerIndex + 1]
            return nextElement.name !== 'dropAreaWide'
          }

          return true
        })()

        // Only create and add the addBelowButton if it should be shown
        if (shouldShowAddBelowButton) {
          const addBelowButton = viewWriter.createUIElement(
            'div',
            {
              class: 'editor-header-add-button editor-header-add-below',
            },
            function (domDocument) {
              const domElement = this.toDomElement(domDocument)

              domElement.innerHTML = `
              <div class="ck ck-tooltip__container">
                <button class="editor-header-button" data-tooltip="Add Block">
                 ${addBlockSvg}
                </button>
              </div>
            `

              const button = domElement.querySelector('button')
              const tooltipText = button.dataset.tooltip

              const tooltip = document.createElement('div')
              tooltip.className = 'ck ck-tooltip ck-tooltip_s'
              tooltip.innerHTML = `<div class="ck ck-tooltip__text">${tooltipText}</div>`

              button.addEventListener('mouseenter', () => {
                button.parentElement.appendChild(tooltip)
                tooltip.style.bottom = '-66px'
                tooltip.style.left = '50%'
                tooltip.style.transform = 'translateX(-50%)'
                tooltip.style.background = '#303031'
                tooltip.style.padding = '4px'
                tooltip.style.borderRadius = '4px'
                tooltip.style.zIndex = '9999999'
              })

              button.addEventListener('mouseleave', () => {
                tooltip.remove()
              })

              button.addEventListener('click', () => {
                editor.execute('addContentBelow')
              })

              return domElement
            },
          )

          // Add the add-below button
          viewWriter.insert(
            viewWriter.createPositionAt(widgetContainer, 'end'),
            addBelowButton,
          )
        }

        // Insert the overlay with edit icon into the widget container
        if (theme === 0) {
          viewWriter.insert(
            viewWriter.createPositionAt(widgetContainer, 0),
            overlay,
          )
        }

        // Add the landing page list element (which now includes the settings button)
        viewWriter.insert(
          viewWriter.createPositionAt(widgetContainer, 0),
          landingPageListElement,
        )

        // Add direct click handling for the button
        setTimeout(() => {
          const headerElement = document.querySelector(
            `[data-id="${headerId}"]`,
          )
          const button = headerElement?.querySelector(
            '.editor-header-edit-button',
          )

          if (button) {
            button.addEventListener('click', event => {
              event.stopPropagation()

              // You can handle the edit action here
              try {
                // Trigger your edit action
                // For example:
                editor.execute('editHeader')

                // Or call a custom handler if defined:
                const onEditHeader = editor.config.get('simpleBox.onEditHeader')
                if (typeof onEditHeader === 'function') {
                  onEditHeader()
                }

                // If there's a dispatch function available
                const dispatch = editor.config.get(
                  'videoGalleryPlugin.dispatch',
                )
                if (dispatch) {
                  dispatch({
                    type: 'OPEN_HEADER_EDIT_MODAL',
                    payload: { headerId },
                  })
                }
              } catch (e) {
                console.error('Error handling header edit button click:', e)
              }
            })
          }
        }, 100) // Slightly longer timeout to ensure DOM is ready

        return toWidget(widgetContainer, viewWriter, {
          label: 'landing page header widget',
          hasSelectionHandle: false,
        })
      },
    })
    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: ['dynamic-list'],
      },
      model: (viewElement, { writer }) => {
        const type = viewElement.getAttribute('data-type') || ''
        const template = viewElement.getAttribute('data-template') || ''
        const grid = viewElement.getAttribute('data-grid') || ''
        const collections = viewElement.getAttribute('data-collections') || ''
        const catalogId = viewElement.getAttribute('data-catalog-id') || ''
        const priceTagActive =
          viewElement.getAttribute('data-price-tag-active') === 'true'
        const completionRewardBgColor =
          viewElement.getAttribute('data-completion-reward-bg-color') || ''
        const startText = viewElement.getAttribute('data-start') || ''
        const resumeText = viewElement.getAttribute('data-resume') || ''
        const badgeHeaderTitle = viewElement.getAttribute('data-badge-header-title') || ''
        const badgeHeaderIcon = viewElement.getAttribute('data-badge-header-icon') || ''
        const clTitle = viewElement.getAttribute('data-cl-title') || ''
        const clLinkLabel = viewElement.getAttribute('data-cl-link-label') || ''
        const clTitleColor = viewElement.getAttribute('data-cl-title-color') || ''

        return writer.createElement('dynamicList', {
          type: type,
          template: template,
          grid: grid,
          collections: collections,
          catalogId: catalogId,
          priceTagActive: priceTagActive,
          completionRewardBgColor: completionRewardBgColor,
          startText: startText,
          resumeText: resumeText,
          badgeHeaderTitle: badgeHeaderTitle,
          badgeHeaderIcon: badgeHeaderIcon,
          clTitle: clTitle,
          clLinkLabel: clLinkLabel,
          clTitleColor: clTitleColor,
          class: 'dynamic-list',
        })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'dynamicList',
      view: (modelElement, { writer: viewWriter }) => {
        const type = modelElement.getAttribute('type') || ''
        const template = modelElement.getAttribute('template') || ''
        const grid = modelElement.getAttribute('grid') || ''
        const collections = modelElement.getAttribute('collections') || ''
        const catalogId = modelElement.getAttribute('catalogId') || ''
        const priceTagActive =
          modelElement.getAttribute('priceTagActive') || false
        const completionRewardBgColor =
          modelElement.getAttribute('completionRewardBgColor') || ''
        const startText = modelElement.getAttribute('startText') || ''
        const resumeText = modelElement.getAttribute('resumeText') || ''
        const badgeHeaderTitle = modelElement.getAttribute('badgeHeaderTitle') || ''
        const badgeHeaderIcon = modelElement.getAttribute('badgeHeaderIcon') || ''
        const clTitle =
          modelElement.getAttribute('clTitle') || CONTINUE_LEARNING_TITLE
        const clLinkLabel =
          modelElement.getAttribute('clLinkLabel') || CONTINUE_LEARNING_LINK_LABEL
        const clTitleColor = modelElement.getAttribute('clTitleColor') || ''

        let listId = modelElement.getAttribute('listId')
        if (!listId) {
          listId = `dynamic-list-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`
        }

        const attrs = {
          class: 'dynamic-list',
          'data-id': listId,
          'data-type': type,
          'data-template': template,
          'data-grid': grid,
          'data-collections': collections,
          'data-catalog-id': catalogId,
          'data-price-tag-active': priceTagActive.toString(),
        }

        if (completionRewardBgColor) {
          attrs['data-completion-reward-bg-color'] = completionRewardBgColor
        }
        if (startText) {
          attrs['data-start'] = startText
        }
        if (resumeText) {
          attrs['data-resume'] = resumeText
        }
        if (badgeHeaderTitle) {
          attrs['data-badge-header-title'] = badgeHeaderTitle
        }
        if (badgeHeaderIcon) {
          attrs['data-badge-header-icon'] = badgeHeaderIcon
        }
        if (type === 'continueLearning') {
          attrs['data-cl-title'] = clTitle
          attrs['data-cl-link-label'] = clLinkLabel
          if (clTitleColor) attrs['data-cl-title-color'] = clTitleColor
        }

        return viewWriter.createContainerElement('div', attrs)
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'dynamicList',
      triggerBy: {
        attributes: [
          'badgeHeaderTitle',
          'badgeHeaderIcon',
          'catalogId',
          'clTitle',
          'clLinkLabel',
          'clTitleColor',
        ],
      },
      view: (modelElement, { writer: viewWriter }) => {
        let footerId = modelElement.getAttribute('id')
        if (!footerId) {
          footerId = `dynamic-list-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`
        }

        const type = modelElement.getAttribute('type') || ''
        const template = modelElement.getAttribute('template') || ''
        const grid = modelElement.getAttribute('grid') || ''
        const collections = modelElement.getAttribute('collections') || ''
        const catalogId = modelElement.getAttribute('catalogId') || ''
        const priceTagActive =
          modelElement.getAttribute('priceTagActive') || false
        const completionRewardBgColor =
          modelElement.getAttribute('completionRewardBgColor') || ''
        const startText = modelElement.getAttribute('startText') || ''
        const resumeText = modelElement.getAttribute('resumeText') || ''
        const badgeHeaderTitle = modelElement.getAttribute('badgeHeaderTitle') || ''
        const badgeHeaderIcon = modelElement.getAttribute('badgeHeaderIcon') || ''
        const clTitle =
          modelElement.getAttribute('clTitle') || CONTINUE_LEARNING_TITLE
        const clLinkLabel =
          modelElement.getAttribute('clLinkLabel') || CONTINUE_LEARNING_LINK_LABEL
        const clTitleColor = modelElement.getAttribute('clTitleColor') || ''

        const courseCards = editor.config.get('simpleBox.courseCards') || []
        const landingPageData =
          editor.config.get('simpleBox.landingPageData') || []
        const collectionCards =
          editor.config.get('simpleBox.collectionCards') || []
        const pageUrl = editor.config.get('simpleBox.pageUrl') || ''
        const subPage = editor.config.get('simpleBox.subPage') || ''
        const collectionGeneralSettings =
          editor.config.get('simpleBox.collectionGeneralSettings') || {}

        const shouldShowFooter =
          (pageUrl === 'mini-course' && subPage === 'editor') ||
          (pageUrl === 'collection' && subPage === 'editor')

        const widgetAttrs = {
          class: 'dynamic-list',
          'data-non-draggable': 'true',
          'data-id': footerId,
          'data-type': type,
          'data-template': template,
          'data-grid': grid,
          'data-collections': collections,
          'data-catalog-id': catalogId,
          'data-price-tag-active': priceTagActive.toString(),
        }

        if (completionRewardBgColor) {
          widgetAttrs['data-completion-reward-bg-color'] = completionRewardBgColor
        }
        if (startText) {
          widgetAttrs['data-start'] = startText
        }
        if (resumeText) {
          widgetAttrs['data-resume'] = resumeText
        }
        if (badgeHeaderTitle) {
          widgetAttrs['data-badge-header-title'] = badgeHeaderTitle
        }
        if (badgeHeaderIcon) {
          widgetAttrs['data-badge-header-icon'] = badgeHeaderIcon
        }
        if (type === 'continueLearning') {
          widgetAttrs['data-cl-title'] = clTitle
          widgetAttrs['data-cl-link-label'] = clLinkLabel
          if (clTitleColor) widgetAttrs['data-cl-title-color'] = clTitleColor
        }

        // Otherwise, continue with normal footer creation
        const widgetContainer = viewWriter.createContainerElement('div', widgetAttrs)

        const landingPageListElement = viewWriter.createRawElement(
          'div',
          {
            class: 'landing-page-list-container',
          },
          function (domElement) {
            const settingsWrapper = document.createElement('div')
            settingsWrapper.className = 'editor-key'
            settingsWrapper.style.marginTop = '10px'
            const urlName =
              pageUrl === 'mini-course' ? 'course' : 'learning path'

            settingsWrapper.innerHTML = `
          <div class="ck ck-tooltip__container_key">
            <button class="dynamic-list-key-button" data-tooltip="This Dynamic block comes from the ${urlName} content. It cannot be edited here.">
              ${keySvg}
            </button>
          </div>
        `
            const button = settingsWrapper.querySelector('button')
            const tooltipText = button.dataset.tooltip

            const tooltip = document.createElement('div')
            tooltip.className = 'ck ck-tooltip ck-tooltip_s'
            tooltip.innerHTML = `<div class="ck ck-tooltip__text">${tooltipText}</div>`

            button.addEventListener('mouseenter', () => {
              button.parentElement.appendChild(tooltip)
              tooltip.style.width = '240px'
              tooltip.style.background = '#303031'
              tooltip.style.padding = '4px'
              tooltip.style.borderRadius = '4px'
              tooltip.style.zIndex = '9999999'
            })

            button.addEventListener('mouseleave', () => {
              tooltip.remove()
            })

            let contentHTML = ''
            if (modelElement.getAttribute('type') === 'courseOutline1') {
              contentHTML = createCourseTemplate(courseCards, landingPageData)
            } else if (modelElement.getAttribute('type') === 'courseOutline2') {
              contentHTML = createCourseTemplateAccordion(
                courseCards,
                landingPageData,
              )
            } else if (modelElement.getAttribute('type') === 'courseList1') {
              this.dispatch = editor.config.get('videoGalleryPlugin.dispatch')
              const isEdit = true
              contentHTML = CreateCourseList(
                collectionCards,
                this.dispatch,
                isEdit,
              )
            } else if (modelElement.getAttribute('type') === 'courseList2') {
              this.dispatch = editor.config.get('videoGalleryPlugin.dispatch')
              const isEdit = true
              contentHTML = CreateCourseList2(
                collectionCards,
                this.dispatch,
                isEdit,
              )
            } else if (modelElement.getAttribute('type') === 'collectionList') {
              this.dispatch = editor.config.get('videoGalleryPlugin.dispatch')
              const isEdit = true

              contentHTML = '<div class="loading-collections"></div>'

              CreateCollectionList(
                this.dispatch,
                isEdit,
                collections,
                priceTagActive,
              )
                .then(html => {
                  const contentContainer = domElement.querySelector(
                    '.landing-page-content',
                  )
                  if (contentContainer) {
                    contentContainer.innerHTML = html
                  }
                })
                .catch(error => {
                  console.error('Failed to load collections:', error)
                  const contentContainer = domElement.querySelector(
                    '.landing-page-content',
                  )
                  if (contentContainer) {
                    contentContainer.innerHTML =
                      '<div class="error-message">Failed to load learning paths</div>'
                  }
                })
            } else if (modelElement.getAttribute('type') === 'catalog') {
              const collection = editor.config.get('simpleBox.collection') || {}

              contentHTML = '<div class="loading-collections"></div>'

              CreateCatalogList(grid, priceTagActive, catalogId)
                .then(html => {
                  const contentContainer = domElement.querySelector(
                    '.landing-page-content',
                  )
                  if (contentContainer) {
                    contentContainer.innerHTML = html
                    initCatalogFilterEvents(contentContainer, {
                      catalogId,
                      priceTagActive,
                    })
                  }
                })
                .catch(error => {
                  console.error('Failed to load catalog:', error)
                  const contentContainer = domElement.querySelector(
                    '.landing-page-content',
                  )
                  if (contentContainer) {
                    contentContainer.innerHTML =
                      '<div class="error-message">Failed to load catalog</div>'
                  }
                })
            } else if (modelElement.getAttribute('type') === 'continueLearning') {
              contentHTML = '<div class="loading-collections"></div>'

              CreateContinueLearning(grid, priceTagActive, {
                title: clTitle,
                linkLabel: clLinkLabel,
                titleColor: clTitleColor,
              })
                .then(({ html, items }) => {
                  const contentContainer = domElement.querySelector(
                    '.landing-page-content',
                  )
                  if (contentContainer) {
                    contentContainer.innerHTML = html
                    initContinueLearningEvents(contentContainer, {
                      items,
                      // Typing over the heading in place writes the same attribute the
                      // block settings' Title field does.
                      onTitleChange: nextTitle => {
                        editor.model.change(writer => {
                          writer.setAttribute('clTitle', nextTitle, modelElement)
                        })
                      },
                    })
                  }
                })
                .catch(error => {
                  console.error('Failed to load continue learning:', error)
                  const contentContainer = domElement.querySelector(
                    '.landing-page-content',
                  )
                  if (contentContainer) {
                    contentContainer.innerHTML =
                      '<div class="error-message">Failed to load continue learning</div>'
                  }
                })
            } else if (modelElement.getAttribute('type') === 'badge') {
              const collection = editor.config.get('simpleBox.collection') || {}
              const collectionId = collection.id
              const rewardBgColor = completionRewardBgColor || 'linear-gradient(90deg, #004483 0%, #001C36 100%)'
              const resetReward = !!window.badgeListResetReward

              contentHTML = '<div class="loading-badges"></div>'

              CreateBadgeList(collectionId, rewardBgColor, badgeHeaderTitle, badgeHeaderIcon, startText || 'Start', false, resetReward)
                .then(html => {
                  const contentContainer = domElement.querySelector(
                    '.landing-page-content',
                  )
                  if (contentContainer) {
                    contentContainer.innerHTML = html
                    // Clear flag only after actual DOM update
                    window.badgeListResetReward = false
                    const rewardCard = contentContainer.querySelector(
                      '.badge-completion-reward-card',
                    )
                    if (rewardCard) {
                      rewardCard.style.background = rewardBgColor
                      // Remove any inline background from text container (may exist from old saved data)
                      const textContainer = rewardCard.querySelector('.badge-reward-text-container')
                      if (textContainer) {
                        textContainer.style.background = ''
                      }
                    }
                    const rewardSection = contentContainer.querySelector(
                      '.badge-completion-reward',
                    )
                    initRewardEvents(rewardSection, collectionId, editor, rewardBgColor)
                    initHeaderEvents(contentContainer, editor, modelElement)

                    // Edit button click → navigate to course completion page
                    contentContainer.querySelectorAll('.badge-list-card-edit-btn').forEach(btn => {
                      btn.addEventListener('click', e => {
                        e.preventDefault()
                        e.stopPropagation()
                        const courseId = btn.getAttribute('data-course-id')
                        if (courseId) {
                          window.open(`/mini-course/${courseId}/completion`, '_blank')
                        }
                      })
                    })
                  }
                })
                .catch(error => {
                  console.error('Failed to load badges:', error)
                  const contentContainer = domElement.querySelector(
                    '.landing-page-content',
                  )
                  if (contentContainer) {
                    contentContainer.innerHTML =
                      '<div class="error-message">Failed to load badges</div>'
                  }
                })
            }

            const contentContainer = document.createElement('div')
            contentContainer.className = 'landing-page-content'
            contentContainer.innerHTML = contentHTML
            domElement.appendChild(contentContainer)
          },
        )

        // Add the landing page list element (which now includes the settings button)
        viewWriter.insert(
          viewWriter.createPositionAt(widgetContainer, 'end'),
          landingPageListElement,
        )

        return toWidget(widgetContainer, viewWriter, {
          label: 'landing page list widget',
          hasSelectionHandle: true,
        })
      },
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: 'type',
      view: 'data-type',
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'priceTagActive',
      view: 'data-price-tag-active',
    })

    editor.model.document.on('change', () => {
      const changes = editor.model.document.differ.getChanges()

      for (const change of changes) {
        if (
          change.type === 'attribute' &&
          change.attributeKey === 'priceTagActive' &&
          change.range
        ) {
          for (const item of change.range.getItems()) {
            if (item.is('element', 'dynamicList')) {
              editor.editing.reconvertItem(item)
              editor.editing.view.forceRender()
            }
          }
        }

        // The continue learning block is a single row whose card count equals the column
        // setting, so a grid change has to rebuild it; the other lists only need the CSS. Its
        // title and link label are drawn into the block too, so edits to them - from the block
        // settings or from typing over the heading - rebuild it as well.
        if (
          change.type === 'attribute' &&
          ['grid', 'clTitle', 'clLinkLabel'].includes(change.attributeKey) &&
          change.range
        ) {
          for (const item of change.range.getItems()) {
            if (
              item.is('element', 'dynamicList') &&
              item.getAttribute('type') === 'continueLearning'
            ) {
              editor.editing.reconvertItem(item)
              editor.editing.view.forceRender()
            }
          }
        }


      }
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'grid',
      view: 'data-grid',
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: 'template',
      view: 'data-template',
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: 'collections',
      view: 'data-collections',
    })

    conversion.for('upcast').elementToElement({
      model: 'cardWrapper',
      view: {
        name: 'div',
        classes: ['card-wrapper'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'cardWrapper',
      view: {
        name: 'div',
        classes: ['card-wrapper'],
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'cardWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const wrapper = viewWriter.createContainerElement('div', {
          class: 'card-wrapper',
        })
        return toWidget(wrapper, viewWriter)
      },
    })

    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: 'card-box',
      },
      model: (viewElement, { writer: modelWriter }) => {
        const type = viewElement.getAttribute('type')

        return modelWriter.createElement('cardBox', {
          type: type,
        })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'cardBox',
      view: (modelElement, { writer: viewWriter }) => {
        const type = modelElement.getAttribute('type')
        return viewWriter.createContainerElement('div', {
          class: 'card-box',
          type: type,
        })
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'cardBox',
      view: (modelElement, { writer: viewWriter }) => {
        const type = modelElement.getAttribute('type')
        const cardWrapper = modelElement.parent
        const cardBoxCount = cardWrapper._children._nodes.length

        const div = viewWriter.createContainerElement('div', {
          class: 'card-box',
          type: type,
        })
        if (
          (type === 'flipCard' && cardBoxCount > 1) ||
          (type !== 'flipCard' && cardBoxCount > 2)
        ) {
          const closeButton = viewWriter.createRawElement(
            'div',
            {
              class: 'ck ck-tooltip__container_card_box',
            },
            function (domElement) {
              domElement.innerHTML = `
            <button class="card-box-close-button" data-tooltip="Remove Block">
             ${closeButtonSvg}
            </button>
            `

              // Get button and tooltip text
              const button = domElement.querySelector('button')
              const tooltipText = button.dataset.tooltip

              // Create tooltip element
              const tooltip = document.createElement('div')
              tooltip.className = 'ck ck-tooltip ck-tooltip_s'
              tooltip.innerHTML = `<div class="ck ck-tooltip__text">${tooltipText}</div>`

              // Show tooltip on hover
              button.addEventListener('mouseenter', () => {
                button.parentElement.appendChild(tooltip)
                // Position the tooltip
                tooltip.style.bottom = '97%'
                tooltip.style.right = '0'
                tooltip.style.transform = 'translateX(20%)'
                tooltip.style.zIndex = '9999999'
                tooltip.style.position = 'absolute'
              })

              // Hide tooltip when mouse leaves
              button.addEventListener('mouseleave', () => {
                tooltip.remove()
              })

              // Add click event
              button.addEventListener('click', () => {
                editor.execute('cardBoxDelete')
              })

              return domElement
            },
          )
          viewWriter.insert(viewWriter.createPositionAt(div, 0), closeButton)
        }

        return toWidget(div, viewWriter, {
          label: 'card box widget',
          hasSelectionHandle: true,
        })
      },
    })

    // Card Number converters
    conversion.for('upcast').elementToElement({
      model: (viewElement, { writer }) => {
        return writer.createElement('cardHeader')
      },
      view: {
        name: 'div',
        classes: ['card-header'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'cardHeader',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createContainerElement('div', {
          class: 'card-header',
        })
        return div
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'cardHeader',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('div', {
          class: 'card-header',
        })
        return toWidgetEditable(div, viewWriter)
      },
    })

    // Card Content converters
    conversion.for('upcast').elementToElement({
      model: 'cardContent',
      view: {
        name: 'div',
        classes: ['card-content'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'cardContent',
      view: {
        name: 'div',
        classes: ['card-content'],
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'cardContent',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('div', {
          class: 'card-content',
        })
        return toWidgetEditable(div, viewWriter)
      },
    })

    // Card Content converters
    conversion.for('upcast').elementToElement({
      model: 'cardIconButton',
      view: {
        name: 'button',
        classes: ['card-icon-button'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'cardIconButton',
      view: {
        name: 'button',
        classes: ['card-icon-button'],
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'cardIconButton',
      view: (modelElement, { writer: viewWriter }) => {
        const button = viewWriter.createContainerElement('button', {
          class: 'card-icon-button',
        })

        viewWriter.setAttribute('data-listener-added', 'true', button)
        editor.editing.view.document.on('click', (evt, data) => {
          if (data.target === button) {
            const target = data.target
            editor.model.change(writer => {
              const modelElement = editor.editing.mapper.toModelElement(target)
              if (modelElement) {
                writer.setSelection(modelElement, 'on')
              }
            })

            this.dispatch = editor.config.get('videoGalleryPlugin.dispatch')
            handleIconButtonOptions(this.dispatch, editor, target)
          }
        })
        return toWidgetEditable(button, viewWriter)
      },
    })

    //Wrapper
    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: ['simple-box-wrapper'],
      },
      model: (viewElement, { writer }) => {
        // Get the actual attributes from the view element
        const backgroundImage =
          viewElement.getAttribute('background-image') || ''
        const backgroundColor =
          viewElement.getAttribute('background-color') || ''
        const backgroundType = viewElement.getAttribute('background-type') || ''
        const backgroundPosition =
          viewElement.getAttribute('background-position') || ''
        const backgroundSize = viewElement.getAttribute('background-size') || ''
        const sizeType = viewElement.getAttribute('size-type') || ''
        const overlayColor = viewElement.getAttribute('overlay-color') || ''
        const linearValue = viewElement.getAttribute('linear-value') || ''
        const opacity = viewElement.getAttribute('opacity') || ''
        const useImageOverlay =
          viewElement.getAttribute('use-image-overlay') || ''
        const layoutType = viewElement.getAttribute('layout-type')
        const blockId = viewElement.getAttribute('data-block-id') || ''
        const intercomArticleId = viewElement.getAttribute('data-intercom-article-id') || ''

        return writer.createElement('simpleBoxWrapper', {
          backgroundImage: backgroundImage,
          backgroundColor: backgroundColor,
          backgroundType: backgroundType,
          backgroundPosition: backgroundPosition,
          backgroundSize: backgroundSize,
          sizeType: sizeType,
          overlayColor: overlayColor,
          opacity: opacity,
          linearValue: linearValue,
          useImageOverlay: useImageOverlay,
          layoutType: layoutType,
          blockId: blockId,
          intercomArticleId: intercomArticleId,
          class: 'simple-box-wrapper',
        })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'simpleBoxWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const backgroundImage =
          modelElement.getAttribute('backgroundImage') || ''
        const backgroundColor =
          modelElement.getAttribute('backgroundColor') || ''
        const backgroundType = modelElement.getAttribute('backgroundType') || ''
        const backgroundPosition =
          modelElement.getAttribute('backgroundPosition') || 'center'
        const sizeType =
          modelElement.getAttribute('sizeType') || 'SCALE_TO_FILL'
        const overlayColor = modelElement.getAttribute('overlayColor') || ''
        const opacity = modelElement.getAttribute('opacity') || ''
        const linearValue = modelElement.getAttribute('linearValue') || ''
        const useImageOverlay =
          modelElement.getAttribute('useImageOverlay') || ''
        const layoutType = modelElement.getAttribute('layoutType')
        const blockId = modelElement.getAttribute('blockId') || ''
        const intercomArticleId = modelElement.getAttribute('intercomArticleId') || ''

        // SizeType mapping
        const SizeType = {
          SCALE_TO_FILL: { name: 'Scale to fill', class: 'cover' },
          ORIGINAL_SIZE: { name: 'Original size', class: 'auto' },
          TILE: { name: 'Tile', class: 'contain' },
        }

        // Determine the actual background-size CSS value based on sizeType
        const backgroundSize = SizeType[sizeType]
          ? SizeType[sizeType].class
          : 'cover'

        // Build style string conditionally
        let styleString = ''

        if (backgroundColor) {
          styleString += `background-color: ${backgroundColor}; `
        }
        if (sizeType === 'ORIGINAL_SIZE') {
          styleString += `background-repeat: no-repeat; `
        }
        styleString += `background-size: ${backgroundSize}; `

        if (backgroundPosition) {
          styleString += `background-position: ${backgroundPosition}; `
        }

        // Handle background-image with proper checks
        if (backgroundImage && linearValue && String(useImageOverlay) === 'true') {
          // Only apply overlay gradient if useImageOverlay is true
          styleString += `background-image: linear-gradient(${linearValue}, ${linearValue}), url(${backgroundImage}); `
        } else if (backgroundImage) {
          // If we have image but no overlay, just use the image
          styleString += `background-image: url(${backgroundImage}); `
        }

        const attrs = {
          class: 'simple-box-wrapper',
          'background-image': backgroundImage,
          'background-color': backgroundColor,
          'background-type': backgroundType,
          'background-size': backgroundSize,
          'size-type': sizeType,
          'use-image-overlay': useImageOverlay,
          'background-position': backgroundPosition,
          'linear-value': linearValue,
          opacity: opacity,
          'overlay-color': overlayColor,
          style: styleString.trim(),
        }

        if (layoutType) {
          attrs['layout-type'] = layoutType
        }

        if (blockId) {
          attrs['data-block-id'] = blockId
        }

        if (intercomArticleId) {
          attrs['data-intercom-article-id'] = intercomArticleId
        }

        return viewWriter.createEditableElement('div', attrs)
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'simpleBoxWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const backgroundImage =
          modelElement.getAttribute('backgroundImage') || ''
        const backgroundType = modelElement.getAttribute('backgroundType') || ''
        const backgroundColor =
          modelElement.getAttribute('backgroundColor') || ''
        const backgroundPosition =
          modelElement.getAttribute('backgroundPosition') || 'center'
        const sizeType =
          modelElement.getAttribute('sizeType') || 'SCALE_TO_FILL'
        const overlayColor = modelElement.getAttribute('overlayColor') || ''
        const opacity = modelElement.getAttribute('opacity') || ''
        const linearValue = modelElement.getAttribute('linearValue') || ''
        const useImageOverlay =
          modelElement.getAttribute('useImageOverlay') || ''
        const layoutType = modelElement.getAttribute('layoutType')
        const blockId = modelElement.getAttribute('blockId') || ''
        const intercomArticleId = modelElement.getAttribute('intercomArticleId') || ''

        // Build style string conditionally - same approach as dataDowncast
        const SizeType = {
          SCALE_TO_FILL: { name: 'Scale to fill', class: 'cover' },
          ORIGINAL_SIZE: { name: 'Original size', class: 'auto' },
          TILE: { name: 'Tile', class: 'contain' },
        }

        // Determine the actual background-size CSS value based on sizeType
        const backgroundSize = SizeType[sizeType]
          ? SizeType[sizeType].class
          : 'cover'

        // Build style string conditionally
        let styleString = ''

        if (backgroundColor) {
          styleString += `background-color: ${backgroundColor}; `
        }
        if (sizeType === 'ORIGINAL_SIZE') {
          styleString += `background-repeat: no-repeat; `
        }
        styleString += `background-size: ${backgroundSize}; `

        if (backgroundPosition) {
          styleString += `background-position: ${backgroundPosition}; `
        }
        // Handle background-image with proper checks
        if (backgroundImage && linearValue && String(useImageOverlay) === 'true') {
          // Only apply overlay gradient if useImageOverlay is true
          styleString += `background-image: linear-gradient(${linearValue}, ${linearValue}), url(${backgroundImage}); `
        } else if (backgroundImage) {
          // If we have image but no overlay, just use the image
          styleString += `background-image: url(${backgroundImage}); `
        }
        // Don't add any background-image property if there's no actual image

        const containerAttrs = {
          class: 'simple-box-wrapper',
          'background-image': backgroundImage,
          'background-color': backgroundColor,
          'background-type': backgroundType,
          'background-position': backgroundPosition,
          'background-size': backgroundSize,
          'size-type': sizeType,
          'overlay-color': overlayColor,
          'linear-value': linearValue,
          opacity: opacity,
          'use-image-overlay': useImageOverlay,
          style: styleString.trim(), // Trim to remove trailing space
        }

        if (layoutType) {
          containerAttrs['layout-type'] = layoutType
        }

        if (blockId) {
          containerAttrs['data-block-id'] = blockId
        }

        if (intercomArticleId) {
          containerAttrs['data-intercom-article-id'] = intercomArticleId
        }

        const widgetContainer = viewWriter.createContainerElement(
          'div',
          containerAttrs,
        )

        // Create the add drop above button
        const addAboveButton = viewWriter.createUIElement(
          'div',
          {
            class: 'simple-box-wrapper-add-button simple-box-wrapper-add-above',
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            // Button container with data attribute for tooltip
            domElement.innerHTML = `
              <div class="ck ck-tooltip__container">
                <button class="simple-box-wrapper-button" data-tooltip="Add Block">
                 ${addBlockSvg}
                </button>
              </div>
            `

            // Add tooltip functionality
            const button = domElement.querySelector('button')
            const tooltipText = button.dataset.tooltip

            // Create tooltip element
            const tooltip = document.createElement('div')
            tooltip.className = 'ck ck-tooltip ck-tooltip_s'
            tooltip.innerHTML = `<div class="ck ck-tooltip__text">${tooltipText}</div>`

            // Show/hide tooltip on hover
            button.addEventListener('mouseenter', () => {
              button.parentElement.appendChild(tooltip)
              // Position the tooltip
              tooltip.style.bottom = '-66px'
              tooltip.style.left = '50%'
              tooltip.style.transform = 'translateX(-50%)'
              tooltip.style.background = '#303031'
              tooltip.style.padding = '4px'
              tooltip.style.borderRadius = '4px'
              tooltip.style.zIndex = '9999999'
            })

            button.addEventListener('mouseleave', () => {
              tooltip.remove()
            })

            // Add click listener
            button.addEventListener('click', () => {
              editor.execute('addContentAbove')
            })

            return domElement
          },
        )

        const addBelowButton = viewWriter.createUIElement(
          'div',
          {
            class: 'simple-box-wrapper-add-button simple-box-wrapper-add-below',
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            domElement.innerHTML = `
              <div class="ck ck-tooltip__container">
                <button class="simple-box-wrapper-button" data-tooltip="Add Block">
                   ${addBlockSvg}
                </button>
              </div>
            `

            const button = domElement.querySelector('button')
            const tooltipText = button.dataset.tooltip

            const tooltip = document.createElement('div')
            tooltip.className = 'ck ck-tooltip ck-tooltip_s'
            tooltip.innerHTML = `<div class="ck ck-tooltip__text">${tooltipText}</div>`

            button.addEventListener('mouseenter', () => {
              button.parentElement.appendChild(tooltip)
              tooltip.style.bottom = '-66px'
              tooltip.style.left = '50%'
              tooltip.style.transform = 'translateX(-50%)'
              tooltip.style.background = '#303031'
              tooltip.style.padding = '4px'
              tooltip.style.borderRadius = '4px'
              tooltip.style.zIndex = '9999999'
            })

            button.addEventListener('mouseleave', () => {
              tooltip.remove()
            })

            button.addEventListener('click', () => {
              editor.execute('addContentBelow')
            })

            return domElement
          },
        )

        // Assemble the widget
        viewWriter.insert(
          viewWriter.createPositionAt(widgetContainer, 0),
          addAboveButton,
        )

        viewWriter.insert(
          viewWriter.createPositionAt(widgetContainer, 'end'),
          addBelowButton,
        )

        return toWidget(widgetContainer, viewWriter, {
          label: 'simple box widget',
          hasSelectionHandle: true,
        })
      },
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'backgroundImage',
      view: 'background-image',
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'overlayColor',
      view: 'overlay-color',
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: 'opacity',
      view: 'opacity',
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: 'linearValue',
      view: 'linear-value',
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'backgroundColor',
      view: 'background-color',
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'backgroundType',
      view: 'background-type',
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'backgroundSize',
      view: 'background-size',
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'backgroundPosition',
      view: 'background-position',
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'sizeType',
      view: 'size-type',
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'useImageOverlay',
      view: 'use-image-overlay',
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: {
        name: 'simpleBoxWrapper',
        key: 'layoutType',
      },
      view: 'layout-type',
    })

    // dropAreaWide converters
    conversion.for('upcast').elementToElement({
      model: 'dropAreaWide',
      view: {
        name: 'div',
        classes: ['drop-area-wide'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'dropAreaWide',
      view: {
        name: 'p',
        classes: ['empty-dom'],
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'dropAreaWide',
      view: (modelElement, { writer: viewWriter }) => {
        // Create main container with flex row
        const div = viewWriter.createContainerElement('div', {
          class: 'drop-area-wide',
        })

        setTimeout(() => {
          const dropAreaWide = document.querySelector('.drop-area-wide')
          if (dropAreaWide) {
            dropAreaWide.addEventListener('click', e => {
              if (e.target === dropAreaWide) {
                editor.execute('addClickdropAreaWide')
              }
            })
          }
        }, 0)

        // Create icon container
        const iconContainer = viewWriter.createContainerElement('div', {
          style: 'display: flex; justify-content: center; align-items: center;',
        })

        // Create SVG element
        const svgContainer = viewWriter.createUIElement(
          'div',
          null,
          function (domDocument) {
            const domElement = domDocument.createElement('div')
            domElement.innerHTML = dropAreaWideSvg
            return domElement
          },
        )

        // Create text container with vertical layout
        const textContainer = viewWriter.createContainerElement('div', {
          style:
            'display: flex; flex-direction: column; justify-content: center; align-items: flex-start; gap: 4px; height: 38px; margin-top: 20px',
        })

        // Create primary text element
        const primaryText = viewWriter.createUIElement(
          'span',
          null,
          function (domDocument) {
            const element = domDocument.createElement('span')
            element.textContent = 'Drag or double-click a content block'
            element.style.color = 'var(--mcg-dark, #002E47)'
            element.style.fontSize = '14px'
            element.style.fontWeight = '500'
            element.style.lineHeight = '22px'
            element.style.fontFamily = 'Inter'
            return element
          },
        )

        // Create secondary text element
        const secondaryText = viewWriter.createUIElement(
          'span',
          null,
          function (domDocument) {
            const element = domDocument.createElement('span')
            element.textContent = 'from the right panel to add it to your page.'
            element.style.color = '#919EAB'
            element.style.fontSize = '12px'
            element.style.fontWeight = '400'
            element.style.lineHeight = '22px'
            element.style.fontFamily = 'Inter'
            return element
          },
        )

        // Add SVG to icon container
        viewWriter.insert(
          viewWriter.createPositionAt(iconContainer, 0),
          svgContainer,
        )

        // Add text elements to text container
        viewWriter.insert(
          viewWriter.createPositionAt(textContainer, 0),
          primaryText,
        )
        viewWriter.insert(
          viewWriter.createPositionAt(textContainer, 'end'),
          secondaryText,
        )

        // Add icon container and text container to main div
        viewWriter.insert(viewWriter.createPositionAt(div, 0), iconContainer)
        viewWriter.insert(
          viewWriter.createPositionAt(div, 'end'),
          textContainer,
        )

        return toWidget(div, viewWriter, {
          label: 'drop area wrapper',
        })
      },
    })

    conversion.for('upcast').elementToElement({
      model: (viewElement, { writer }) => {
        const dataVisibility = viewElement.getAttribute('data-visibility') || 'true'
        return writer.createElement('dropAreaNarrow', { dataVisibility })
      },
      view: {
        name: 'div',
        classes: ['drop-area-narrow'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'dropAreaNarrow',
      view: {
        name: 'p',
        classes: ['empty-dom'],
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'dropAreaNarrow',
      view: (modelElement, { writer: viewWriter }) => {
        const dataVisibility = modelElement.getAttribute('dataVisibility') || 'true'
        const div = viewWriter.createEditableElement('div', {
          class: 'drop-area-narrow',
          'data-visibility': dataVisibility,
        })
        return toWidget(div, viewWriter, {
          label: 'drag area wrapper',
        })
      },
    })
    // SimpleBox converters
    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: ['simple-box'],
      },
      model: (viewElement, { writer }) => {
        const blockType =
          viewElement.getAttribute('blockType') ||
          viewElement.getAttribute('block-type')

        return writer.createElement('simpleBox', {
          ...(blockType && { blockType: blockType }),
        })
      },
    })

    // 3. Update the dataDowncast converter (around line 1302)
    conversion.for('dataDowncast').elementToElement({
      model: 'simpleBox',
      view: (modelElement, { writer: viewWriter }) => {
        const blockType = modelElement.getAttribute('blockType')

        const attributes = {
          class: 'simple-box',
        }

        // Add blockType attribute if it exists
        if (blockType) {
          attributes['block-type'] = blockType
        }

        const div = viewWriter.createEditableElement('div', attributes)
        return div
      },
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: 'blockType',
      view: 'block-type',
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'simpleBox',
      view: (modelElement, { writer: viewWriter }) => {
        const blockType = modelElement.getAttribute('blockType')

        const attributes = {
          class: 'simple-box',
        }

        if (blockType) {
          attributes['block-type'] = blockType
        }

        const widgetContainer = viewWriter.createContainerElement(
          'div',
          attributes,
        )

        let hasCardWrapper = false
        let hasButtonWrapper = false
        let hasAccordionWrapperEl = false
        let hasTabContainerEl = false
        let hasHotspotContainerEl = false
        let shouldShowAddCardButton = false
        let hasPixelQuest = false
        let hasCrossword = false
        let hasRiddle = false
        let hasWordle = false
        let hasWordSearch = false
        let hasJumble = false
        let hasScorm = false
        let hasImageCarousel = false
        let hasDynamicListBadge = false
        let hasCatalog = false

        // Recursive function to find specific element types
        const findElements = element => {
          if (!element) return

          // Check if this element is a cardWrapper or buttonWrapper
          if (element.name === 'cardWrapper') {
            hasCardWrapper = true

            // Check if the cardWrapper has the flipCard, numberedCard or card attribute
            const isFlipCard = Array.from(element.getChildren()).filter(
              item => item.getAttribute('type') === 'flipCard',
            )

            const isNumberedCard = Array.from(element.getChildren()).filter(
              item => item.getAttribute('type') === 'numberedCard',
            )

            const isCard = Array.from(element.getChildren()).filter(
              item => item.getAttribute('type') === 'card',
            )

            // Allow more than 4 cards for flipCard, numberedCard and card types, otherwise limit to 4
            if (
              isFlipCard.length !== 0 ||
              isNumberedCard.length !== 0 ||
              isCard.length !== 0 ||
              Array.from(element.getChildren()).length < 4
            ) {
              shouldShowAddCardButton = true
            }
          } else if (
            element.name === 'buttonWrapper' &&
            element.name === 'buttonWrapper'
          ) {
            hasButtonWrapper = true
          } else if (element.name === 'accordionWrapper') {
            hasButtonWrapper = true
            hasAccordionWrapperEl = true
          } else if (element.name === 'tabContainer') {
            hasButtonWrapper = true
            hasTabContainerEl = true
          } else if (element.name === 'imageHotspotContainer') {
            hasButtonWrapper = true
            hasHotspotContainerEl = true
          } else if (element.name === 'imageCarouselWrapper') {
            hasImageCarousel = true
          } else if (element.name === 'pixelQuest') {
            hasPixelQuest = true
          } else if (element.name === 'crosswordPuzzle') {
            hasCrossword = true
          } else if (element.name === 'riddle') {
            hasRiddle = true
          } else if (element.name === 'wordle') {
            hasWordle = true
          } else if (element.name === 'wordSearch') {
            hasWordSearch = true
          } else if (element.name === 'jumble') {
            hasJumble = true
          } else if (element.name === 'scormPackage') {
            hasScorm = true
          } else if (element.name === 'dynamicList' && element.getAttribute('type') === 'badge') {
            hasDynamicListBadge = true
          } else if (element.name === 'dynamicList' && element.getAttribute('type') === 'catalog') {
            hasCatalog = true
          }

          // Check children recursively
          if (element.getChildren) {
            Array.from(element.getChildren()).forEach(child => {
              findElements(child)
            })
          }
        }

        const theme = editor.config.get('simpleBox.theme')
        const pageUrl = editor.config.get('simpleBox.pageUrl')
        const subPage = editor.config.get('simpleBox.page')
        findElements(modelElement)

        // Determine if we should show the add button
        const hasWrapper =
          (hasCardWrapper && shouldShowAddCardButton) || hasButtonWrapper

        // Determine add button tooltip based on wrapper type
        const addButtonTooltip = hasAccordionWrapperEl
          ? 'Add accordion'
          : hasTabContainerEl
            ? 'Add tab'
            : hasHotspotContainerEl
              ? 'Add hotspot'
              : hasButtonWrapper
                ? 'Add button'
                : 'Add card'

        const isCollection = pageUrl === 'collection'
        const isPageSubPage = subPage === 'page'
        const isCompletionSubPage = subPage === 'completion'
        const isLandingPageWithThemeZero =
          subPage === 'landing-page' && theme === 0

        const showSettingsButton =
          isCollection ||
          isPageSubPage ||
          isCompletionSubPage ||
          isLandingPageWithThemeZero

        // Check if this is an interaction builder block
        const isInteractionBuilder = blockType === 'interactionBuilder'

        // Define button configurations
        const buttonConfigs = [
          ...(showSettingsButton
            ? [
              {
                class: 'add-settings-button',
                icon: addSettingButtonSvg,
                action: 'contentSettings',
                tooltip: 'Settings',
              },
            ]
            : []),
          ...(hasCatalog
            ? [
              {
                class: 'catalog-ordering-button',
                icon: catalogOrderingSvg,
                action: 'catalogOrdering',
                tooltip: 'Ordering',
              },
            ]
            : []),
          ...(!hasDynamicListBadge
            ? [
                {
                  class: 'add-copy-button',
                  icon: addCopyButtonSvg,
                  action: 'contentCopy',
                  tooltip: 'Copy',
                },
                {
                  class: 'add-duplicate-button',
                  icon: addDuplicateButtonSvg,
                  action: 'contentDuplicate',
                  tooltip: 'Duplicate',
                },
              ]
            : []),
          ...(hasWrapper
            ? [
              {
                class: 'add-card-and-button',
                icon: addCardAndSvg,
                action: 'contentAdd',
                tooltip: addButtonTooltip,
              },
            ]
            : []),
          ...(hasImageCarousel
            ? [
              {
                class: 'carousel-add-item-button',
                icon: `<svg width="24" height="26" viewBox="0 0 24 26" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.1992 14.9996V17.0996H14.0992V18.4996H16.1992V20.5996H17.5992V18.4996H19.6992V17.0996H17.5992V14.9996H16.1992ZM12.9092 19.1996H7.09922C6.32922 19.1996 5.69922 18.5696 5.69922 17.7996V7.99961C5.69922 7.22961 6.32922 6.59961 7.09922 6.59961H16.8992C17.6692 6.59961 18.2992 7.22961 18.2992 7.99961V13.8096C17.8792 13.6696 17.3892 13.5996 16.8992 13.5996C16.1292 13.5996 15.3592 13.8096 14.7292 14.2296L13.7492 12.8996L11.2992 16.0496L9.54922 13.9496L7.09922 17.0996H12.7692C12.6992 17.3096 12.6992 17.5896 12.6992 17.7996C12.6992 18.2896 12.7692 18.7796 12.9092 19.1996Z" fill="#002E47"/></svg>`,
                action: 'imageCarouselItemAdd',
                tooltip: 'Add new image to carousel',
              },
            ]
            : []),
          ...(isInteractionBuilder
            ? [
              {
                class: 'edit-interaction-button',
                icon: editInteractionSvg,
                action: 'editInteraction',
                tooltip: 'Edit interaction',
              },
            ]
            : []),
          ...(hasPixelQuest || hasCrossword || hasRiddle || hasWordle || hasJumble || hasWordSearch || hasScorm
            ? [
              {
                class: 'report-button',
                icon: reportButtonSvg,
                action: 'exportReport',
                tooltip: 'Report',
              },
            ]
            : []),

          {
            class: 'add-delete-button',
            icon: addDeleteButtonSvg,
            action: 'contentDelete',
            tooltip: 'Delete',
          },
        ]

        const findHotspotWrapper = element => {
          if (!element || !element.is('element')) return false
          if (element.name === 'imageHotspotContainer') return true

          for (const child of element.getChildren()) {
            if (findHotspotWrapper(child)) return true
          }

          return false
        }

        const findCarouselWrapperInSimpleBox = element => {
          if (!element || !element.is('element')) return null

          if (element.name === 'imageCarouselWrapper') return element

          for (const child of element.getChildren()) {
            const found = findCarouselWrapperInSimpleBox(child)
            if (found) return found
          }

          return null
        }
        const buttonContainer = viewWriter.createUIElement(
          'div',
          { class: 'simple-box-buttons-container' },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            // Check if this is a hotspot container
            const isHotspotContainer = findHotspotWrapper(modelElement)
            const getWrapperElement = () =>
              domElement.closest('.simple-box-wrapper')

            const menuAbortController =
              typeof AbortController !== 'undefined'
                ? new AbortController()
                : null
            const menuSignal = menuAbortController
              ? menuAbortController.signal
              : null

            const updateMenuViewportConstraints = () => {
              const wrapperElement = getWrapperElement()
              if (!wrapperElement) return

              const wrapperRect = wrapperElement.getBoundingClientRect()
              const editorContainer =
                wrapperElement.closest('.editor-container__editor') ||
                document.querySelector('.editor-container__editor')
              const editorRect = editorContainer
                ? editorContainer.getBoundingClientRect()
                : { top: 0, bottom: window.innerHeight }

              const visibleBottom = Math.min(editorRect.bottom, window.innerHeight)
              const preferredTop = 50
              const gap = 8
              const availableHeight = Math.max(
                72,
                visibleBottom - wrapperRect.top - preferredTop - gap,
              )
              domElement.style.setProperty(
                '--simple-box-menu-max-height',
                `${Math.round(availableHeight)}px`,
              )

              // Let the browser reflow with the new max-height before measuring
              requestAnimationFrame(() => {
                const isScrollable =
                  domElement.scrollHeight > domElement.clientHeight + 2
                domElement.classList.toggle('is-scrollable', isScrollable)
              })
            }

            const clearMenuViewportConstraints = () => {
              domElement.style.removeProperty('--simple-box-menu-max-height')
              domElement.classList.remove('is-scrollable')
            }

            let wrapperEventsBound = false
            const bindWrapperEvents = () => {
              const wrapperElement = getWrapperElement()
              if (!wrapperElement || wrapperEventsBound) return

              const fallbackListeners = []
              const addWrapperListener = (type, handler) => {
                if (menuSignal) {
                  try {
                    wrapperElement.addEventListener(type, handler, {
                      signal: menuSignal,
                    })
                    return
                  } catch (error) {
                    // Fallback to plain add/remove on browsers that don't support { signal }.
                  }
                }

                wrapperElement.addEventListener(type, handler)
                fallbackListeners.push([type, handler])
              }

              const onMouseEnter = () => {
                updateMenuViewportConstraints()
              }
              const onFocusIn = () => {
                updateMenuViewportConstraints()
              }
              const onMouseLeave = () => {
                clearMenuViewportConstraints()
              }
              const onFocusOut = () => {
                setTimeout(() => {
                  const stillActive =
                    wrapperElement.matches(':hover') ||
                    wrapperElement.contains(document.activeElement)

                  if (!stillActive) {
                    clearMenuViewportConstraints()
                  }
                }, 0)
              }

              addWrapperListener('mouseenter', onMouseEnter)
              addWrapperListener('focusin', onFocusIn)
              addWrapperListener('mouseleave', onMouseLeave)
              addWrapperListener('focusout', onFocusOut)

              wrapperEventsBound = true

              editor.on('destroy', () => {
                if (menuAbortController) {
                  menuAbortController.abort()
                }
                fallbackListeners.forEach(([type, handler]) => {
                  wrapperElement.removeEventListener(type, handler)
                })
                fallbackListeners.length = 0
              })
            }

            requestAnimationFrame(bindWrapperEvents)
            setTimeout(bindWrapperEvents, 0)

            // Generate buttons HTML
            domElement.innerHTML = buttonConfigs
              .map(config => {
                // Override tooltip for hotspot add button
                const tooltipText =
                  config.class === 'add-card-and-button' && isHotspotContainer
                    ? 'Double-click on the image to add hotspot'
                    : config.tooltip || ''

                return `
          <button type="button" class="${config.class}" data-tooltip="${tooltipText}">
            ${config.icon}
          </button>
        `
              })
              .join('')

            // Add event listeners for all buttons
            buttonConfigs.forEach(config => {
              const button = domElement.querySelector(`.${config.class}`)

              if (button) {
                // Add tooltip functionality
                const tooltipText = button.dataset.tooltip
                let tooltip = null
                const removeTooltip = () => {
                  if (tooltip) {
                    tooltip.remove()
                    tooltip = null
                  }
                }

                // Add click listener
                button.addEventListener('click', () => {
                  removeTooltip()
                  if (config.class === 'carousel-add-item-button') {
                    // Prefer the carousel wrapper inside this simpleBox
                    const carouselWrapper =
                      findCarouselWrapperInSimpleBox(modelElement)
                    if (carouselWrapper) {
                      editor.execute(config.action, { carouselWrapper })
                    } else {
                      editor.execute(config.action)
                    }
                  } else {
                    // Store the target model element so commands can find the correct container
                    editor._contentAddTarget = modelElement
                    editor.execute(config.action)
                    editor._contentAddTarget = null
                  }
                })

                if (tooltipText) {
                  button.addEventListener('mouseenter', () => {
                    removeTooltip()
                    tooltip = document.createElement('div')
                    tooltip.textContent = tooltipText
                    document.body.appendChild(tooltip)
                    const rect = button.getBoundingClientRect()
                    tooltip.style.cssText = `
                      position: fixed;
                      top: ${rect.top + rect.height / 2}px;
                      left: ${rect.right + 6}px;
                      transform: translateY(-50%);
                      background-color: #212B36;
                      padding: 4px 8px;
                      border-radius: 8px;
                      white-space: nowrap;
                      z-index: 9999999;
                      color: white;
                      font-size: 0.6875rem;
                      font-weight: 500;
                      font-family: Inter, sans-serif;
                      line-height: 1.57143;
                      pointer-events: none;
                    `
                  })

                  button.addEventListener('mouseleave', () => {
                    removeTooltip()
                  })

                  button.addEventListener('mousedown', () => {
                    removeTooltip()
                  })
                }
              }
            })

            return domElement
          },
        )

        viewWriter.insert(
          viewWriter.createPositionAt(widgetContainer, 0),
          buttonContainer,
        )

        return toWidget(widgetContainer, viewWriter, {
          label: 'simple box widget',
          hasSelectionHandle: true,
        })
      },
    })
    //BUTTON BOX WRAPPER

    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: 'button-wrapper',
      },
      model: (viewElement, { writer: modelWriter }) => {
        const alignment = viewElement.getAttribute('data-alignment') || 'left'
        const sizing = viewElement.getAttribute('data-sizing') || 'fit'

        return modelWriter.createElement('buttonWrapper', {
          dataAlignment: alignment,
          dataSizing: sizing,
        })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'buttonWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const alignment = modelElement.getAttribute('dataAlignment') || 'left'
        const sizing = modelElement.getAttribute('dataSizing') || 'fit'

        return viewWriter.createContainerElement('div', {
          class: 'button-wrapper',
          'data-alignment': alignment,
          'data-sizing': sizing,
        })
      },
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: 'dataAlignment',
      view: 'data-alignment',
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'dataSizing',
      view: 'data-sizing',
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'buttonWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const alignment = modelElement.getAttribute('dataAlignment') || 'left'
        const sizing = modelElement.getAttribute('dataSizing') || 'left'
        const div = viewWriter.createContainerElement('div', {
          class: 'button-wrapper',
          'data-alignment': alignment,
          'data-sizing': sizing,
        })

        return toWidget(div, viewWriter, {
          label: 'button wrapper widget',
          hasSelectionHandle: true,
        })
      },
    })

    //BUTTON
    conversion.for('upcast').elementToElement({
      view: {
        name: 'a',
        classes: ['simple-button'],
      },
      model: (viewElement, { writer: modelWriter }) => {
        return modelWriter.createElement('simpleButton', {
          href: viewElement.getAttribute('href'),
          textcolor: viewElement.getAttribute('textcolor'),
          text: viewElement.getAttribute('text'),
          type: viewElement.getAttribute('type'),
          pdfname: viewElement.getAttribute('pdfname'),
          targetvalue: viewElement.getAttribute('targetvalue') || '_blank',
        })
      },
    })

    // Update the button data downcast converter
    conversion.for('dataDowncast').elementToElement({
      model: 'simpleButton',
      view: (modelElement, { writer: viewWriter }) => {
        const attrs = {
          class: ['simple-button'],
          href: modelElement.getAttribute('href') || '',
          text: modelElement.getAttribute('text'),
          textcolor: modelElement.getAttribute('textcolor') || '',
          type: modelElement.getAttribute('type') || '',
          pdfname: modelElement.getAttribute('pdfname') || '',
          target: modelElement.getAttribute('targetvalue') || '_blank',
          targetvalue: modelElement.getAttribute('targetvalue') || '_blank',
          style: `color: ${modelElement.getAttribute('textcolor') || ''
            }; border: 1px solid ${modelElement.getAttribute('textcolor') || ''}`,
        }

        return viewWriter.createContainerElement('a', attrs)
      },
    })

    // Update the button editing downcast converter
    conversion.for('editingDowncast').elementToElement({
      model: 'simpleButton',
      view: (modelElement, { writer: viewWriter }) => {
        const attrs = {
          class: ['simple-button'],
          href: modelElement.getAttribute('href') || '',
          text: modelElement.getAttribute('text'),
          textcolor: modelElement.getAttribute('textcolor') || '',
          type: modelElement.getAttribute('type') || '',
          pdfname: modelElement.getAttribute('pdfname') || '',
          target: modelElement.getAttribute('targetvalue') || '_blank',
          targetvalue: modelElement.getAttribute('targetvalue') || '_blank',
          contenteditable: 'false',
          style: `color: ${modelElement.getAttribute('textcolor') || ''
            }; border: 1px solid ${modelElement.getAttribute('textcolor') || ''}`,
        }

        const button = viewWriter.createContainerElement('a', attrs)

        // Add event listeners
        viewWriter.setAttribute('data-listener-added', 'true', button)
        editor.editing.view.document.on('click', (evt, data) => {
          if (data.target === button) {
            evt.stop()
            data.preventDefault()

            editor.model.change(writer => {
              const modelElement = editor.editing.mapper.toModelElement(
                data.target,
              )
              if (modelElement) {
                writer.setSelection(modelElement, 'on')
              }
            })

            handleButtonOptions(
              editor.config.get('videoGalleryPlugin.dispatch'),
              editor,
              data.target,
            )
          }
        })

        return toWidget(button, viewWriter, {
          label: 'button widget',
          hasSelectionHandle: true,
        })
      },
    })

    //SIMPLE ICON
    conversion.for('upcast').elementToElement({
      view: {
        name: 'iconify-icon',
        classes: 'simple-icon',
      },
      model: (viewElement, { writer: modelWriter }) => {
        const icon = viewElement.getAttribute('icon')
        const iconName = viewElement.getAttribute('iconname')
        const width = viewElement.getAttribute('width')
        const height = viewElement.getAttribute('height')
        return modelWriter.createElement('simpleIcon', {
          icon: icon,
          iconname: iconName,
          width: width,
          height: height,
        })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'simpleIcon',
      view: (modelElement, { writer: viewWriter }) => {
        const icon = modelElement.getAttribute('icon')
        const iconName = modelElement.getAttribute('iconname')
        const width = modelElement.getAttribute('width')
        const height = modelElement.getAttribute('height')
        return viewWriter.createEditableElement('iconify-icon', {
          class: 'simple-icon',
          icon: icon,
          iconname: iconName,
          width: width,
          height: height,
        })
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'simpleIcon',
      view: (modelElement, { writer: viewWriter }) => {
        const icon = modelElement.getAttribute('icon')
        const iconName = modelElement.getAttribute('iconname')
        const width = modelElement.getAttribute('width')
        const height = modelElement.getAttribute('height')
        const iconElement = viewWriter.createEditableElement('iconify-icon', {
          class: 'simple-icon',
          icon: icon,
          iconname: iconName,
          width: width,
          height: height,
        })

        return toWidget(iconElement, viewWriter, {
          label: 'icon widget',
        })
      },
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: {
        key: 'iconValue',
        name: 'simpleIcon',
      },
      view: {
        key: 'icon',
      },
    })

    // COLUMN
    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: 'column',
      },
      model: (viewElement, { writer: modelWriter }) => {
        const flexSize = viewElement.getAttribute('flex-size')
        return modelWriter.createElement('column', {
          'flex-size': flexSize,
        })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'column',
      view: (modelElement, { writer: viewWriter }) => {
        const flexSize = modelElement.getAttribute('flex-size')
        return viewWriter.createEditableElement('div', {
          class: 'column',
          'flex-size': flexSize,
        })
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'column',
      view: (modelElement, { writer: viewWriter }) => {
        const flexSize = modelElement.getAttribute('flex-size')
        const div = viewWriter.createEditableElement('div', {
          class: 'column',
          'flex-size': flexSize,
        })

        return toWidgetEditable(div, viewWriter)
      },
    })

    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: 'column-header',
      },
      model: (viewElement, { writer: modelWriter }) => {
        return modelWriter.createElement('columnHeader', {})
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'columnHeader',
      view: (modelElement, { writer: viewWriter }) => {
        return viewWriter.createEditableElement('div', {
          class: 'column-header',
        })
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'columnHeader',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('div', {
          class: 'column-header',
        })

        return toWidgetEditable(div, viewWriter)
      },
    })

    // HTML BOX
    conversion.for('upcast').elementToElement({
      model: 'htmlBox',
      view: {
        name: 'div',
        classes: 'html-box',
      },
    })
    conversion.for('dataDowncast').elementToElement({
      model: 'htmlBox',
      view: {
        name: 'div',
        classes: 'html-box',
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'htmlBox',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('div', {
          class: 'html-box',
        })

        return toWidgetEditable(div, viewWriter)
      },
    })

    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: ['img-wrapper'],
      },
      model: (viewElement, { writer }) => {
        const wrapper = writer.createElement('imageWrapper')
        const aspectRatio = viewElement.getAttribute('aspectratio')
        const id = viewElement.getAttribute('id')

        if (aspectRatio)
          writer.setAttribute('aspectratio', aspectRatio, wrapper)
        if (id) writer.setAttribute('id', id, wrapper)

        return wrapper
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'imageWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const attributes = {}
        const aspectRatio = modelElement.getAttribute('aspectratio')
        const id = modelElement.getAttribute('id')

        if (aspectRatio) attributes.aspectratio = aspectRatio
        if (id) attributes.id = id

        return viewWriter.createContainerElement('div', {
          ...attributes,
          class: 'img-wrapper',
        })
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'imageWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const attributes = {}
        const aspectRatio = modelElement.getAttribute('aspectratio')
        const id = modelElement.getAttribute('id')

        if (aspectRatio) attributes.aspectratio = aspectRatio
        if (id) attributes.id = id

        const wrapper = viewWriter.createEditableElement('div', {
          ...attributes,
          class: 'img-wrapper',
        })

        return toWidgetEditable(wrapper, viewWriter)
      },
    })

    // IMAGE CAROUSEL WRAPPER
    conversion.for('upcast').elementToElement({
      model: 'imageCarouselWrapper',
      view: {
        name: 'div',
        classes: ['image-carousel-wrapper'],
      },
    })
    conversion.for('dataDowncast').elementToElement({
      model: 'imageCarouselWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const wrapper = viewWriter.createContainerElement('div', {
          class: 'image-carousel-wrapper',
        })

        const itemCount = modelElement.childCount
        const dotsHtml = Array.from({ length: itemCount }, (_, i) =>
          `<button class="image-carousel-editor-dot${i === 0 ? ' active' : ''}"></button>`
        ).join('')

        const navElement = viewWriter.createRawElement(
          'div',
          { class: 'image-carousel-editor-nav' },
          function (domElement) {
            domElement.innerHTML =
              `<button class="carousel-arrow carousel-prev"><svg xmlns="http://www.w3.org/2000/svg" width="6" height="10" viewBox="0 0 6 10" fill="none"><path d="M2.17558 5L5.74809 8.49776C5.91603 8.66218 6 8.87145 6 9.12556C6 9.37967 5.91603 9.58894 5.74809 9.75336C5.58015 9.91779 5.36641 10 5.10687 10C4.84733 10 4.63359 9.91779 4.46565 9.75336L0.251922 5.6278C0.160319 5.53812 0.0955861 5.44096 0.0577236 5.33632C0.0198611 5.23169 0.000624779 5.11958 1.40929e-05 5C-0.000596593 4.88042 0.0186398 4.76831 0.0577236 4.66368C0.0968075 4.55904 0.16154 4.46188 0.251922 4.3722L4.46565 0.246636C4.63359 0.0822119 4.84733 0 5.10687 0C5.36641 0 5.58015 0.0822119 5.74809 0.246636C5.91603 0.411061 6 0.620329 6 0.874439C6 1.12855 5.91603 1.33782 5.74809 1.50224L2.17558 5Z" fill="#002E47"/></svg></button>` +
              `<div class="image-carousel-editor-dots">${dotsHtml}</div>` +
              `<button class="carousel-arrow carousel-next"><svg xmlns="http://www.w3.org/2000/svg" width="6" height="10" viewBox="0 0 6 10" fill="none"><path d="M3.82442 5L0.251908 1.50224C0.0839699 1.33782 -1.78108e-07 1.12855 -1.55893e-07 0.874441C-1.33678e-07 0.620329 0.08397 0.411064 0.251908 0.246639C0.419847 0.0822139 0.633587 4.84527e-07 0.893128 5.07217e-07C1.15267 5.29907e-07 1.36641 0.082214 1.53435 0.246639L5.74808 4.3722C5.83968 4.46188 5.90441 4.55904 5.94228 4.66368C5.98014 4.76831 5.99938 4.88042 5.99999 5C6.0006 5.11958 5.98136 5.23169 5.94228 5.33632C5.90319 5.44096 5.83846 5.53812 5.74808 5.6278L1.53435 9.75336C1.36641 9.91779 1.15267 10 0.893127 10C0.633586 10 0.419846 9.91779 0.251907 9.75336C0.0839692 9.58894 -8.99443e-07 9.37967 -8.77228e-07 9.12556C-8.55013e-07 8.87145 0.0839692 8.66218 0.251907 8.49776L3.82442 5Z" fill="#002E47"/></svg></button>`
          },
        )
        viewWriter.insert(viewWriter.createPositionAt(wrapper, 'end'), navElement)

        return wrapper
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'imageCarouselWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const wrapper = viewWriter.createContainerElement('div', {
          class: 'image-carousel-wrapper',
        })

        const navElement = viewWriter.createRawElement(
          'div',
          {
            class: 'image-carousel-editor-nav',
          },
          function (domElement) {
            domElement.innerHTML = `
              <button class="carousel-arrow carousel-prev"><svg xmlns="http://www.w3.org/2000/svg" width="6" height="10" viewBox="0 0 6 10" fill="none"><path d="M2.17558 5L5.74809 8.49776C5.91603 8.66218 6 8.87145 6 9.12556C6 9.37967 5.91603 9.58894 5.74809 9.75336C5.58015 9.91779 5.36641 10 5.10687 10C4.84733 10 4.63359 9.91779 4.46565 9.75336L0.251922 5.6278C0.160319 5.53812 0.0955861 5.44096 0.0577236 5.33632C0.0198611 5.23169 0.000624779 5.11958 1.40929e-05 5C-0.000596593 4.88042 0.0186398 4.76831 0.0577236 4.66368C0.0968075 4.55904 0.16154 4.46188 0.251922 4.3722L4.46565 0.246636C4.63359 0.0822119 4.84733 0 5.10687 0C5.36641 0 5.58015 0.0822119 5.74809 0.246636C5.91603 0.411061 6 0.620329 6 0.874439C6 1.12855 5.91603 1.33782 5.74809 1.50224L2.17558 5Z" fill="#002E47"/></svg></button>
              <div class="image-carousel-editor-dots"></div>
              <button class="carousel-arrow carousel-next"><svg xmlns="http://www.w3.org/2000/svg" width="6" height="10" viewBox="0 0 6 10" fill="none"><path d="M3.82442 5L0.251908 1.50224C0.0839699 1.33782 -1.78108e-07 1.12855 -1.55893e-07 0.874441C-1.33678e-07 0.620329 0.08397 0.411064 0.251908 0.246639C0.419847 0.0822139 0.633587 4.84527e-07 0.893128 5.07217e-07C1.15267 5.29907e-07 1.36641 0.082214 1.53435 0.246639L5.74808 4.3722C5.83968 4.46188 5.90441 4.55904 5.94228 4.66368C5.98014 4.76831 5.99938 4.88042 5.99999 5C6.0006 5.11958 5.98136 5.23169 5.94228 5.33632C5.90319 5.44096 5.83846 5.53812 5.74808 5.6278L1.53435 9.75336C1.36641 9.91779 1.15267 10 0.893127 10C0.633586 10 0.419846 9.91779 0.251907 9.75336C0.0839692 9.58894 -8.99443e-07 9.37967 -8.77228e-07 9.12556C-8.55013e-07 8.87145 0.0839692 8.66218 0.251907 8.49776L3.82442 5Z" fill="#002E47"/></svg></button>
            `

            let currentIndex = 0

            const initCarousel = () => {
              const wrapperEl = domElement.closest('.image-carousel-wrapper')
              if (!wrapperEl) return

              const items = wrapperEl.querySelectorAll(':scope > .ck-widget.image-carousel-item, :scope > div > .ck-widget.image-carousel-item, :scope > .image-carousel-item')
              const dotsContainer = domElement.querySelector('.image-carousel-editor-dots')

              if (items.length === 0 || !dotsContainer) return

              // Read saved active index from editor instance
              const savedIndex = typeof editor._carouselActiveIndex === 'number' ? editor._carouselActiveIndex : 0
              currentIndex = savedIndex < items.length ? savedIndex : 0
              // Clear after reading so it doesn't affect future normal navigation
              delete editor._carouselActiveIndex

              dotsContainer.innerHTML = ''
              items.forEach((_, i) => {
                const dot = document.createElement('button')
                dot.className = 'image-carousel-editor-dot' + (i === currentIndex ? ' active' : '')
                dot.addEventListener('click', (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  goTo(i)
                })
                dotsContainer.appendChild(dot)
              })

              items.forEach((item, i) => {
                if (i === currentIndex) {
                  item.classList.add('active')
                } else {
                  item.classList.remove('active')
                }
              })
            }

            const goTo = (index) => {
              const wrapperEl = domElement.closest('.image-carousel-wrapper')
              if (!wrapperEl) return
              const items = wrapperEl.querySelectorAll(':scope > .ck-widget.image-carousel-item, :scope > div > .ck-widget.image-carousel-item, :scope > .image-carousel-item')
              if (items.length === 0) return

              currentIndex = ((index % items.length) + items.length) % items.length
              items.forEach((item, i) => {
                if (i === currentIndex) {
                  item.classList.add('active')
                } else {
                  item.classList.remove('active')
                }
              })

              const dots = domElement.querySelectorAll('.image-carousel-editor-dot')
              dots.forEach((dot, i) => {
                if (i === currentIndex) {
                  dot.classList.add('active')
                } else {
                  dot.classList.remove('active')
                }
              })
            }

            domElement.querySelector('.carousel-prev').addEventListener('click', (e) => {
              e.preventDefault()
              e.stopPropagation()
              goTo(currentIndex - 1)
            })
            domElement.querySelector('.carousel-next').addEventListener('click', (e) => {
              e.preventDefault()
              e.stopPropagation()
              goTo(currentIndex + 1)
            })

            setTimeout(initCarousel, 100)

            const observerCallback = () => {
              setTimeout(initCarousel, 50)
            }
            setTimeout(() => {
              const wrapperEl = domElement.closest('.image-carousel-wrapper')
              if (wrapperEl) {
                const observer = new MutationObserver(observerCallback)
                observer.observe(wrapperEl, { childList: true, subtree: false })
              }
            }, 200)

            return domElement
          },
        )

        viewWriter.insert(
          viewWriter.createPositionAt(wrapper, 'end'),
          navElement,
        )

        return toWidget(wrapper, viewWriter, {
          label: 'image carousel widget',
        })
      },
    })

    // IMAGE CAROUSEL ITEM
    conversion.for('upcast').elementToElement({
      model: 'imageCarouselItem',
      view: {
        name: 'div',
        classes: ['image-carousel-item'],
      },
    })
    conversion.for('dataDowncast').elementToElement({
      model: 'imageCarouselItem',
      view: (modelElement, { writer: viewWriter }) => {
        const carouselWrapper = modelElement.parent
        const itemIndex = carouselWrapper
          ? carouselWrapper._children._nodes.indexOf(modelElement)
          : 0

        return viewWriter.createContainerElement('div', {
          class: itemIndex === 0 ? 'image-carousel-item active' : 'image-carousel-item',
        })
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'imageCarouselItem',
      view: (modelElement, { writer: viewWriter }) => {
        const carouselWrapper = modelElement.parent
        const itemCount = carouselWrapper
          ? carouselWrapper._children._nodes.length
          : 0
        const itemIndex = carouselWrapper
          ? carouselWrapper._children._nodes.indexOf(modelElement)
          : 0

        const div = viewWriter.createContainerElement('div', {
          class: 'image-carousel-item',
        })

        if (itemCount > 2) {
          const closeButton = viewWriter.createRawElement(
            'div',
            {
              class: 'carousel-item-delete-container',
            },
            function (domElement) {
              domElement.innerHTML = `
                <button class="carousel-item-delete-btn">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6H5H21" stroke="#002E47" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#002E47" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              `
              const button = domElement.querySelector('.carousel-item-delete-btn')
              button.addEventListener('mousedown', (e) => {
                e.preventDefault()
                e.stopPropagation()
                e.stopImmediatePropagation()
              })
              button.addEventListener('click', (e) => {
                e.preventDefault()
                e.stopPropagation()
                e.stopImmediatePropagation()
                // Directly remove this specific model element
                const carouselWrap = modelElement.parent
                if (!carouselWrap) return
                const allItems = Array.from(carouselWrap.getChildren()).filter(
                  child => child.name === 'imageCarouselItem'
                )
                if (allItems.length <= 2) return
                const deletedIndex = allItems.indexOf(modelElement)
                const newActiveIndex = deletedIndex > 0 ? deletedIndex - 1 : 0
                editor._carouselActiveIndex = newActiveIndex
                editor.model.change(writer => {
                  writer.remove(modelElement)
                  editor.editing.reconvertItem(carouselWrap)
                  editor.editing.reconvertItem(carouselWrap.parent.parent)
                  editor.editing.view.forceRender()
                })
              })
              return domElement
            },
          )
          viewWriter.insert(viewWriter.createPositionAt(div, 0), closeButton)
        }

        return toWidget(div, viewWriter, {
          label: 'image carousel item widget',
          hasSelectionHandle: true,
        })
      },
    })

    // QUOTE
    conversion.for('upcast').elementToElement({
      model: 'quote',
      view: {
        name: 'blockquote',
      },
    })
    conversion.for('dataDowncast').elementToElement({
      model: 'quote',
      view: {
        name: 'blockquote',
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'quote',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('blockquote')

        return toWidgetEditable(div, viewWriter)
      },
    })
    // IFRAME
    conversion.for('upcast').elementToElement({
      view: {
        name: 'iframe',
        classes: 'iframe-player',
      },
      model: (viewElement, { writer: modelWriter }) => {
        return modelWriter.createElement('iframePlayer', {
          src: viewElement.getAttribute('src'),
        })
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'iframePlayer',
      view: (modelElement, { writer: viewWriter }) => {
        const iframe = viewWriter.createEditableElement('iframe', {
          class: 'iframe-player',
          src: modelElement.getAttribute('src'),
          frameborder: '0',
          allow: 'autoplay; encrypted-media',
          allowfullscreen: true,
        })

        return toWidget(iframe, viewWriter, { label: 'Iframe player editable' })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'iframePlayer',
      view: (modelElement, { writer: viewWriter }) => {
        return viewWriter.createContainerElement('iframe', {
          class: 'iframe-player',
          src: modelElement.getAttribute('src'),
          frameborder: '0',
          allow: 'autoplay; encrypted-media',
          allowfullscreen: true,
        })
      },
    })

    //VIDEO
    conversion.for('upcast').elementToElement({
      view: {
        name: 'video',
        classes: 'video-player',
      },
      model: (viewElement, { writer: modelWriter }) => {
        // Create base attributes
        const modelAttributes = {
          src: viewElement.getAttribute('src'),
          aspectratio: viewElement.getAttribute('aspectratio'),
          poster: viewElement.getAttribute('poster'),
          controlslist: 'nofullscreen nodownload noplaybackrate noremoteplayback',
          disablePictureInPicture: 'true',
        }

        // Only add autoplay if it exists in the HTML
        if (viewElement.hasAttribute('autoplay')) {
          modelAttributes.autoplay = true
        }

        if (viewElement.hasAttribute('muted')) {
          modelAttributes.muted = true
        }

        if (viewElement.hasAttribute('data-shop-clip-id')) {
          modelAttributes.shopClipId = viewElement.getAttribute('data-shop-clip-id')
        }

        if (viewElement.hasAttribute('crossorigin')) {
          modelAttributes.crossorigin = viewElement.getAttribute('crossorigin')
        }

        // Learner controls
        if (viewElement.getAttribute('data-must-complete') === 'true') {
          modelAttributes.mustComplete = true
        }
        if (viewElement.getAttribute('data-skip-ahead') === 'false') {
          modelAttributes.disableSkipAhead = true
        }
        if (viewElement.getAttribute('data-hide-speed') === 'true') {
          modelAttributes.hideSpeed = true
        }

        // Read <track> children
        const tracks = []
        for (const child of viewElement.getChildren()) {
          if (child.is && child.is('element', 'track')) {
            tracks.push({
              kind: child.getAttribute('kind') || 'subtitles',
              src: child.getAttribute('src') || '',
              srclang: child.getAttribute('srclang') || '',
              label: child.getAttribute('label') || '',
              default: child.hasAttribute('default'),
            })
          }
        }
        if (tracks.length > 0) {
          modelAttributes.tracks = JSON.stringify(tracks)
        }

        return modelWriter.createElement('videoPlayer', modelAttributes)
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'videoPlayer',
      view: (modelElement, { writer: viewWriter }) => {
        const src = modelElement.getAttribute('src')
        const aspectratio = modelElement.getAttribute('aspectratio')
        const poster = modelElement.getAttribute('poster')
        const shopClipId = modelElement.getAttribute('shopClipId')

        const container = viewWriter.createContainerElement('div', {
          class: 'video-wrapper',
        })

        const videoAttributes = {
          class: 'video-player',
          src: src,
          aspectratio: aspectratio,
          controls: 'true',
          muted: 'muted',
          controlslist: 'nofullscreen nodownload noplaybackrate noremoteplayback',
          disablePictureInPicture: 'true',
          playsinline: 'playsinline',
        }

        // Only add poster if it exists
        if (poster) {
          videoAttributes.poster = poster
        }

        if (shopClipId) {
          videoAttributes['data-shop-clip-id'] = shopClipId
        }

        // Add crossorigin for subtitle support
        const tracksJsonCheck = modelElement.getAttribute('tracks')
        if (tracksJsonCheck || modelElement.getAttribute('crossorigin')) {
          videoAttributes.crossorigin = 'anonymous'
        }

        const video = viewWriter.createEditableElement('video', videoAttributes)

        // Insert <track> elements into video
        const tracksJson = modelElement.getAttribute('tracks')
        if (tracksJson) {
          try {
            const tracks = JSON.parse(tracksJson)
            tracks.forEach(track => {
              const trackAttrs = {
                kind: track.kind || 'subtitles',
                src: track.src,
                srclang: track.srclang,
              }
              if (track.label) trackAttrs.label = track.label
              if (track.default) trackAttrs.default = 'default'
              const trackEl = viewWriter.createEmptyElement('track', trackAttrs)
              viewWriter.insert(viewWriter.createPositionAt(video, 'end'), trackEl)
            })
          } catch (e) {}
        }

        const playButton = viewWriter.createUIElement(
          'div',
          { class: 'play-button-overlay' },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)
            domElement.innerHTML =
              '<svg viewBox="0 0 24 24"><path fill="white" d="M8 5v14l11-7z"/></svg>'

            domElement.addEventListener('click', e => {
              if (!domElement.parentNode) return

              const videoElement = domElement.parentNode.querySelector('video')
              if (videoElement) {
                if (videoElement.paused) {
                  // ✅ Autoplay çalışmazsa user interaction ile play
                  videoElement.muted = false // User click'te unmute
                  videoElement.play().catch(err => {
                    videoElement.muted = true // Fallback to muted
                    videoElement.play()
                  })
                } else {
                  videoElement.pause()
                }
              }
            })

            setTimeout(() => {
              if (domElement.parentNode) {
                const videoElement = domElement.parentNode.querySelector(
                  'video',
                )
                if (videoElement) {
                  // ✅ Autoplay başarısız olursa play button'u göster
                  videoElement.addEventListener('pause', () => {
                    domElement.style.display = 'flex'
                  })

                  videoElement.addEventListener('play', () => {
                    domElement.style.display = 'none'
                  })

                  videoElement.addEventListener('ended', () => {
                    domElement.style.display = 'flex'
                  })

                  // ✅ Autoplay policy check
                  if (videoElement.autoplay && !videoElement.paused) {
                    domElement.style.display = 'none'
                  } else {
                    domElement.style.display = videoElement.paused
                      ? 'flex'
                      : 'none'
                  }
                }
              }
            }, 100) // ✅ Biraz daha uzun timeout

            return domElement
          },
        )

        viewWriter.insert(viewWriter.createPositionAt(container, 0), video)
        viewWriter.insert(
          viewWriter.createPositionAt(container, 'end'),
          playButton,
        )

        return toWidget(container, viewWriter, {
          label: 'Video player editable',
          hasSelectionHandle: true,
          preventAutoparagraph: true,
          isAllowedInsideAttributeElement: true,
        })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'videoPlayer',
      view: (modelElement, { writer: viewWriter }) => {
        const src = modelElement.getAttribute('src')
        const aspectratio = modelElement.getAttribute('aspectratio')
        const poster = modelElement.getAttribute('poster')
        const autoplay = modelElement.getAttribute('autoplay')
        const shopClipId = modelElement.getAttribute('shopClipId')

        const container = viewWriter.createContainerElement('div', {
          class: 'video-wrapper',
        })

        const videoAttributes = {
          class: 'video-player',
          src: src,
          aspectratio: aspectratio,
          controls: 'true',
          controlslist: 'nodownload',
          playsinline: 'playsinline',
          onclick:
            "if(this.paused){this.muted=false;this.play().catch(()=>{this.muted=true;this.play();});this.nextElementSibling.style.display='none';}else{this.pause();this.nextElementSibling.style.display='flex';}",
          onplay: "this.nextElementSibling.style.display='none';",
          onpause: "this.nextElementSibling.style.display='flex';",
        }

        if (autoplay) {
          videoAttributes.autoplay = 'autoplay'
          const muted = modelElement.getAttribute('muted')
          if (muted === true) {
            videoAttributes.muted = 'muted'
          }
        }

        if (poster) {
          videoAttributes.poster = poster
        }

        if (shopClipId) {
          videoAttributes['data-shop-clip-id'] = shopClipId
        }

        // Learner controls, enforced by the app
        if (modelElement.getAttribute('mustComplete') === true) {
          videoAttributes['data-must-complete'] = 'true'
        }
        if (modelElement.getAttribute('disableSkipAhead') === true) {
          videoAttributes['data-skip-ahead'] = 'false'
        }
        if (modelElement.getAttribute('hideSpeed') === true) {
          videoAttributes['data-hide-speed'] = 'true'
          videoAttributes.controlslist = 'nodownload noplaybackrate'
        }

        // Add crossorigin for subtitle support
        const tracksJsonData = modelElement.getAttribute('tracks')
        if (tracksJsonData || modelElement.getAttribute('crossorigin')) {
          videoAttributes.crossorigin = 'anonymous'
        }

        const video = viewWriter.createContainerElement(
          'video',
          videoAttributes,
        )

        // Insert <track> elements into video
        const tracksJson = modelElement.getAttribute('tracks')
        if (tracksJson) {
          try {
            const tracks = JSON.parse(tracksJson)
            tracks.forEach(track => {
              const trackAttrs = {
                kind: track.kind || 'subtitles',
                src: track.src,
                srclang: track.srclang,
              }
              if (track.label) trackAttrs.label = track.label
              if (track.default) trackAttrs.default = 'default'
              const trackEl = viewWriter.createEmptyElement('track', trackAttrs)
              viewWriter.insert(viewWriter.createPositionAt(video, 'end'), trackEl)
            })
          } catch (e) {}
        }

        // ✅ Play button da tek satırda
        const playButton = viewWriter.createContainerElement('div', {
          class: 'play-button-overlay',
          onclick:
            "var video=this.previousElementSibling;if(video.paused){video.muted=false;video.play().catch(()=>{video.muted=true;video.play();});this.style.display='none';}else{video.pause();this.style.display='flex';}",
        })

        const playIcon = viewWriter.createRawElement(
          'div',
          { class: 'play-icon' },
          domElement => {
            // ✅ innerHTML'de backtick yerine normal string
            domElement.innerHTML =
              '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="#fff" d="m6.192 3.67l13.568 7.633a.8.8 0 0 1 0 1.394L6.192 20.33A.8.8 0 0 1 5 19.632V4.368a.8.8 0 0 1 1.192-.697"/></svg>'
          },
        )

        viewWriter.insert(viewWriter.createPositionAt(playButton, 0), playIcon)
        viewWriter.insert(viewWriter.createPositionAt(container, 0), video)
        viewWriter.insert(
          viewWriter.createPositionAt(container, 'end'),
          playButton,
        )

        return container
      },
    })

    conversion.for('editingDowncast').markerToHighlight({
      model: 'persistentHighlight',
      view: {
        classes: 'persistentHighlight',
      },
    })

    // AUDIO
    conversion.for('upcast').elementToElement({
      view: {
        name: 'audio',
        classes: 'audio-player',
      },
      model: (viewElement, { writer: modelWriter }) => {
        return modelWriter.createElement('audioPlayer', {
          src: viewElement.getAttribute('src'),
        })
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'audioPlayer',
      view: (modelElement, { writer: viewWriter }) => {
        const src = modelElement.getAttribute('src')

        const wrapper = viewWriter.createContainerElement('div', {
          class: 'simple-box-wrapper',
        })

        const simpleBox = viewWriter.createContainerElement('div', {
          class: 'simple-box',
        })

        const column = viewWriter.createContainerElement('div', {
          class: 'column',
        })

        const audio = viewWriter.createEditableElement('audio', {
          class: 'audio-player',
          src: src,
          controls: 'true',
          style: 'width:100%',
        })

        viewWriter.insert(viewWriter.createPositionAt(column, 0), audio)
        viewWriter.insert(viewWriter.createPositionAt(simpleBox, 0), column)
        viewWriter.insert(viewWriter.createPositionAt(wrapper, 0), simpleBox)

        return toWidget(wrapper, viewWriter, {
          label: 'Audio player editable',
          hasSelectionHandle: true,
        })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'audioPlayer',
      view: (modelElement, { writer: viewWriter }) => {
        const wrapper = viewWriter.createContainerElement('div', {
          class: 'simple-box-wrapper',
        })

        const simpleBox = viewWriter.createContainerElement('div', {
          class: 'simple-box',
        })

        const column = viewWriter.createContainerElement('div', {
          class: 'column',
        })

        const audio = viewWriter.createContainerElement('audio', {
          class: 'audio-player',
          src: modelElement.getAttribute('src'),
          controls: 'true',
          style: 'width:100%',
        })

        viewWriter.insert(viewWriter.createPositionAt(column, 0), audio)
        viewWriter.insert(viewWriter.createPositionAt(simpleBox, 0), column)
        viewWriter.insert(viewWriter.createPositionAt(wrapper, 0), simpleBox)

        return wrapper
      },
    })
    //Accordion

    conversion.for('upcast').elementToElement({
      model: 'accordionWrapper',
      view: {
        name: 'div',
        classes: ['accordion-wrapper'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'accordionWrapper',
      view: {
        name: 'div',
        classes: ['accordion-wrapper'],
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'accordionWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const wrapper = viewWriter.createContainerElement('div', {
          class: 'accordion-wrapper',
        })
        return toWidget(wrapper, viewWriter)
      },
    })

    // Accordion Item converters
    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: 'accordion-item',
      },
      model: (viewElement, { writer: modelWriter }) => {
        const isOpen = viewElement.getAttribute('data-open') === 'true'
        return modelWriter.createElement('accordionItem', {
          isOpen: isOpen,
        })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'accordionItem',
      view: (modelElement, { writer: viewWriter }) => {
        const isOpen = modelElement.getAttribute('isOpen')
        return viewWriter.createContainerElement('div', {
          class: 'accordion-item',
          'data-open': isOpen ? 'true' : 'false',
        })
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'accordionItem',
      view: (modelElement, { writer: viewWriter }) => {
        const isOpen = modelElement.getAttribute('isOpen')
        const addOpen = modelElement.getAttribute('addOpen')

        const div = viewWriter.createContainerElement('div', {
          class: 'accordion-item',
          'data-open': isOpen || addOpen ? 'true' : 'false',
        })

        return toWidget(div, viewWriter, {
          label: 'accordion item widget',
          hasSelectionHandle: true,
        })
      },
    })

    // Accordion Content converters
    conversion.for('upcast').elementToElement({
      model: 'accordionContent',
      view: {
        name: 'div',
        classes: ['accordion-content'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'accordionContent',
      view: {
        name: 'div',
        classes: ['accordion-content'],
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'accordionContent',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('div', {
          class: 'accordion-content',
        })
        return toWidgetEditable(div, viewWriter)
      },
    })

    // Tab Container converters
    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: ['tab-container'],
      },
      model: (viewElement, { writer }) => {
        const activeTab = viewElement.getAttribute('data-active-tab') || '1'
        const type = viewElement.getAttribute('data-type') || 'horizontal'
        return writer.createElement('tabContainer', {
          activeTab: activeTab,
          type: type,
        })
      },
    })

    // 3. tabContainer dataDowncast converter'ını güncelle
    conversion.for('dataDowncast').elementToElement({
      model: 'tabContainer',
      view: (modelElement, { writer: viewWriter }) => {
        const activeTab = '1'
        const type = modelElement.getAttribute('type') || 'horizontal'

        const classes = ['tab-container']
        if (type === 'vertical') {
          classes.push('tab-container--vertical')
        }

        return viewWriter.createContainerElement('div', {
          class: classes.join(' '),
          'data-active-tab': activeTab,
          'data-type': type,
        })
      },
    })

    // 4. tabContainer editingDowncast converter'ını güncelle
    conversion.for('editingDowncast').elementToElement({
      model: 'tabContainer',
      view: (modelElement, { writer: viewWriter }) => {
        const activeTab = modelElement.getAttribute('activeTab') || '1'
        const type = modelElement.getAttribute('type') || 'horizontal'

        const classes = ['tab-container']
        if (type === 'vertical') {
          classes.push('tab-container--vertical')
        }

        const container = viewWriter.createContainerElement('div', {
          class: classes.join(' '),
          'data-active-tab': activeTab,
          'data-type': type,
        })

        return toWidget(container, viewWriter, {
          label: 'tab container widget',
          hasSelectionHandle: true,
        })
      },
    })

    // 5. Attribute mapping ekle
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'type',
      view: 'data-type',
    })

    // Tab Headers converters
    conversion.for('upcast').elementToElement({
      model: 'tabHeaders',
      view: { name: 'div', classes: ['tab-headers'] },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'tabHeaders',
      view: { name: 'div', classes: ['tab-headers'] },
    })

    conversion.for('editingDowncast').elementToStructure({
      model: 'tabHeaders',
      view: (modelElement, conversionApi) => {
        const { writer } = conversionApi
        const tabContainer = modelElement.parent
        const type = tabContainer ? tabContainer.getAttribute('type') : 'horizontal'

        // Vertical: no scroll wrapper, just tab-headers
        if (type === 'vertical') {
          return writer.createContainerElement('div', { class: 'tab-headers' }, [
            writer.createSlot()
          ])
        }

        // Horizontal: scroll wrapper with left/right buttons
        const leftBtn = writer.createUIElement('button', {
          class: 'tab-scroll-btn tab-scroll-left',
          type: 'button',
        }, function(domDocument) {
          const btn = this.toDomElement(domDocument)
          btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>`
          btn.addEventListener('click', (e) => {
            e.preventDefault()
            e.stopPropagation()
            const headers = btn.parentElement.querySelector('.tab-headers')
            if (headers) {
              headers.scrollBy({ left: -200, behavior: 'smooth' })
            }
          })
          // Set up scroll tracking after DOM is ready
          setTimeout(() => {
            const headers = btn.parentElement ? btn.parentElement.querySelector('.tab-headers') : null
            if (headers) {
              const update = () => updateTabScrollButtons(headers)
              headers.addEventListener('scroll', update)
              try { new ResizeObserver(update).observe(headers) } catch(e) {}
              update()
            }
          }, 100)
          return btn
        })

        const rightBtn = writer.createUIElement('button', {
          class: 'tab-scroll-btn tab-scroll-right',
          type: 'button',
        }, function(domDocument) {
          const btn = this.toDomElement(domDocument)
          btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>`
          btn.addEventListener('click', (e) => {
            e.preventDefault()
            e.stopPropagation()
            const headers = btn.parentElement.querySelector('.tab-headers')
            if (headers) {
              headers.scrollBy({ left: 200, behavior: 'smooth' })
            }
          })
          return btn
        })

        return writer.createContainerElement('div', { class: 'tab-headers-scroll-wrapper' }, [
          leftBtn,
          writer.createContainerElement('div', { class: 'tab-headers' }, [
            writer.createSlot()
          ]),
          rightBtn,
        ])
      }
    })
    // 2. Update the tab header upcast converter to set only first tab as active
    conversion.for('upcast').elementToElement({
      view: {
        name: 'button',
        classes: ['tab-header'],
      },
      model: (viewElement, { writer }) => {
        const tabIndex = viewElement.getAttribute('data-tab-index') || '1'
        const tabTitle =
          viewElement.getAttribute('data-tab-title') || `Tab ${tabIndex}`
        // Only set first tab as active when loading
        const isActive = tabIndex === '1' ? 'true' : 'false'
        const icon = viewElement.getAttribute('data-icon') || ''
        const backgroundColor =
          viewElement.getAttribute('data-background-color') || ''
        const textColor = viewElement.getAttribute('data-text-color') || ''
        const text = viewElement.getAttribute('data-text') || tabTitle

        return writer.createElement('tabHeader', {
          tabIndex: tabIndex,
          tabTitle: tabTitle,
          isActive: isActive,
          icon: icon,
          backgroundColor: backgroundColor,
          textColor: textColor,
          text: text,
        })
      },
    })

    // 3. Update the dataDowncast converter to save new attributes
    conversion.for('dataDowncast').elementToElement({
      model: 'tabHeader',
      view: (modelElement, { writer: viewWriter }) => {
        const tabIndex = modelElement.getAttribute('tabIndex') || '1'
        const tabTitle = modelElement.getAttribute('tabTitle') || 'Tab'
        const isActive = tabIndex === '1' ? true : false
        const icon = modelElement.getAttribute('icon') || ''
        const backgroundColor =
          modelElement.getAttribute('backgroundColor') || ''
        const textColor = modelElement.getAttribute('textColor') || ''
        const text = modelElement.getAttribute('text') || tabTitle

        // Build style string
        let styleString = ''

        if (textColor) {
          styleString += `color: ${textColor}; `
        }

        return viewWriter.createContainerElement('button', {
          class: `tab-header${isActive ? ' active' : ''}`,
          'data-tab-index': tabIndex,
          'data-tab-title': tabTitle,
          'data-is-active': isActive ? 'true' : 'false',
          'data-icon': icon,
          'data-background-color': backgroundColor,
          'data-text-color': textColor,
          'data-text': text,
          style: styleString.trim(),
        })
      },
    })

    // 4. Update the editingDowncast converter with enhanced styling and content
    conversion.for('editingDowncast').elementToElement({
      model: 'tabHeader',
      view: (modelElement, { writer: viewWriter }) => {
        const tabIndex = modelElement.getAttribute('tabIndex') || '1'
        const isActive = modelElement.getAttribute('isActive') === 'true'
        const icon = modelElement.getAttribute('icon') || ''
        const backgroundColor =
          modelElement.getAttribute('backgroundColor') || ''
        const textColor = modelElement.getAttribute('textColor') || ''

        // Build style string

        let styleString =
          'position: relative; display: flex; align-items: center; gap: 8px; '
        if (isActive === true) {
          if (backgroundColor) {
            styleString += `border-bottom-color: ${backgroundColor}; `
          }
        }
        if (textColor) {
          styleString += `color: ${textColor}; `
        }

        // Main container (button) - Keep it editable for text content
        const button = viewWriter.createEditableElement('button', {
          class: `tab-header${isActive ? ' active' : ''}`,
          'data-tab-index': tabIndex,
          'data-is-active': isActive ? 'true' : 'false',
          'data-icon': icon,
          'data-background-color': backgroundColor,
          'data-text-color': textColor,
          style: styleString,
        })

        // Add icon if present - as a UI element so it doesn't interfere with text editing
        if (icon) {
          const iconElement = viewWriter.createUIElement(
            'iconify-icon',
            {
              icon: icon,
              class: 'tab-header-icon',
              style: 'width: 16px; height: 16px; flex-shrink: 0;',
            },
            function (domDocument) {
              const iconEl = this.toDomElement(domDocument)
              return iconEl
            },
          )
          viewWriter.insert(viewWriter.createPositionAt(button, 0), iconElement)
        }

        // Edit button - positioned absolutely to not interfere with text
        const editButton = viewWriter.createUIElement(
          'span',
          {
            class: 'tab-edit-btn',
            style: `
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s;
          z-index: 10;
        `,
            title: 'Edit Tab',
          },
          function (domDocument) {
            const span = this.toDomElement(domDocument)
            span.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
  <path d="M13.5816 3.28702C14.0848 2.78372 14.7673 2.50092 15.479 2.50085C16.1907 2.50078 16.8733 2.78344 17.3766 3.28664C17.8799 3.78984 18.1627 4.47237 18.1628 5.18407C18.1629 5.89578 17.8802 6.57836 17.377 7.08166L16.7081 7.75135L12.9134 3.95595L13.5816 3.28702ZM12.1185 4.75163L4.61919 12.2502C4.31438 12.5547 4.10018 12.9379 4.0005 13.3571L3.17933 16.8083C3.15709 16.9017 3.15918 16.9994 3.18538 17.0918C3.21159 17.1842 3.26104 17.2684 3.32902 17.3363C3.397 17.4042 3.48125 17.4536 3.57372 17.4797C3.66619 17.5058 3.76381 17.5078 3.85726 17.4854L7.30769 16.6635C7.72713 16.564 8.11063 16.3498 8.41534 16.0448L15.9131 8.54628L12.1185 4.75163Z" fill="#002E47"/>
</svg>`
            return span
          },
        )

        viewWriter.insert(
          viewWriter.createPositionAt(button, 'end'),
          editButton,
        )

        // Event listener remains the same as in your original code
        if (!editor._tabClickHandlerAdded) {
          editor._tabClickHandlerAdded = true

          editor.editing.view.document.on('click', (evt, data) => {
            const target = data.target

            let isEditButtonClick = false
            let editButtonElement = null
            let currentElement = target

            // Edit button check
            while (currentElement) {
              if (
                currentElement.hasClass &&
                currentElement.hasClass('tab-edit-btn')
              ) {
                isEditButtonClick = true
                editButtonElement = currentElement
                break
              }
              currentElement = currentElement.parent
            }

            if (isEditButtonClick) {
              evt.stop()
              data.preventDefault()

              let tabHeaderElement = editButtonElement.parent
              const modelElementFromClick = editor.editing.mapper.toModelElement(
                tabHeaderElement,
              )

              if (modelElementFromClick) {
                editor.model.change(writer => {
                  writer.setSelection(modelElementFromClick, 'on')
                })

                const selectedElement = editor.model.document.selection.getSelectedElement()
                const position = editor.model.document.selection.getFirstPosition()

                const tabData = {
                  tabIndex: modelElementFromClick.getAttribute('tabIndex'),
                  isActive: modelElementFromClick.getAttribute('isActive'),
                  icon: modelElementFromClick.getAttribute('icon'),
                  backgroundColor: modelElementFromClick.getAttribute(
                    'backgroundColor',
                  ),
                  textColor: modelElementFromClick.getAttribute('textColor'),
                  text: modelElementFromClick.getAttribute('text'),
                }

                handleTabOptions(
                  editor.config.get('videoGalleryPlugin.dispatch'),
                  editor,
                  data.target,
                  modelElementFromClick,
                  position,
                  selectedElement,
                )
              }
              return
            }

            // Tab header click check
            let tabHeaderElement = null
            currentElement = target

            while (currentElement) {
              if (
                currentElement.hasClass &&
                currentElement.hasClass('tab-header')
              ) {
                tabHeaderElement = currentElement
                break
              }
              currentElement = currentElement.parent
            }

            if (tabHeaderElement) {
              const tabIndex = tabHeaderElement.getAttribute('data-tab-index')
              evt.stop()
              data.preventDefault()
              editor.execute('switchTab', tabIndex)
            }
          })
        }

        return toWidget(button, viewWriter, {
          label: 'tab header widget',
          hasSelectionHandle: true,
        })
      },
    })

    // 5. Add attribute mapping for editing downcast
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'icon',
      view: 'data-icon',
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: 'backgroundColor',
      view: 'data-background-color',
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: 'textColor',
      view: 'data-text-color',
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: 'text',
      view: 'data-text',
    })

    // Tab Contents converters
    conversion.for('upcast').elementToElement({
      model: 'tabContents',
      view: { name: 'div', classes: ['tab-contents'] },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'tabContents',
      view: { name: 'div', classes: ['tab-contents'] },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'tabContents',
      view: (modelElement, { writer: viewWriter }) => {
        return viewWriter.createContainerElement('div', {
          class: 'tab-contents',
        })
      },
    })

    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: ['tab-content'],
      },
      model: (viewElement, { writer }) => {
        const tabIndex = viewElement.getAttribute('data-tab-index') || '1'
        // Only set first tab content as active when loading
        const isActive = tabIndex === '1' ? 'true' : 'false'

        return writer.createElement('tabContent', {
          tabIndex: tabIndex,
          isActive: isActive,
        })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'tabContent',
      view: (modelElement, { writer: viewWriter }) => {
        const tabIndex = modelElement.getAttribute('tabIndex') || '1'
        const isActive = tabIndex === '1' ? true : false

        return viewWriter.createContainerElement('div', {
          class: `tab-content${isActive ? ' active' : ''}`,
          'data-tab-index': tabIndex,
          'data-is-active': isActive ? 'true' : 'false',
        })
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'tabContent',
      view: (modelElement, { writer: viewWriter }) => {
        const tabIndex = modelElement.getAttribute('tabIndex') || '1'
        const isActive = modelElement.getAttribute('isActive') === 'true'

        const div = viewWriter.createEditableElement('div', {
          class: `tab-content${isActive ? ' active' : ''}`,
          'data-tab-index': tabIndex,
          'data-is-active': isActive ? 'true' : 'false',
        })

        return toWidgetEditable(div, viewWriter)
      },
    })
    //Accordion

    conversion.for('upcast').elementToElement({
      model: 'accordionWrapper',
      view: {
        name: 'div',
        classes: ['accordion-wrapper'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'accordionWrapper',
      view: {
        name: 'div',
        classes: ['accordion-wrapper'],
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'accordionWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const wrapper = viewWriter.createContainerElement('div', {
          class: 'accordion-wrapper',
        })
        return toWidget(wrapper, viewWriter)
      },
    })

    // Accordion Item converters
    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: 'accordion-item',
      },
      model: (viewElement, { writer: modelWriter }) => {
        const isOpen = viewElement.getAttribute('data-open') === 'true'
        return modelWriter.createElement('accordionItem', {
          isOpen: isOpen,
        })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'accordionItem',
      view: (modelElement, { writer: viewWriter }) => {
        const isOpen = modelElement.getAttribute('isOpen')
        return viewWriter.createContainerElement('div', {
          class: 'accordion-item',
          'data-open': isOpen ? 'true' : 'false',
        })
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'accordionItem',
      view: (modelElement, { writer: viewWriter }) => {
        const isOpen = modelElement.getAttribute('isOpen')
        const addOpen = modelElement.getAttribute('addOpen')

        const div = viewWriter.createContainerElement('div', {
          class: 'accordion-item',
          'data-open': isOpen || addOpen ? 'true' : 'false',
        })

        return toWidget(div, viewWriter, {
          label: 'accordion item widget',
          hasSelectionHandle: true,
        })
      },
    })

    // Accordion Header converters
    conversion.for('upcast').elementToElement({
      model: 'accordionHeader',
      view: {
        name: 'div',
        classes: ['accordion-header'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'accordionHeader',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createContainerElement('div', {
          class: 'accordion-header',
        })

        // Create toggle button for data output
        const toggleButton = viewWriter.createUIElement(
          'button',
          {
            class: 'accordion-toggle-button',
            'aria-expanded': 'false',
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            // SVG for closed state (down arrow)
            const closedSVG = `
          <svg class="accordion-chevron" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 8.49963L12 15.4996L19 8.49963" stroke="#002E47" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `

            // SVG for open state (up arrow)
            const openSVG = `
          <svg class="accordion-chevron" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12.0233 8.502C12.2569 8.50154 12.4833 8.58292 12.6633 8.732L18.6633 13.732C19.0885 14.0855 19.1467 14.7167 18.7933 15.142C18.4398 15.5673 17.8085 15.6255 17.3833 15.272L12.0233 10.792L6.66326 15.112C6.45666 15.2798 6.19171 15.3583 5.92707 15.3301C5.66243 15.302 5.41993 15.1695 5.25326 14.962C5.06812 14.7541 4.978 14.4785 5.00457 14.2014C5.03114 13.9244 5.17199 13.6709 5.39326 13.502L11.3933 8.672C11.5783 8.54648 11.8002 8.48663 12.0233 8.502Z" fill="#002E47"/>
          </svg>
        `

            // Set initial state (closed)
            domElement.innerHTML = closedSVG

            // Add click event for toggling
            domElement.addEventListener('click', event => {
              event.stopPropagation()

              const accordionItem = domElement.closest('.accordion-item')
              if (accordionItem) {
                const isOpen = accordionItem.dataset.open === 'true'
                accordionItem.dataset.open = (!isOpen).toString()

                const content = accordionItem.querySelector(
                  '.accordion-content',
                )

                if (content) {
                  if (!isOpen) {
                    // Opening accordion
                    content.style.display = 'block'
                    domElement.innerHTML = openSVG
                    domElement.setAttribute('aria-expanded', 'true')
                  } else {
                    // Closing accordion
                    content.style.display = 'none'
                    domElement.innerHTML = closedSVG
                    domElement.setAttribute('aria-expanded', 'false')
                  }
                }
              }
            })

            return domElement
          },
        )

        viewWriter.insert(viewWriter.createPositionAt(div, 'end'), toggleButton)
        return div
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'accordionHeader',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('div', {
          class: 'accordion-header',
        })

        // Create a single UIElement that contains all the controls
        const controlsContainer = viewWriter.createUIElement(
          'div',
          {
            class: 'accordion-header-controls',
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            // SVG for closed state (down arrow)
            const closedSVG = `
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 8.49963L12 15.4996L19 8.49963" stroke="#002E47" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `

            // SVG for open state (up arrow)
            const openSVG = `
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12.0233 8.502C12.2569 8.50154 12.4833 8.58292 12.6633 8.732L18.6633 13.732C19.0885 14.0855 19.1467 14.7167 18.7933 15.142C18.4398 15.5673 17.8085 15.6255 17.3833 15.272L12.0233 10.792L6.66326 15.112C6.45666 15.2798 6.19171 15.3583 5.92707 15.3301C5.66243 15.302 5.41993 15.1695 5.25326 14.962C5.06812 14.7541 4.978 14.4785 5.00457 14.2014C5.03114 13.9244 5.17199 13.6709 5.39326 13.502L11.3933 8.672C11.5783 8.54648 11.8002 8.48663 12.0233 8.502Z" fill="#002E47"/>
          </svg>
        `

            const column = modelElement.parent.parent.parent

            // Count accordion wrappers in the column
            const accordionWrappers = column
              ? Array.from(column.getChildren()).filter(
                child => child.name === 'accordionWrapper',
              )
              : []
            const shouldShowDeleteButton = accordionWrappers.length > 1

            let controlsHTML = ''

            // Conditionally add trash button (left side) - only if there's more than one item
            if (shouldShowDeleteButton) {
              controlsHTML += `
            <div>
              <button class="accordion-item-close-button">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M16.6667 7.49996L15.0042 16.955C14.9363 17.3411 14.7346 17.691 14.4343 17.9431C14.1341 18.1951 13.7545 18.3333 13.3625 18.3333H6.6375C6.24545 18.3333 5.86594 18.1951 5.56569 17.9431C5.26543 17.691 5.06366 17.3411 4.99583 16.955L3.33333 7.49996M17.5 4.99996H12.8125M12.8125 4.99996V3.33329C12.8125 2.89127 12.6369 2.46734 12.3243 2.15478C12.0118 1.84222 11.5879 1.66663 11.1458 1.66663H8.85417C8.41214 1.66663 7.98822 1.84222 7.67566 2.15478C7.36309 2.46734 7.1875 2.89127 7.1875 3.33329V4.99996M12.8125 4.99996H7.1875M2.5 4.99996H7.1875" stroke="#002E47" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          `
            }

            // Add toggle button (right side) - start with closed state
            controlsHTML += `
          <button class="accordion-toggle-button" aria-expanded="false">
            ${closedSVG}
          </button>
        `

            domElement.innerHTML = controlsHTML

            // Add event listeners for trash button (only if it exists)
            if (shouldShowDeleteButton) {
              const closeButton = domElement.querySelector(
                '.accordion-item-close-button',
              )

              if (closeButton) {
                closeButton.addEventListener('click', () => {
                  editor.execute('accordionItemDelete')
                })
              }
            }

            // Add event listeners for toggle button
            const toggleButton = domElement.querySelector(
              '.accordion-toggle-button',
            )
            toggleButton?.addEventListener('click', event => {
              event.stopPropagation()

              const accordionItem = domElement.closest('.accordion-item')
              if (accordionItem) {
                const isOpen = accordionItem.dataset.open === 'true'

                if (!isOpen) {
                  // Kapatılacak diğer açık item'ları bul
                  const allAccordionItems = accordionItem
                    .closest('.column') // veya uygun kapsayıcı
                    ?.querySelectorAll('.accordion-item')

                  allAccordionItems?.forEach(item => {
                    if (item !== accordionItem) {
                      item.dataset.open = 'false'

                      const toggleBtn = item.querySelector(
                        '.accordion-toggle-button',
                      )
                      const content = item.querySelector('.accordion-content')

                      if (toggleBtn) {
                        toggleBtn.innerHTML = closedSVG
                        toggleBtn.setAttribute('aria-expanded', 'false')
                      }

                      if (content) {
                        content.style.display = 'none'
                      }

                      const modelElementToClose = editor.editing.mapper.toModelElement(
                        item,
                      )
                      if (modelElementToClose) {
                        editor.model.change(writer => {
                          writer.setAttribute(
                            'isOpen',
                            false,
                            modelElementToClose,
                          )
                        })
                      }
                    }
                  })
                }

                // Şu anki item'i aç/kapat
                accordionItem.dataset.open = (!isOpen).toString()

                const modelElement = editor.editing.mapper.toModelElement(
                  accordionItem,
                )
                if (modelElement) {
                  editor.model.change(writer => {
                    writer.setAttribute('isOpen', !isOpen, modelElement)
                  })
                }

                const content = accordionItem.querySelector(
                  '.accordion-content',
                )

                if (content) {
                  if (!isOpen) {
                    content.style.display = 'block'
                    toggleButton.innerHTML = openSVG
                    toggleButton.setAttribute('aria-expanded', 'true')
                  } else {
                    content.style.display = 'none'
                    toggleButton.innerHTML = closedSVG
                    toggleButton.setAttribute('aria-expanded', 'false')
                  }
                }
              }
            })

            return domElement
          },
        )

        // Insert controls container at the end of the header
        viewWriter.insert(
          viewWriter.createPositionAt(div, 'end'),
          controlsContainer,
        )

        return toWidgetEditable(div, viewWriter, {
          label: 'accordion box widget',
          hasSelectionHandle: true,
        })
      },
    })
    // Accordion Content converters
    conversion.for('upcast').elementToElement({
      model: 'accordionContent',
      view: {
        name: 'div',
        classes: ['accordion-content'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'accordionContent',
      view: {
        name: 'div',
        classes: ['accordion-content'],
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'accordionContent',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('div', {
          class: 'accordion-content',
        })
        return toWidgetEditable(div, viewWriter)
      },
    })

    // Image Hotspot Commands - Updated to use modal with simpleBox content
    editor.commands.add('openHotspotModal', {
      execute: (options = {}) => {
        const { containerId, hotspotId, mode = 'edit' } = options

        editor.model.change(writer => {
          const root = editor.model.document.getRoot()
          let hotspotContainer = null
          let selectedHotspot = null

          // Find the hotspot container
          if (containerId) {
            const findContainerById = (element, id) => {
              if (
                element.name === 'imageHotspotContainer' &&
                element.getAttribute('id') === id
              ) {
                return element
              }
              if (element.getChildren) {
                for (const child of element.getChildren()) {
                  const result = findContainerById(child, id)
                  if (result) return result
                }
              }
              return null
            }
            hotspotContainer = findContainerById(root, containerId)
          }

          // Find specific hotspot if editing
          if (hotspotId) {
            selectedHotspot = this._findHotspotById(root, hotspotId)
            if (selectedHotspot) {
              hotspotContainer = selectedHotspot.parent
            }
          }

          if (hotspotContainer) {
            // Get all existing hotspots with their content
            const existingHotspots = Array.from(hotspotContainer.getChildren())
              .filter(child => child.name === 'imageHotspot')
              .map(hotspot => {
                // Extract content from imageHotspotContent child
                let contentData = null
                const contentChild = Array.from(hotspot.getChildren()).find(
                  child => child.name === 'imageHotspotContent',
                )

                if (contentChild) {
                  // Convert content to a transferable format
                  const contentFragment = editor.model.change(writer => {
                    const fragment = writer.createDocumentFragment()
                    const contentChildren = Array.from(
                      contentChild.getChildren(),
                    )
                    contentChildren.forEach(child => {
                      writer.append(writer.cloneElement(child, true), fragment)
                    })
                    return fragment
                  })

                  contentData = {
                    html: editor.data.stringify(contentFragment),
                    modelData: contentChild, // Keep reference for modal editing
                  }
                }

                return {
                  id: hotspot.getAttribute('id'),
                  x: parseFloat(hotspot.getAttribute('x') || '50'),
                  y: parseFloat(hotspot.getAttribute('y') || '50'),
                  title: hotspot.getAttribute('title') || 'Hotspot',
                  isVisible: hotspot.getAttribute('isVisible') === 'true',
                  content: contentData,
                }
              })

            const containerData = {
              id: hotspotContainer.getAttribute('id') || Date.now().toString(),
              src: hotspotContainer.getAttribute('src'),
              alt: hotspotContainer.getAttribute('alt') || '',
              width: hotspotContainer.getAttribute('width'),
              height: hotspotContainer.getAttribute('height'),
            }

            // Dispatch modal open event
            const dispatch = editor.config.get('videoGalleryPlugin.dispatch')
            if (dispatch) {
              dispatch({
                type: 'OPEN_HOTSPOT_MODAL',
                payload: {
                  mode: mode,
                  containerData: containerData,
                  hotspots: existingHotspots,
                  selectedHotspotId: hotspotId,
                  element: hotspotContainer,
                  onSave: updatedHotspots => {
                    editor.execute('updateHotspotsFromModal', {
                      containerId: containerData.id,
                      hotspots: updatedHotspots,
                    })
                  },
                },
              })
            }

            // Fallback: call config function if dispatch not available
            const onOpenHotspotModal = editor.config.get(
              'simpleBox.onOpenHotspotModal',
            )
            if (typeof onOpenHotspotModal === 'function') {
              onOpenHotspotModal({
                mode: mode,
                containerData: containerData,
                hotspots: existingHotspots,
                selectedHotspotId: hotspotId,
                element: hotspotContainer,
                onSave: updatedHotspots => {
                  editor.execute('updateHotspotsFromModal', {
                    containerId: containerData.id,
                    hotspots: updatedHotspots,
                  })
                },
              })
            }
          }
        })
      },
    })

    editor.commands.add('updateHotspotsFromModal', {
      execute: (options = {}) => {
        const { containerId, hotspots } = options

        editor.model.change(writer => {
          const root = editor.model.document.getRoot()

          const findContainerById = (element, id) => {
            if (
              element.name === 'imageHotspotContainer' &&
              element.getAttribute('id') === id
            ) {
              return element
            }
            if (element.getChildren) {
              for (const child of element.getChildren()) {
                const result = findContainerById(child, id)
                if (result) return result
              }
            }
            return null
          }

          const hotspotContainer = findContainerById(root, containerId)

          if (hotspotContainer) {
            // Remove all existing hotspots AND their content
            const existingHotspots = Array.from(
              hotspotContainer.getChildren(),
            ).filter(child => child.name === 'imageHotspot')

            const existingContents = Array.from(
              hotspotContainer.getChildren(),
            ).filter(child => child.name === 'imageHotspotContent')

            existingHotspots.forEach(hotspot => writer.remove(hotspot))
            existingContents.forEach(content => writer.remove(content))

            // Add new hotspots with content as siblings
            hotspots.forEach(hotspotData => {
              const hotspot = writer.createElement('imageHotspot', {
                x: hotspotData.x.toString(),
                y: hotspotData.y.toString(),
                title: hotspotData.title,
                id: hotspotData.id,
                isVisible: hotspotData.isVisible ? 'true' : 'false',
              })

              // Create content as sibling with hotspotId
              const hotspotContent = writer.createElement(
                'imageHotspotContent',
                {
                  hotspotId: hotspotData.id,
                },
              )

              // Parse and add content
              if (hotspotData.content && hotspotData.content.html) {
                try {
                  const modelFragment = editor.data.htmlProcessor.toModel(
                    hotspotData.content.html,
                  )
                  const fragmentChildren = Array.from(
                    modelFragment.getChildren(),
                  )
                  fragmentChildren.forEach(child => {
                    writer.append(
                      writer.cloneElement(child, true),
                      hotspotContent,
                    )
                  })
                } catch (error) {
                  console.warn('Failed to parse hotspot content HTML:', error)
                  this.createDefaultHotspotContent(writer, hotspotContent)
                }
              } else {
                this.createDefaultHotspotContent(writer, hotspotContent)
              }

              // Append both as siblings
              writer.append(hotspot, hotspotContainer)
              writer.append(hotspotContent, hotspotContainer)
            })

            editor.editing.reconvertItem(hotspotContainer)
            editor.editing.view.forceRender()
          }
        })
      },
    })
    editor.commands.add('convertImageToHotspotContainer', {
      execute: () => {
        const selection = editor.model.document.selection
        const selectedElement = selection.getSelectedElement()

        if (selectedElement && selectedElement.name === 'imageBlock') {
          editor.model.change(writer => {
            const src = selectedElement.getAttribute('src')
            const alt = selectedElement.getAttribute('alt') || ''

            // Create hotspot container
            const hotspotContainer = writer.createElement(
              'imageHotspotContainer',
              {
                src: src,
                alt: alt,
              },
            )

            // Replace the image with hotspot container
            writer.insert(hotspotContainer, selectedElement, 'before')
            writer.remove(selectedElement)

            // Set selection to the new container
            writer.setSelection(hotspotContainer, 'on')
          })
        }
      },
    })
    editor.commands.add('addImageHotspot', {
      execute: (options = {}) => {
        const {
          x = 50,
          y = 50,
          title = 'New Hotspot',
          hotspotContainer: explicitHotspotContainer,
        } = options

        editor.model.change(writer => {
          const selection = editor.model.document.selection
          let hotspotContainer = null

          // Helper function to find imageHotspotContainer recursively in model
          const findHotspotContainerInCarouselItem = carouselItem => {
            for (const child of carouselItem.getChildren()) {
              if (child.name === 'imageHotspotContainer') {
                return child
              }
            }
            return null
          }

          const findFirstCarouselWrapper = element => {
            if (!element || !element.is?.('element')) return null

            if (element.name === 'imageCarouselWrapper') {
              return element
            }

            if (element.getChildren) {
              for (const child of element.getChildren()) {
                const found = findFirstCarouselWrapper(child)
                if (found) return found
              }
            }

            return null
          }

          const findActiveHotspotContainerInCarousel = carouselWrapper => {
            if (!carouselWrapper || carouselWrapper.name !== 'imageCarouselWrapper') {
              return null
            }

            const viewWrapper = editor.editing.mapper.toViewElement(carouselWrapper)
            if (!viewWrapper) return null

            const domWrapper = editor.editing.view.domConverter.mapViewToDom(viewWrapper)
            if (!domWrapper) return null

            // Find active index from DOM
            const domItems = domWrapper.querySelectorAll('.image-carousel-item')
            let activeIndex = -1
            domItems.forEach((item, i) => {
              if (item.classList.contains('active')) {
                activeIndex = i
              }
            })

            if (activeIndex < 0) return null

            const modelItems = Array.from(carouselWrapper.getChildren()).filter(
              child => child.name === 'imageCarouselItem'
            )
            if (!modelItems[activeIndex]) return null

            return findHotspotContainerInCarouselItem(modelItems[activeIndex])
          }

          const findHotspotContainerFromSelection = () => {
            let element = selection.getSelectedElement()
            if (!element) {
              element = selection.getFirstPosition()?.parent
            }

            while (element && element.name !== 'imageHotspotContainer') {
              element = element.parent
            }

            return element
          }

          const findScopedFallbackContainer = () => {
            let scopeRoot = selection.getSelectedElement()
            if (!scopeRoot) {
              scopeRoot = selection.getFirstPosition()?.parent
            }

            if (!scopeRoot) return null

            while (
              scopeRoot &&
              scopeRoot.name !== 'simpleBox' &&
              scopeRoot.name !== 'simpleBoxWrapper' &&
              scopeRoot.name !== 'imageCarouselWrapper'
            ) {
              scopeRoot = scopeRoot.parent
            }

            const scopedSearchRoot =
              scopeRoot || selection.getSelectedElement() || selection.getFirstPosition()?.parent
            if (!scopedSearchRoot) return null

            const scopedCarouselWrapper = findFirstCarouselWrapper(scopedSearchRoot)
            if (!scopedCarouselWrapper) return null

            return findActiveHotspotContainerInCarousel(scopedCarouselWrapper)
          }

          if (
            explicitHotspotContainer &&
            explicitHotspotContainer.name === 'imageHotspotContainer'
          ) {
            hotspotContainer = explicitHotspotContainer
          }

          if (!hotspotContainer) {
            hotspotContainer = findHotspotContainerFromSelection()
          }

          if (!hotspotContainer) {
            hotspotContainer = findScopedFallbackContainer()
          }

          if (
            !hotspotContainer ||
            hotspotContainer.name !== 'imageHotspotContainer'
          ) {
            return
          }

          const hotspotId = 'hotspot-' + generateUUID()

          // Create hotspot WITHOUT content child
          const hotspot = writer.createElement('imageHotspot', {
            x: x.toString(),
            y: y.toString(),
            title: title,
            id: hotspotId,
            isVisible: 'false',
            backgroundColor: '#015FFB',
            iconColor: '#ffffff',
            icon: '',
            iconName: 'mdi:plus',
          })

          // Create hotspot content as SIBLING with hotspotId attribute
          const hotspotContent = writer.createElement('imageHotspotContent', {
            hotspotId: hotspotId,
          })

          // Create default structure with heading and paragraph
          const simpleBoxWrapper = writer.createElement('simpleBoxWrapper')
          const simpleBox = writer.createElement('simpleBox')
          const column = writer.createElement('column', { 'flex-size': '1' })

          // Create heading
          const heading = writer.createElement('heading2')
          writer.insertText('Heading', heading)

          // Create paragraph
          const paragraph = writer.createElement('paragraph')
          writer.insertText(
            "I'm a paragraph. Click to start writing that will inform, inspire, or encourage your community. We know you have so many things to share with the world. Happy editing!",
            paragraph,
          )

          // Assemble structure
          writer.append(heading, column)
          writer.append(paragraph, column)
          writer.append(column, simpleBox)
          writer.append(simpleBox, simpleBoxWrapper)
          writer.append(simpleBoxWrapper, hotspotContent)

          // Append both as siblings to container
          writer.append(hotspot, hotspotContainer)
          writer.append(hotspotContent, hotspotContainer)

          // Save active carousel index only for the wrapper that owns this hotspot container.
          let carouselWrapperModel = hotspotContainer.parent
          while (carouselWrapperModel && carouselWrapperModel.name !== 'imageCarouselWrapper') {
            carouselWrapperModel = carouselWrapperModel.parent
          }

          if (carouselWrapperModel) {
            const viewWrapper = editor.editing.mapper.toViewElement(carouselWrapperModel)
            if (viewWrapper) {
              const domWrapper = editor.editing.view.domConverter.mapViewToDom(viewWrapper)
              if (domWrapper) {
                const allItems = domWrapper.querySelectorAll('.image-carousel-item')
                allItems.forEach((item, i) => {
                  if (item.classList.contains('active')) {
                    editor._carouselActiveIndex = i
                  }
                })
              }
            }
          }

          // Force re-render
          editor.editing.reconvertItem(hotspotContainer)
          editor.editing.view.forceRender()
        })
      },
    })

    editor.commands.add('removeImageHotspot', {
      execute: hotspotId => {
        editor.model.change(writer => {
          const root = editor.model.document.getRoot()
          const hotspot = this._findHotspotById(root, hotspotId)

          if (hotspot) {
            const container = hotspot.parent

            // Find and remove the associated content
            const content = Array.from(container.getChildren()).find(
              child =>
                child.name === 'imageHotspotContent' &&
                child.getAttribute('hotspotId') === hotspotId,
            )

            writer.remove(hotspot)
            if (content) {
              writer.remove(content)
            }

            // Force re-render
            editor.editing.reconvertItem(container)
            editor.editing.view.forceRender()
          }
        })
      },
    })

    editor.commands.add('updateImageHotspot', {
      execute: (options = {}) => {
        const { id, x, y, title, content } = options

        editor.model.change(writer => {
          const root = editor.model.document.getRoot()
          const hotspot = this._findHotspotById(root, id)

          if (hotspot) {
            if (x !== undefined) writer.setAttribute('x', x.toString(), hotspot)
            if (y !== undefined) writer.setAttribute('y', y.toString(), hotspot)
            if (title !== undefined)
              writer.setAttribute('title', title, hotspot)
            if (content !== undefined)
              writer.setAttribute('content', content, hotspot)

            // Force re-render
            const container = hotspot.parent
            editor.editing.reconvertItem(container)
            editor.editing.view.forceRender()
          }
        })
      },
    })
    editor.commands.add('accordionItemDelete', {
      execute() {
        const selection = editor.model.document.selection
        const selectedElement =
          selection.getSelectedElement() || selection.getFirstPosition().parent

        let accordionItem = selectedElement
        while (accordionItem && accordionItem.name !== 'accordionItem') {
          accordionItem = accordionItem.parent
        }

        if (accordionItem && accordionItem.name === 'accordionItem') {
          const accordionWrapper = accordionItem.parent
          const column = accordionWrapper.parent

          editor.model.change(writer => {
            writer.remove(accordionWrapper)
          })

          // Force a complete re-render of the remaining accordion items
          setTimeout(() => {
            if (column) {
              const remainingAccordionWrappers = Array.from(
                column.getChildren(),
              ).filter(child => child.name === 'accordionWrapper')

              remainingAccordionWrappers.forEach(wrapper => {
                // Force reconvert the entire accordion wrapper and its children
                editor.editing.reconvertItem(wrapper)

                const accordionItems = Array.from(wrapper.getChildren()).filter(
                  child => child.name === 'accordionItem',
                )

                accordionItems.forEach(item => {
                  editor.editing.reconvertItem(item)

                  const accordionHeaders = Array.from(
                    item.getChildren(),
                  ).filter(child => child.name === 'accordionHeader')

                  accordionHeaders.forEach(header => {
                    editor.editing.reconvertItem(header)
                  })
                })
              })

              // Force a complete view refresh
              editor.editing.view.forceRender()
            }
          }, 0)
        }
      },
    })
    // Image Hotspot Container Converters
    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: ['image-hotspot-container'],
      },
      model: (viewElement, { writer }) => {
        const src = viewElement.getAttribute('data-src')
        const alt = viewElement.getAttribute('data-alt') || ''
        const width = viewElement.getAttribute('data-width')
        const height = viewElement.getAttribute('data-height')

        const attributes = { src, alt }
        if (width) attributes.width = width
        if (height) attributes.height = height

        return writer.createElement('imageHotspotContainer', attributes)
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'imageHotspotContainer',
      view: (modelElement, { writer: viewWriter }) => {
        const src = modelElement.getAttribute('src')
        const alt = modelElement.getAttribute('alt') || ''
        const width = modelElement.getAttribute('width')
        const height = modelElement.getAttribute('height')

        const attributes = {
          class: 'image-hotspot-container',
          'data-src': src,
          'data-alt': alt,
        }

        if (width) attributes['data-width'] = width
        if (height) attributes['data-height'] = height

        const container = viewWriter.createContainerElement('div', attributes)

        // Add the image with hotspot-image class
        const image = viewWriter.createEmptyElement('img', {
          class: 'hotspot-image', // ✅ Class eklendi
          src: src,
          alt: alt,
          style: 'width: 100%; height: auto; display: block;',
        })

        viewWriter.insert(viewWriter.createPositionAt(container, 0), image)

        return container
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'imageHotspotContainer',
      view: (modelElement, { writer: viewWriter }) => {
        const src = modelElement.getAttribute('src')
        const alt = modelElement.getAttribute('alt') || ''
        const width = modelElement.getAttribute('width')
        const height = modelElement.getAttribute('height')

        const container = viewWriter.createContainerElement('div', {
          class: 'image-hotspot-container',
          'data-src': src,
          'data-alt': alt,
          style: 'position: relative; display: inline-block; width: 100%;',
        })

        // Add the image with hotspot-image class
        const image = viewWriter.createEmptyElement('img', {
          class: 'hotspot-image', // ✅ Class eklendi
          src: src,
          alt: alt,
          style: 'width: 100%; height: auto; display: block;',
        })

        // Add click to add hotspot functionality
        const clickOverlay = viewWriter.createUIElement(
          'div',
          {
            class: 'hotspot-click-overlay',
            style: `
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 5;
          cursor: crosshair;
        `,
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            domElement.addEventListener('dblclick', e => {
              e.preventDefault()
              e.stopPropagation()

              const imgElement = domElement.parentElement.querySelector('img')
              if (!imgElement) return

              const rect = imgElement.getBoundingClientRect()
              const x = ((e.clientX - rect.left) / rect.width) * 100
              const y = ((e.clientY - rect.top) / rect.height) * 100

              editor.execute('addImageHotspot', {
                x: Math.max(0, Math.min(100, x)),
                y: Math.max(0, Math.min(100, y)),
                hotspotContainer: modelElement,
                hotspotContainer: modelElement,
              })
            })

            return domElement
          },
        )

        viewWriter.insert(viewWriter.createPositionAt(container, 0), image)

        viewWriter.insert(
          viewWriter.createPositionAt(container, 'end'),
          clickOverlay,
        )

        // Show/hide controls on hover
        setTimeout(() => {
          const containerElement = editor.editing.view.domConverter.viewToDom(
            container,
          )
          if (containerElement) {
            containerElement.addEventListener('mouseenter', () => {
              const btn = containerElement.querySelector('.add-hotspot-btn')
              if (btn) btn.style.opacity = '1'
            })

            containerElement.addEventListener('mouseleave', () => {
              const btn = containerElement.querySelector('.add-hotspot-btn')
              if (btn) btn.style.opacity = '0'
            })
          }
        }, 100)

        return toWidget(container, viewWriter, {
          label: 'Image with hotspots',
          hasSelectionHandle: true,
        })
      },
    })

    // Replace your existing imageHotspot converters with these:

    // Image Hotspot Converters - Updated
    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: ['image-hotspot'],
      },
      model: (viewElement, { writer }) => {
        return writer.createElement('imageHotspot', {
          x: viewElement.getAttribute('data-x') || '50',
          y: viewElement.getAttribute('data-y') || '50',
          title: viewElement.getAttribute('data-title') || 'Hotspot',
          id: viewElement.getAttribute('data-id') || 'hotspot-' + generateUUID(),
          isVisible: viewElement.getAttribute('data-visible') || 'false',
          // Yeni attribute'ler
          backgroundColor:
            viewElement.getAttribute('data-background-color') || '#015FFB',
          iconColor: viewElement.getAttribute('data-icon-color') || '#ffffff',
          icon: viewElement.getAttribute('data-icon') || '',
          iconName: viewElement.getAttribute('data-icon-name') || 'mdi:plus',
        })
      },
    })

    // DataDowncast converter güncelleme
    conversion.for('dataDowncast').elementToElement({
      model: 'imageHotspot',
      view: (modelElement, { writer: viewWriter }) => {
        const x = modelElement.getAttribute('x') || '50'
        const y = modelElement.getAttribute('y') || '50'
        const title = modelElement.getAttribute('title') || 'Hotspot'
        const id = modelElement.getAttribute('id')
        const isVisible = modelElement.getAttribute('isVisible') || 'false'
        const backgroundColor =
          modelElement.getAttribute('backgroundColor') || '#015FFB'
        const iconColor = modelElement.getAttribute('iconColor') || '#ffffff'
        const icon = modelElement.getAttribute('icon') || ''
        const iconName = modelElement.getAttribute('iconName') || 'mdi:plus'

        const hotspotContainer = viewWriter.createContainerElement('div', {
          class: 'image-hotspot',
          'data-x': x,
          'data-y': y,
          'data-title': title,
          'data-id': id,
          'data-visible': isVisible,
          'data-background-color': backgroundColor,
          'data-icon-color': iconColor,
          'data-icon': icon,
          'data-icon-name': iconName,
          style: `
        position: absolute;
        left: ${x}%;
        top: ${y}%;
        transform: translate(-50%, -50%);
        z-index: 100;
      `,
        })

        const hotspotIconElement = viewWriter.createUIElement(
          'div',
          { class: 'hotspot-icon-wrapper' },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            const hexToRgba = (hex, alpha) => {
              const r = parseInt(hex.slice(1, 3), 16)
              const g = parseInt(hex.slice(3, 5), 16)
              const b = parseInt(hex.slice(5, 7), 16)
              return `rgba(${r}, ${g}, ${b}, ${alpha})`
            }

            const ringColor = hexToRgba(backgroundColor, 0.6)
            const ringColorLight = hexToRgba(backgroundColor, 0.4)
            const iconToUse = icon || 'mdi:plus'

            const iconHtml = icon
              ? `<iconify-icon icon="${iconToUse}" style="color: ${iconColor}; width: 16px; height: 16px;"></iconify-icon>`
              : `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="${iconColor}">
           <path d="M12 2C13.1 2 14 2.9 14 4V10H20C21.1 10 22 10.9 22 12C22 13.1 21.1 14 20 14H14V20C14 21.1 13.1 22 12 22C10.9 22 10 21.1 10 20V14H4C2.9 14 2 13.1 2 12C2 10.9 2.9 10 4 10H10V4C10 2.9 10.9 2 12 2Z"/>
         </svg>`

            domElement.innerHTML = `
      <div class="hotspot-icon" style="
        width: 24px;
        height: 24px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${backgroundColor};
        border-radius: 50%;
        color: ${iconColor};
        font-weight: bold;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        position: relative;
        --hotspot-ring-color: ${ringColor};
        --hotspot-ring-color-light: ${ringColorLight};
      ">
        ${iconHtml}
      </div>
    `

            return domElement
          },
        )

        viewWriter.insert(
          viewWriter.createPositionAt(hotspotContainer, 0),
          hotspotIconElement,
        )
        return hotspotContainer
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'imageHotspot',
      view: (modelElement, { writer: viewWriter }) => {
        const x = modelElement.getAttribute('x') || '50'
        const y = modelElement.getAttribute('y') || '50'
        const title = modelElement.getAttribute('title') || 'Hotspot'
        const id = modelElement.getAttribute('id')
        const isVisible = modelElement.getAttribute('isVisible') || 'false'
        const backgroundColor =
          modelElement.getAttribute('backgroundColor') || '#015FFB'
        const iconColor = modelElement.getAttribute('iconColor') || '#ffffff'
        const icon = modelElement.getAttribute('icon') || ''
        const iconName = modelElement.getAttribute('iconName') || 'mdi:plus'

        const hotspotContainer = viewWriter.createContainerElement('div', {
          class: 'image-hotspot',
          'data-x': x,
          'data-y': y,
          'data-title': title,
          'data-id': id,
          'data-visible': isVisible,
          'data-background-color': backgroundColor,
          'data-icon-color': iconColor,
          'data-icon': icon,
          'data-icon-name': iconName,
          style: `
        position: absolute;
        left: ${x}%;
        top: ${y}%;
        transform: translateY(-50%);
        z-index: 10;
        display: flex;
        align-items: center;
        gap: 0;
      `,
        })

        const iconContainer = viewWriter.createUIElement(
          'div',
          {
            class: 'hotspot-icon-wrapper',
            style: 'position: relative; transform: translateX(-50%);',
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            const iconToUse = icon || iconName || 'mdi:plus'

            // Convert hex to rgba
            const hexToRgba = (hex, alpha) => {
              const r = parseInt(hex.slice(1, 3), 16)
              const g = parseInt(hex.slice(3, 5), 16)
              const b = parseInt(hex.slice(5, 7), 16)
              return `rgba(${r}, ${g}, ${b}, ${alpha})`
            }

            const ringColor = hexToRgba(backgroundColor, 0.6)
            const ringColorLight = hexToRgba(backgroundColor, 0.4)

            const iconHtml = icon
              ? `<iconify-icon icon="${iconToUse}" style="color: ${iconColor}; display: flex; align-items: center; justify-content: center; width: 16px; height: 16px;"></iconify-icon>`
              : `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="${iconColor}">
         <path d="M12 2C13.1 2 14 2.9 14 4V10H20C21.1 10 22 10.9 22 12C22 13.1 21.1 14 20 14H14V20C14 21.1 13.1 22 12 22C10.9 22 10 21.1 10 20V14H4C2.9 14 2 13.1 2 12C2 10.9 2.9 10 4 10H10V4C10 2.9 10.9 2 12 2Z"/>
       </svg>`

            domElement.innerHTML = `
      <div class="hotspot-icon" style="
        width: 32px;
        height: 32px;
        cursor: move;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${backgroundColor};
        border-radius: 50%;
        color: ${iconColor};
        font-weight: bold;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        position: relative;
        --hotspot-ring-color: ${ringColor};
        --hotspot-ring-color-light: ${ringColorLight};
      ">
        ${iconHtml}
      </div>
        `

            // Drag functionality
            let isDragging = false
            const iconEl = domElement.querySelector('.hotspot-icon')

            iconEl.addEventListener('mousedown', e => {
              isDragging = true
              iconEl.style.cursor = 'grabbing'
              e.preventDefault()
              e.stopPropagation()
            })

            domDocument.addEventListener('mousemove', e => {
              if (!isDragging) return

              const container = domElement.closest('.image-hotspot-container')
              const img = container?.querySelector('.hotspot-image')
              if (!img) return

              const imgRect = img.getBoundingClientRect()
              const newX = ((e.clientX - imgRect.left) / imgRect.width) * 100
              const newY = ((e.clientY - imgRect.top) / imgRect.height) * 100

              const constrainedX = Math.max(0, Math.min(100, newX))
              const constrainedY = Math.max(0, Math.min(100, newY))

              const hotspotDiv = domElement.closest('.image-hotspot')
              if (hotspotDiv) {
                hotspotDiv.style.left = `${constrainedX}%`
                hotspotDiv.style.top = `${constrainedY}%`
              }
            })

            domDocument.addEventListener('mouseup', e => {
              if (!isDragging) return
              isDragging = false
              iconEl.style.cursor = 'move'

              const container = domElement.closest('.image-hotspot-container')
              const img = container?.querySelector('.hotspot-image')
              if (!img) return

              const imgRect = img.getBoundingClientRect()
              const newX = ((e.clientX - imgRect.left) / imgRect.width) * 100
              const newY = ((e.clientY - imgRect.top) / imgRect.height) * 100

              const constrainedX = Math.max(0, Math.min(100, newX))
              const constrainedY = Math.max(0, Math.min(100, newY))

              editor.execute('updateImageHotspot', {
                id,
                x: constrainedX,
                y: constrainedY,
              })
            })

            return domElement
          },
        )

        // ✅ Controls container - #fff background
        const controlsContainer = viewWriter.createUIElement(
          'div',
          {
            class: 'hotspot-controls',
            style: `
          display: none;
          flex-direction: column;
          gap: 4px;
          left: 70%;
          position: absolute;
          padding: 4px;
          border-radius: 6px;
          background: #fff;
          box-shadow: 0 4px 16px 0 rgba(145, 158, 171, 0.24);
        `,
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            domElement.innerHTML = `
          <button class="edit-hotspot-btn" style="background: transparent; border: 0; cursor: pointer;width:24px;height:24px">${hotspotEditSvg}</button>
          <button class="settings-hotspot-btn" style="background: transparent; border: 0; cursor: pointer;width:24px;height:24px">${hotspotSettingsSvg}</button>
          <button class="delete-hotspot-btn" style="background: transparent; border: 0; cursor: pointer;width:24px;height:24px">${hotspotDeleteSvg}</button>
        `

            // Edit button
            const editBtn = domElement.querySelector('.edit-hotspot-btn')
            editBtn?.addEventListener('click', e => {
              e.stopPropagation()
              e.preventDefault()

              const hotspotDiv = domElement.closest('.image-hotspot')
              if (!hotspotDiv) return

              const hotspotId = hotspotDiv.getAttribute('data-id')
              const imageContainer = hotspotDiv.parentElement

              if (imageContainer) {
                const allContents = imageContainer.querySelectorAll(
                  '.image-hotspot-content',
                )
                allContents.forEach(c => (c.style.display = 'none'))

                const allHotspots = imageContainer.querySelectorAll(
                  '.image-hotspot',
                )
                allHotspots.forEach(h => (h.style.zIndex = '10'))

                const targetContent = imageContainer.querySelector(
                  `.image-hotspot-content[data-hotspot-id="${hotspotId}"]`,
                )

                if (targetContent) {
                  hotspotDiv.style.zIndex = '1000'
                  targetContent.style.display = 'block'
                  targetContent.style.position = 'absolute'
                  targetContent.style.left = '5%'
                  targetContent.style.top = '5%'
                  targetContent.style.zIndex = '1001'

                  setTimeout(() => {
                    const firstEditable = targetContent.querySelector(
                      '[contenteditable="true"]',
                    )
                    if (firstEditable) firstEditable.focus()
                  }, 50)
                }
              }
            })

            // Settings button
            const settingsBtn = domElement.querySelector(
              '.settings-hotspot-btn',
            )
            settingsBtn?.addEventListener('click', e => {
              e.stopPropagation()
              const dispatch = editor.config.get('videoGalleryPlugin.dispatch')
              if (dispatch) {
                handleHotspotSettings(dispatch, editor, id, modelElement)
              }
            })

            // Delete button
            const deleteBtn = domElement.querySelector('.delete-hotspot-btn')
            deleteBtn?.addEventListener('click', e => {
              e.stopPropagation()
              editor.execute('removeImageHotspot', id)
            })

            return domElement
          },
        )

        // ✅ Hover event listener
        setTimeout(() => {
          const hotspotDiv = document.querySelector(
            `.image-hotspot[data-id="${id}"]`,
          )

          if (hotspotDiv) {
            const controls = hotspotDiv.querySelector('.hotspot-controls')

            hotspotDiv.addEventListener('mouseenter', () => {
              if (controls) {
                controls.style.display = 'flex'
              }
            })

            hotspotDiv.addEventListener('mouseleave', () => {
              if (controls) {
                controls.style.display = 'none'
              }
            })
          }
        }, 100)

        viewWriter.insert(
          viewWriter.createPositionAt(hotspotContainer, 0),
          iconContainer,
        )

        viewWriter.insert(
          viewWriter.createPositionAt(hotspotContainer, 'end'),
          controlsContainer,
        )

        return toWidget(hotspotContainer, viewWriter, {
          label: 'Image hotspot',
          hasSelectionHandle: true,
        })
      },
    })

    // Attribute mapping'ler ekleyin
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'backgroundColor',
      view: 'data-background-color',
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: 'iconColor',
      view: 'data-icon-color',
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: 'icon',
      view: 'data-icon',
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: 'iconName',
      view: 'data-icon-name',
    })

    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: ['image-hotspot-content'],
      },
      model: (viewElement, { writer }) => {
        const hotspotId = viewElement.getAttribute('data-hotspot-id') // view'dan oku
        return writer.createElement('imageHotspotContent', {
          hotspotId: hotspotId, // ✅ model'e camelCase olarak yaz
        })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'imageHotspotContent',
      view: (modelElement, { writer: viewWriter }) => {
        const hotspotId = modelElement.getAttribute('hotspotId')

        const div = viewWriter.createContainerElement('div', {
          class: 'image-hotspot-content',
          'data-hotspot-id': hotspotId,
          style: `
            display: none;
          `,
        })

        // Close button with inline event handler
        const closeButton = viewWriter.createContainerElement('button', {
          class: 'hotspot-content-close',
          onclick:
            "this.closest('.image-hotspot-content').style.display='none'",
        })

        // SVG icon as raw element
        const closeIcon = viewWriter.createRawElement(
          'svg',
          {
            width: '20',
            height: '20',
            viewBox: '0 0 24 24',
            fill: 'none',
          },
          domElement => {
            domElement.innerHTML =
              '<path d="M18 6L6 18M6 6L18 18" stroke="#666" stroke-width="2" stroke-linecap="round"/>'
            return domElement
          },
        )

        viewWriter.insert(
          viewWriter.createPositionAt(closeButton, 0),
          closeIcon,
        )
        viewWriter.insert(viewWriter.createPositionAt(div, 0), closeButton)

        return div
      },
    })
    // Upcast
    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: ['image-hotspot-content'],
      },
      model: (viewElement, { writer }) => {
        const hotspotId = viewElement.getAttribute('data-hotspot-id')
        return writer.createElement('imageHotspotContent', {
          'data-hotspot-id': hotspotId,
        })
      },
    })

    // DataDowncast
    conversion.for('dataDowncast').elementToElement({
      model: 'imageHotspotContent',
      view: (modelElement, { writer: viewWriter }) => {
        const hotspotId = modelElement.getAttribute('data-hotspot-id')
        return viewWriter.createContainerElement('div', {
          class: 'image-hotspot-content',
          'data-hotspot-id': hotspotId,
          style: 'display: none;',
        })
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'imageHotspotContent',
      view: (modelElement, { writer: viewWriter }) => {
        const hotspotId = modelElement.getAttribute('hotspotId') // ✅ camelCase

        const div = viewWriter.createEditableElement('div', {
          class: 'image-hotspot-content',
          'data-hotspot-id': hotspotId,
        })

        // Close button
        const closeButton = viewWriter.createUIElement(
          'button',
          {
            class: 'hotspot-content-close',
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)
            domElement.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="#666" stroke-width="2" stroke-linecap="round"/>
          </svg>
        `

            domElement.addEventListener('click', e => {
              e.stopPropagation()
              const contentDiv = domElement.closest('.image-hotspot-content')
              if (contentDiv) {
                contentDiv.style.display = 'none'
              }
            })

            return domElement
          },
        )

        viewWriter.insert(viewWriter.createPositionAt(div, 0), closeButton)

        return div
      },
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: 'hotspotId',
      view: 'data-hotspot-id',
    })
  }
}

export default SimpleBox
